import {createGuestAnalytics} from './guestFunnelRelease';
import { getPriceComparison, getPersonalizedPlan, rememberPlanAnswers, getCheckoutRefreshDeadline, resetPlanReviewDeadline, renderPaymentReassurance } from './checkoutPresentation';
import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  type StripeEmbeddedCheckout,
  type StripeEmbeddedCheckoutAnalyticsEventUnion,
} from '@stripe/stripe-js';
import { withPaymentLoadTimeout } from './paymentLoading';
import { mountPaymentElements } from './paymentElements';
import { createDeferredAnalytics } from './deferredAnalytics';
import { createAcquisitionTracker } from './acquisition';
import { createMetaFunnelSignals } from './metaFunnelSignals';
import { appStoreDestination } from './appStoreDestination';
import { buildSmoothLinePath } from '../../src/features/onboarding/onboarding-impact-chart';

const deferredPostHog = createDeferredAnalytics(
  () => import('posthog-js').then((module) => module.default),
  (error) => console.warn('[analytics] optional SDK unavailable', error),
);

import {
  buildMetaTrackingContext,
  getTrackingConsent as getSavedTrackingConsent,
  initializeMetaPixel,
  isInternalTrackingTestActive,
  isAnalyticsTestActive,
  setTrackingConsent,
  trackMetaPixelCustomEvent,
  trackMetaPixelEvent,
  trackMetaQuizHalfway,
} from './metaTracking';

import firstpickNeonSignUrl from '../../assets/generated/firstpick-neon-sign/runtime/firstpick-neon-sign.png';

import {
  CHOICE_STEP_BY_SCREEN,
  MULTI_STEP_BY_SCREEN,
  ONBOARDING_DAY_ONE_SCREEN,
  ONBOARDING_FINAL_SCREEN,
  ONBOARDING_FIRST_QUESTION_SCREEN,
  ONBOARDING_INTRO_SCREEN,
  ONBOARDING_MINI_SESSION_SCREEN,
  ONBOARDING_PHONE_HOURS_SCREEN,
  ONBOARDING_PHONE_MATH_SCREEN,
  ONBOARDING_PITCH_SCREEN,
  ONBOARDING_PLAN_LOADING_SCREEN,
  ONBOARDING_RECLAIM_SCREEN,
  ONBOARDING_REFLECTION_SCREEN,
  ONBOARDING_SOCIAL_PROOF_SCREEN,
  TEXT_STEP_BY_SCREEN,
  buildOnboardingPersonalizationProfile,
  buildPersistedAnswers,
  getDailyPhoneHours,
  getMultiReaction,
  getNextOnboardingFlowScreen,
  getPhoneMathReaction,
  getPreviousOnboardingFlowScreen,
  getQuestionCheckpointReaction,
  getStartupName,
  getStoredArrayAnswer,
  getStoredStringAnswer,
  getUserName,
  isQuestionReactionCheckpoint,
  resolveCopy,
  type ChoiceStep,
  type MultiStep,
  type OnboardingAnswers,
  type TextAnswerKey,
  type TextStep,
} from './onboardingConfig';
import {
  getWebOnboardingPath,
  getWebOnboardingScreenForPath,
  getWebQuizQuestionProgress,
  resolveNameStepScreen,
  type NameStepArm,
} from './onboardingRoutes';
import {reportableExperimentRunId} from './nameStepExperiment';
import {assignOfferBadgeArm,fetchLocalOfferBadgePercent,fetchOfferBadgePercent,readOfferBadgeArm,type OfferBadgeArm} from './offerBadgeExperiment';
import './styles.css';
import paidQuizIntroHtml from './paidQuizIntro.html?raw';
import identityEntryHtml from './identityEntry.html?raw';
import './identityEntry.css';
import './guidedOnboarding.css';
import { isGuidedPaidQuiz, getGuidedPrompt, getGuidedReclaimCopy } from './guidedOnboarding';
import { prepareGuidedBobAssets, setGuidedBobTyping } from './guidedBobMotion';
import { startStableGuidedSpeech, getGuidedPlanProgress, normalizeGuidedDashes } from './guidedOnboardingEffects';
import { renderGuidedCommitment, mountGuidedCommitment } from './guidedCommitment';
import { renderGuidedProfile, renderGuidedOutcomeChart } from './guidedOnboardingViews';
import { isIdentityEntry, normalizeEntryIdentity, getIdentityPitch } from './identityEntry';
import { isGuestCheckoutPreview, isGuestCheckoutRelease, isGuestCheckoutTestRelease, isOrganicGuestCheckoutPreview, restoreFunnelSearch } from './guestCheckoutGate';
import {saveQuizPlan, readQuizPlan, clearQuizPlan, quizPlanHomePath, resetGuestQuizDraft} from './savedQuizPlan';
import {renderFunnelLoading} from './funnelLoading';

// Restore return-link context before selecting the live or sandbox backend.
try {
  const search = restoreFunnelSearch(location.pathname, location.search, sessionStorage.getItem('focuscorp_internal_tracking_test_v1'));
  if (search !== location.search) history.replaceState(history.state, '', `${location.pathname}${search}${location.hash}`);
} catch { /* Explicit links still work when browser storage is unavailable. */ }
const guestCheckoutReleaseEnabled = import.meta.env.VITE_GUEST_FUNNEL_RELEASE === '1' && isGuestCheckoutRelease({flag:import.meta.env.VITE_GUEST_FUNNEL_RELEASE,hostname:location.hostname,search:location.search,backendUrl:import.meta.env.VITE_SUPABASE_URL,guestBackendUrl:import.meta.env.VITE_GUEST_SUPABASE_URL});
const guestCheckoutTestEnabled = isGuestCheckoutTestRelease({flag:import.meta.env.VITE_GUEST_FUNNEL_RELEASE,hostname:location.hostname,search:location.search,testBackendUrl:import.meta.env.VITE_GUEST_TEST_SUPABASE_URL}) && Boolean(import.meta.env.VITE_GUEST_TEST_SUPABASE_ANON_KEY);
const guestCheckoutHostedEnabled = guestCheckoutReleaseEnabled || guestCheckoutTestEnabled;
const organicGuestEnabled = (guestCheckoutReleaseEnabled && !new URLSearchParams(location.search).has('qz')) || isOrganicGuestCheckoutPreview({dev:import.meta.env.DEV,flag:import.meta.env.VITE_ORGANIC_GUEST_PREVIEW,hostname:location.hostname,search:location.search,backendUrl:import.meta.env.VITE_GUEST_SUPABASE_URL});
let guestCheckoutPreviewEnabled = organicGuestEnabled || guestCheckoutHostedEnabled || import.meta.env.DEV && isGuestCheckoutPreview(import.meta.env.DEV, import.meta.env.VITE_GUEST_CHECKOUT_PREVIEW, window.location.hostname, window.location.search);
const offerBadgeEligible = guestCheckoutPreviewEnabled && !new URLSearchParams(location.search).has('flow');
const offerBadgeNamespace = new URLSearchParams(location.search).get('qz')==='tst1' ? 'tst1' : import.meta.env.DEV ? 'local' : 'live';
const offerBadgeStore = {
  getItem: (key:string) => window.localStorage.getItem(key),
  setItem: (key:string,value:string) => window.localStorage.setItem(key,value),
};
let nameStepArm: NameStepArm = guestCheckoutPreviewEnabled ? 'no_name' : 'control';
const storedOfferBadgeArm = readOfferBadgeArm(offerBadgeStore,offerBadgeNamespace);
let offerBadgeArm:OfferBadgeArm = storedOfferBadgeArm ?? 'plain';
let offerBadgeAssigned = Boolean(storedOfferBadgeArm) || !offerBadgeEligible;
const offerBadgeArmReady:Promise<OfferBadgeArm> = offerBadgeEligible
  ? assignOfferBadgeArm({
      store:offerBadgeStore,namespace:offerBadgeNamespace,random:Math.random,
      override:new URLSearchParams(location.search).get('badge_variant'),
      allowOverride:import.meta.env.DEV || guestCheckoutTestEnabled,
      readPercent:()=>import.meta.env.DEV ? fetchLocalOfferBadgePercent()
        : fetchOfferBadgePercent(import.meta.env.VITE_SUPABASE_URL ?? '',import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''),
    }).then(arm=>{offerBadgeArm=arm;offerBadgeAssigned=true;return arm;})
  : Promise.resolve(offerBadgeArm);
const funnelExperimentReady = offerBadgeArmReady;
let guestCheckoutPreviewStarted = false;
let guestCheckoutModule: Promise<typeof import('./guestCheckoutPreview')> | null = null;
const loadGuestCheckoutModule = () => guestCheckoutModule ??= import('./guestCheckoutPreview').catch(error => {
  guestCheckoutModule = null;
  throw error;
});

const guidedOnboardingEnabled = organicGuestEnabled || guestCheckoutHostedEnabled || isGuidedPaidQuiz(window.location.search);
if (guidedOnboardingEnabled) void prepareGuidedBobAssets();
let guidedSpeechEffect: ReturnType<typeof startStableGuidedSpeech> | null = null;
let lastGuidedSpeechKey: string | null = null;
let guidedHoldEffect: ReturnType<typeof mountGuidedCommitment> | null = null;
let guidedCommitmentChoices = new URLSearchParams(window.location.search).get('commitment') === 'choices';
let guidedPlanFrame: number | null = null;
// The approved identity welcome remains the paid/test entry in production.
const identityEntryEnabled = organicGuestEnabled || isIdentityEntry(window.location.search);
const identityAnswerStorageKey = 'focuscorp_identity_entry_preview';

type FunnelView =
  | 'landing'
  | 'quiz'
  | 'account'
  | 'referral'
  | 'firstpickReward'
  | 'checkout'
  | 'embeddedCheckout'
  | 'checkoutComplete'
  | 'download'
  | 'manageSubscription';
type StatusTone = 'error' | 'success';
type PlanKey = 'annual' | 'monthly' | 'weekly';
type CheckoutOfferKey = 'focuscorp' | 'firstpick' | 'founder';
type CheckoutExitIntent = 'landing' | 'browser_back';
type PremiumSource = 'checkout' | 'referral' | 'restore';

type DevelopmentTimer = {
  checkpoint: (stage: string) => void;
  finish: (stage?: string) => void;
};

type GeneratedPlan = {
  blockList: string;
  dailyTarget: string;
  firstSession: string;
  focusWindow: string;
  mission: string;
  risk: string;
};

type PersistedOnboarding = {
  answers: OnboardingAnswers;
  generatedPlan: GeneratedPlan;
  personalization: ReturnType<typeof buildOnboardingPersonalizationProfile>;
  experimentRunId?: string;
};

type FunnelState = {
  accountSaveRetryAvailable: boolean;
  activeOffer: CheckoutOfferKey | null;
  answers: OnboardingAnswers;
  checkoutExitIntent: CheckoutExitIntent | null;
  founderOfferDismissed: boolean;
  founderOfferVisible: boolean;
  isBusy: boolean;
  miniRemaining: number;
  pendingScreen: number | null;
  premiumSource: PremiumSource | null;
  referralCode?: string;
  selectedPlan: PlanKey;
  planTermsAccepted: boolean;
  session: Session | null;
  statusMessage: string | null;
  statusTone: StatusTone | null;
  user: User | null;
  view: FunnelView;
};

type CheckoutResponse = {
  checkoutUi?: 'embedded' | 'elements';
  canApplyPromotionCode?: boolean;
  expiresAt?: number;
  clientSecret: string;
  checkoutSessionId: string;
};

type CheckoutVerificationResponse = {
  error?: string;
  paymentStatus?: string | null;
  plan?: PlanKey;
  sessionStatus?: string | null;
  subscriptionStatus?: string | null;
  verified: boolean;
};

type RestoreResponse = {
  currentPeriodEnd?: string;
  message?: string;
  restored: boolean;
};

type ReferralResponse = {
  error?: string;
  grantedItemId?: string;
  premiumGrantedUntil?: string;
  referralCode?: string;
  rewardType?: 'inventory_item' | 'premium';
  status?: string;
};

type StripePortalResponse = {
  error?: string;
  url?: string;
};

const appRoot = document.querySelector<HTMLDivElement>('#app');

if (!appRoot) {
  throw new Error('Missing #app root.');
}

const root = appRoot;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim() ?? '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() ?? '';
const appStoreUrl = appStoreDestination(
  import.meta.env.VITE_FOCUSCORP_APP_STORE_URL?.trim() ||
    'https://apps.apple.com/app/deep-work-timer-focuscorp/id6761076603',
  import.meta.env.VITE_FOCUSCORP_ONELINK_URL?.trim() || '',
  window.location.search,
);
const initialWebAuthCallbackRoute = window.location.pathname === '/auth/callback';
const initialWebAuthCallbackQuery = new URLSearchParams(window.location.search);
const initialWebAuthCallbackHash = new URLSearchParams(window.location.hash.replace(/^#/u, ''));
const initialWebAuthCallbackCode = initialWebAuthCallbackQuery.get('code')?.trim() || null;
const initialWebAuthCallbackNext = initialWebAuthCallbackQuery.get('next')?.trim() || null;
const initialWebAuthCallbackHadError = ['error', 'error_code', 'error_description'].some(
  (key) => initialWebAuthCallbackQuery.has(key) || initialWebAuthCallbackHash.has(key),
);
const annualPriceLabel = import.meta.env.VITE_ANNUAL_PRICE_LABEL?.trim() || '€22.99';
const monthlyPriceLabel = import.meta.env.VITE_MONTHLY_PRICE_LABEL?.trim() || '€10.99';
const weeklyPriceLabel = import.meta.env.VITE_WEEKLY_PRICE_LABEL?.trim() || '€3.99';
const focuscorpOfferPriceLabel =
  import.meta.env.VITE_FOCUSCORP_OFFER_PRICE_LABEL?.trim() || annualPriceLabel;
const firstpickOfferPriceLabel =
  import.meta.env.VITE_FIRSTPICK_OFFER_PRICE_LABEL?.trim() || annualPriceLabel;
const founderOfferPriceLabel =
  import.meta.env.VITE_FOUNDER_OFFER_PRICE_LABEL?.trim() || '€9.20';

// The plan cards show the annual plan as its per-week equivalent with the
// savings vs. the weekly plan, derived from the display labels above. If a
// label ever stops parsing (unexpected format), the cards fall back to
// showing the raw label per year.
type ParsedPrice = { amount: number; prefix: string; suffix: string };

function parsePriceLabel(label: string): ParsedPrice | null {
  const match = label.match(/^([^\d]*)(\d+(?:[.,]\d+)?)([^\d]*)$/u);
  const amount = Number.parseFloat((match?.[2] ?? '').replace(',', '.'));

  if (!match || !Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  return { amount, prefix: match[1] ?? '', suffix: match[3] ?? '' };
}

function formatPrice(amount: number, shape: ParsedPrice) {
  return `${shape.prefix}${amount.toFixed(2)}${shape.suffix}`;
}

const annualPrice = parsePriceLabel(annualPriceLabel);
const monthlyPrice = parsePriceLabel(monthlyPriceLabel);
const weeklyPrice = parsePriceLabel(weeklyPriceLabel);
const annualWeeklyLabel = annualPrice ? formatPrice(annualPrice.amount / 52, annualPrice) : null;
const annualSavingsPercent =
  annualPrice && weeklyPrice && weeklyPrice.amount > annualPrice.amount / 52
    ? Math.round((1 - annualPrice.amount / 52 / weeklyPrice.amount) * 100)
    : null;
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.trim() ?? '';
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function prepareStripe() {
  if (!stripePromise && stripePublishableKey) {
    stripePromise = loadStripe(stripePublishableKey).catch((error) => {
      stripePromise = null;
      throw error;
    });
  }
  return stripePromise;
}
const isSupabaseConfigured = supabaseUrl.length > 0 && supabaseAnonKey.length > 0;
const supabase: SupabaseClient | null = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        // Exchange PKCE codes explicitly in hydrateAuth. Supabase's automatic
        // detection ignores a code when this tab has no matching verifier and
        // can otherwise fall back to an unrelated session from storage.
        detectSessionInUrl: false,
        flowType: 'pkce',
        persistSession: true,
      },
    })
  : null;

const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim() ?? '';
const posthogHost = import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://eu.i.posthog.com';
const metaPixelId = import.meta.env.VITE_META_PIXEL_ID?.trim() ?? '';
const INTERNAL_TEST_STORAGE_KEY = 'focuscorp_internal_tracking_test_v1';
const internalTrackingTest = {
  enabled: import.meta.env.VITE_INTERNAL_TRACKING_TEST === 'true',
  hostname: window.location.hostname,
  allowedHostname: import.meta.env.VITE_INTERNAL_TRACKING_TEST_HOST?.trim() ?? '',
  funnelCode: new URLSearchParams(window.location.search).get('qz') ?? '',
};
try {
  const entryParams = new URLSearchParams(window.location.search);
  const entryPath = window.location.pathname.replace(/\/+$/u, '') || '/';
  const isPlainEntry = ['/', '/start'].includes(entryPath) &&
    !entryParams.has('code');
  if (!entryParams.has('qz') && !isPlainEntry && !organicGuestEnabled) {
    internalTrackingTest.funnelCode = sessionStorage.getItem(INTERNAL_TEST_STORAGE_KEY) ?? '';
  }
  if (isInternalTrackingTestActive(internalTrackingTest) || isAnalyticsTestActive(internalTrackingTest)) {
    sessionStorage.setItem(INTERNAL_TEST_STORAGE_KEY, internalTrackingTest.funnelCode);
  } else {
    sessionStorage.removeItem(INTERNAL_TEST_STORAGE_KEY);
  }
} catch {
  // The current-page test still works when session storage is unavailable.
}

function getTrackingConsent() {
  return getSavedTrackingConsent(undefined, internalTrackingTest);
}

function stopInternalTrackingTest() {
  internalTrackingTest.funnelCode = '';
  try {
    sessionStorage.removeItem(INTERNAL_TEST_STORAGE_KEY);
  } catch { /* Storage can be unavailable in private browsing. */ }
  const url = new URL(window.location.href);
  url.searchParams.delete('qz');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

const isPostHogConfigured = posthogToken.length > 0;
const analyticsEnvironment = isAnalyticsTestActive(internalTrackingTest)
  ? 'internal_test'
  : import.meta.env.DEV
  ? 'development'
  : ['focuscorp.app', 'www.focuscorp.app'].includes(window.location.hostname.toLowerCase())
    ? 'production'
    : 'preview';
const devOfferPreview = import.meta.env.DEV
  ? new URLSearchParams(window.location.search).get('preview')
  : null;

const state: FunnelState = {
  accountSaveRetryAvailable: false,
  activeOffer:
    devOfferPreview === 'focuscorp-offer'
      ? 'focuscorp'
      : devOfferPreview === 'firstpick-offer' || devOfferPreview === 'firstpick-reward'
        ? 'firstpick'
        : devOfferPreview === 'founder-offer'
          ? 'founder'
        : null,
  answers: {
    dailyPhoneHours: 3,
  },
  checkoutExitIntent: null,
  founderOfferDismissed: false,
  founderOfferVisible: devOfferPreview === 'founder-popup',
  isBusy: false,
  miniRemaining: 10,
  pendingScreen: null,
  premiumSource: null,
  selectedPlan: 'annual',
  planTermsAccepted: true,
  session: null,
  statusMessage: null,
  statusTone: null,
  user: null,
  view:
    devOfferPreview === 'account'
      ? 'account'
      : devOfferPreview === 'firstpick-reward'
      ? 'firstpickReward'
      : devOfferPreview === 'focuscorp-offer' ||
          devOfferPreview === 'firstpick-offer' ||
          devOfferPreview === 'founder-offer' ||
          devOfferPreview === 'founder-popup' ||
          devOfferPreview === 'plans'
        ? 'checkout'
        : 'landing',
};

let currentScreen = ONBOARDING_INTRO_SCREEN;
let reactionText: string | null = null;
let miniTimerId: number | null = null;
let miniPhaseTimeoutIds: number[] = [];
let planTimerId: number | null = null;
let firstpickRewardTimerId: number | null = null;
let checkoutExitGuardArmed = false;
let lastViewedStep: number | null = null;
let stepViewedAtMs: number | null = null;
let activeQuizRunId: string | null = null;
let capturedQuizStartRunId: string | null = null;
let metaQuizStartRunId: string | null = null;
const metaFunnelSignals = createMetaFunnelSignals(
  (name, properties, eventId) => trackMetaPixelCustomEvent(name, properties, undefined, getTrackingConsent(), eventId),
  crypto.randomUUID(),
);
let quizStartedAtMs: number | null = null;
let quizExitCaptured = false;
let hasTrackedMetaQuizHalfway = false;
let lastTrackedFunnelView: FunnelView | null = null;
let embeddedCheckout: Pick<StripeEmbeddedCheckout, 'destroy'> | null = null;
let checkoutUi: 'embedded' | 'elements' | 'pending' = 'embedded';
let checkoutCanApplyPromotionCode = false;
let checkoutExpiresAt: number | null = null;
let embeddedCheckoutClientSecret: string | null = null;
let embeddedCheckoutClientSecretPromise: Promise<string> | null = null;
let checkoutAttemptVersion = 0;
let embeddedCheckoutSessionId: string | null = null;
let embeddedCheckoutMountPromise: Promise<void> | null = null;
let checkoutResizeObserver: ResizeObserver | null = null;
let embeddedCheckoutError: string | null = null;
let embeddedCheckoutAbandonCaptured = false;
let embeddedCheckoutSubmitted = false;
let embeddedCheckoutViewedCaptured = false;
let checkoutCompletionSessionId: string | null = null;
let checkoutCompletionState: 'idle' | 'loading' | 'incomplete' | 'error' = 'idle';
let checkoutCompletionMessage: string | null = null;
let checkoutCompletionStarted = false;
let checkoutReadyTimer: DevelopmentTimer | null = null;
let hasCapturedLandingView = false;
let hasInitializedPostHog = false;
let hasTrackedMetaFunnelView = false;
let optionalTrackingEnabled = false;
let trackingPreferencesVisible = false;
let referralExpanded = false;
let referralDraft = '';
let referralViewedOnPlans = false;
let referralResolvedOnPlans = false;
const successfulReferralCache = new Map<string, ReferralResponse>();

const EMBEDDED_CHECKOUT_STORAGE_KEY = 'focuscorp_embedded_checkout';
const PENDING_WEB_SIGNUP_STORAGE_KEY = 'focuscorp_pending_web_signup_v1';
const PENDING_WEB_SIGNUP_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const PENDING_BILLING_AUTH_STORAGE_KEY = 'focuscorp_pending_billing_auth_v1';
const PENDING_BILLING_AUTH_MAX_AGE_MS = 30 * 60 * 1000;

type PendingWebSignup = {
  answers: OnboardingAnswers;
  authenticatedUserId?: string;
  createdAt: number;
  expectedEmail?: string;
  provider: 'apple' | 'email';
  version: 1;
};

function createDevelopmentTimer(action: string): DevelopmentTimer {
  const startedAt = performance.now();
  let previousAt = startedAt;

  const log = (stage: string) => {
    if (!import.meta.env.DEV) {
      return;
    }

    const now = performance.now();
    console.info(`[FocusCorp performance] ${JSON.stringify({
      action,
      duration_ms: Math.round((now - previousAt) * 10) / 10,
      stage,
      total_ms: Math.round((now - startedAt) * 10) / 10,
    })}`);
    previousAt = now;
  };

  return {
    checkpoint: log,
    finish: (stage = 'total') => log(stage),
  };
}



const acquisitionTracker = createAcquisitionTracker({
  getLocation: () => window.location,
  getReferrer: () => document.referrer,
  hasConsent: () => getTrackingConsent() === 'accepted',
  getLocalStorage: () => localStorage,
  getSessionStorage: () => sessionStorage,
  isTestEntry: () => isAnalyticsTestActive(internalTrackingTest),
});

function getAcquisitionProperties() {
  return acquisitionTracker.getCurrentProperties();
}

function getDeviceType() {
  if (window.innerWidth <= 680) {
    return 'mobile';
  }

  if (window.innerWidth <= 980) {
    return 'tablet';
  }

  return 'desktop';
}

function getCheckoutPriceLabel(plan: PlanKey = state.selectedPlan) {
  if (state.activeOffer === 'focuscorp') {
    return focuscorpOfferPriceLabel;
  }

  if (state.activeOffer === 'firstpick') {
    return firstpickOfferPriceLabel;
  }

  if (state.activeOffer === 'founder') {
    return founderOfferPriceLabel;
  }

  return plan === 'annual' ? annualPriceLabel : plan === 'monthly' ? monthlyPriceLabel : weeklyPriceLabel;
}

function getCheckoutAnalyticsProperties(plan: PlanKey = state.selectedPlan) {
  const acquisition = getAcquisitionProperties();
  const parsedPrice = parsePriceLabel(getCheckoutPriceLabel(plan));

  return {
    billing_period: plan === 'annual' ? 'year' : 'week',
    billing_period_count: plan === 'monthly' ? 4 : 1,
    campaign: acquisition.utm_campaign ?? 'unknown',
    click_id: acquisition.fbclid ?? acquisition.gclid ?? null,
    content: acquisition.utm_content ?? null,
    currency: 'EUR',
    device_type: getDeviceType(),
    landing_page: acquisition.landing_path,
    medium: acquisition.utm_medium ?? null,
    offer: state.activeOffer ?? 'standard',
    plan,
    price: parsedPrice?.amount ?? (plan === 'annual' ? 22.99 : plan === 'monthly' ? 10.99 : 3.99),
    referrer: acquisition.referrer_origin,
    referral_code: state.referralCode ?? null,
    source: acquisition.utm_source ?? acquisition.referrer_origin,
    term: acquisition.utm_term ?? null,
  };
}

function getCheckoutAttribution() {
  const acquisition = acquisitionTracker.getCheckoutProperties();

  return {
    campaign: acquisition.utm_campaign ?? 'unknown',
    campaignId: acquisition.campaign_id ?? acquisition.af_c_id ?? null,
    adsetId: acquisition.adset_id ?? acquisition.af_adset_id ?? null,
    adId: acquisition.ad_id ?? acquisition.af_ad_id ?? null,
    capturedAt: acquisition.captured_at ?? null,
    clickId: acquisition.fbclid ?? acquisition.gclid ?? null,
    content: acquisition.utm_content ?? null,
    deviceType: getDeviceType(),
    landingPage: acquisition.landing_path,
    medium: acquisition.utm_medium ?? null,
    referrer: acquisition.referrer_origin,
    source: acquisition.utm_source ?? acquisition.referrer_origin,
    term: acquisition.utm_term ?? null,
  };
}

function persistEmbeddedCheckout() {
  if (!embeddedCheckoutClientSecret || !embeddedCheckoutSessionId) {
    sessionStorage.removeItem(EMBEDDED_CHECKOUT_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(EMBEDDED_CHECKOUT_STORAGE_KEY, JSON.stringify({
    analyticsTest: isAnalyticsTestActive(internalTrackingTest),
    clientSecret: embeddedCheckoutClientSecret,
    checkoutUi,
    canApplyPromotionCode: checkoutCanApplyPromotionCode,
    expiresAt: checkoutExpiresAt,
    plan: state.selectedPlan,
    sessionId: embeddedCheckoutSessionId,
  }));
}

function restoreEmbeddedCheckoutFromStorage() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(EMBEDDED_CHECKOUT_STORAGE_KEY) ?? 'null') as {
      analyticsTest?: unknown;
      clientSecret?: unknown;
      checkoutUi?: unknown;
      canApplyPromotionCode?: unknown;
      expiresAt?: unknown;
      plan?: unknown;
      sessionId?: unknown;
    } | null;

    if (
      !stored ||
      (stored.checkoutUi !== undefined && stored.checkoutUi !== 'embedded' && stored.checkoutUi !== 'elements') ||
      typeof stored.clientSecret !== 'string' ||
      !stored.clientSecret.includes('_secret_') ||
      (stored.plan !== 'annual' && stored.plan !== 'monthly' && stored.plan !== 'weekly') ||
      typeof stored.sessionId !== 'string' ||
      !stored.sessionId.startsWith('cs_')
    ) {
      sessionStorage.removeItem(EMBEDDED_CHECKOUT_STORAGE_KEY);
      return false;
    }

    // A restored Stripe session keeps its original server-side attribution and
    // Meta consent. Never reuse one across production/testing boundaries.
    if ((stored.analyticsTest === true) !== isAnalyticsTestActive(internalTrackingTest)) return false;

    checkoutUi = stored.checkoutUi === 'elements' ? 'elements' : 'embedded';
    checkoutCanApplyPromotionCode = stored.canApplyPromotionCode === true;
    checkoutExpiresAt = typeof stored.expiresAt === 'number' && Number.isFinite(stored.expiresAt) ? stored.expiresAt : null;
    embeddedCheckoutClientSecret = stored.clientSecret;
    embeddedCheckoutSessionId = stored.sessionId;
    state.selectedPlan = stored.plan;
    embeddedCheckoutError = null;
    embeddedCheckoutAbandonCaptured = false;
    embeddedCheckoutSubmitted = false;
    return true;
  } catch {
    sessionStorage.removeItem(EMBEDDED_CHECKOUT_STORAGE_KEY);
    return false;
  }
}

function trackMetaFunnelView() {
  if (!optionalTrackingEnabled || hasTrackedMetaFunnelView) {
    return;
  }

  const isOnboardingPath =
    window.location.pathname === '/start' ||
    getWebOnboardingScreenForPath(window.location.pathname) !== null;

  if (!isOnboardingPath) {
    return;
  }

  if (trackMetaPixelEvent('ViewContent', {
    content_category: 'web_funnel',
    content_name: 'FocusCorp focus plan',
  })) {
    hasTrackedMetaFunnelView = true;
  }
}

function runPostHog(operation: (posthog: typeof import('posthog-js').default) => void) {
  return deferredPostHog.run((posthog) => {
    if (!hasInitializedPostHog) {
      posthog.init(posthogToken, {
        api_host: posthogHost,
        autocapture: true,
        before_send: (event) => {
          if (!event || getTrackingConsent() !== 'accepted') return null;
          event.properties = { ...acquisitionTracker.sanitizeEventProperties(event.properties), analytics_environment: analyticsEnvironment };
          return event;
        },
        capture_pageleave: true,
        capture_pageview: true,
        person_profiles: 'identified_only',
        persistence: 'localStorage+cookie',
      });
      posthog.register({
        analytics_environment: analyticsEnvironment,
        analytics_platform: 'web',
      });
      hasInitializedPostHog = true;
    } else if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
    }
    operation(posthog);
  });
}

function initializeOptionalTracking() {
  if (getTrackingConsent() !== 'accepted') {
    optionalTrackingEnabled = false;
    return;
  }
  if (optionalTrackingEnabled) return;

  optionalTrackingEnabled = true;
  deferredPostHog.enable();

  if (isPostHogConfigured) {
    void runPostHog(() => {});
  }

  initializeMetaPixel(metaPixelId, { consent: 'accepted' });
  const pixel = (window as Window & { fbq?: (...args: unknown[]) => void }).fbq;
  pixel?.('consent', 'grant');
  lastTrackedFunnelView = null;
  captureLandingViewOnce();
  trackMetaFunnelView();
  // Consent can be granted after the quiz has rendered. Record only what is
  // currently visible, never reconstruct the visitor's unconsented history.
  if (state.view === 'quiz' && activeQuizRunId) {
    captureQuizStartOnce();
    markStepViewed(currentScreen);
  }
}

function clearOptionalTrackingData() {
  acquisitionTracker.clear();

  const cookieDomains = [
    '',
    window.location.hostname,
    `.${window.location.hostname.replace(/^www\./u, '')}`,
  ];

  for (const name of ['_fbc', '_fbp']) {
    for (const domain of cookieDomains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ''}`;
    }
  }

  hasTrackedMetaFunnelView = false;
}

function disableOptionalTracking() {
  optionalTrackingEnabled = false;
  deferredPostHog.disable();
  userIdentificationPromises.clear();

  if (hasInitializedPostHog) {
    deferredPostHog.peek()?.opt_out_capturing();
  }

  const pixel = (window as Window & { fbq?: (...args: unknown[]) => void }).fbq;
  pixel?.('consent', 'revoke');
  clearOptionalTrackingData();
}

function getCurrentMetaTrackingContext(eventId: string) {
  const acquisition = acquisitionTracker.getCheckoutProperties();

  return buildMetaTrackingContext({
    consent: getTrackingConsent(),
    cookieHeader: document.cookie,
    eventId,
    eventSourceUrl: window.location.href,
    fbclid: acquisition.fbclid ?? null,
  });
}

async function trackMetaRegistration(provider: PendingWebSignup['provider'], userId: string) {
  if (!supabase || getTrackingConsent() !== 'accepted') {
    return;
  }

  const eventId = `registration:${userId}`;
  const metaTracking = getCurrentMetaTrackingContext(eventId);
  const customData = {
    content_name: 'FocusCorp web account',
    registration_method: provider,
    status: 'completed',
  };

  trackMetaPixelEvent('CompleteRegistration', customData, eventId);

  try {
    const { error } = await supabase.functions.invoke('trackMetaWebEvent', {
      body: {
        customData,
        eventName: 'CompleteRegistration',
        metaTracking,
      },
    });

    if (error) {
      console.warn('[tracking] Meta registration event failed', error);
    }
  } catch (error) {
    console.warn('[tracking] Meta registration event failed', error);
  }
}

function capture(event: string, properties: Record<string, unknown> = {}) {
  if (optionalTrackingEnabled && isPostHogConfigured) {
    const capturedProperties = {
      ...(activeQuizRunId ? { quiz_run_id: activeQuizRunId } : {}),
      ...(identityEntryEnabled ? { entry_variant: 'identity' } : {}),
      ...(state.answers.entryIdentity ? { entry_identity: state.answers.entryIdentity } : {}),
      ...(offerBadgeEligible && offerBadgeAssigned ? {experiment_id:'offer_badge_v1',offer_badge_arm:offerBadgeArm} : {}),
      ...properties,
    };
    const occurredAt = new Date();
    // The SDK sends its automatic page view immediately but batches custom
    // events for three seconds. Short visits must not lose their funnel steps.
    // Fetch retains SDK retries and uses keepalive for these small payloads.
    void runPostHog((posthog) => posthog.capture(event, capturedProperties, {
      send_instantly: true,
      transport: 'fetch',
      timestamp: occurredAt,
    }));
  }
}

function captureLandingViewOnce() {
  if (!optionalTrackingEnabled || hasCapturedLandingView) {
    return;
  }
  if (offerBadgeEligible && !offerBadgeAssigned) {
    void offerBadgeArmReady.then(()=>captureLandingViewOnce());
    return;
  }

  capture('landing_view', getAcquisitionProperties());
  hasCapturedLandingView = true;
}

const userIdentificationPromises = new Map<string, Promise<boolean>>();
function identifyUser(user: User | null): Promise<void> {
  if (!user) {
    userIdentificationPromises.clear();
    return Promise.resolve();
  }
  if (!optionalTrackingEnabled || !isPostHogConfigured) return Promise.resolve();
  let identification = userIdentificationPromises.get(user.id);
  if (!identification) {
    identification = performUserIdentification(user).then((identified) => {
      if (!identified) userIdentificationPromises.delete(user.id);
      return identified;
    });
    userIdentificationPromises.set(user.id, identification);
  }
  return identification.then(() => {});
}

async function performUserIdentification(user: User): Promise<boolean> {
  if (!optionalTrackingEnabled || !isPostHogConfigured || !user) {
    return false;
  }

  let classificationAvailable = true;
  let isTestAccount = false;
  let testAccountLabel: string | null = null;

  if (supabase) {
    try {
      const { data, error } = await supabase.rpc('get_my_analytics_classification');

      if (error) {
        classificationAvailable = false;
        console.warn('[analytics] test-account classification failed', error);
      } else {
        const classification = Array.isArray(data) ? data[0] : data;
        isTestAccount = classification?.is_test_account === true;
        testAccountLabel = typeof classification?.test_label === 'string'
          ? classification.test_label
          : null;
      }
    } catch (error) {
      classificationAvailable = false;
      console.warn('[analytics] test-account classification failed', error);
    }
  }

  let identified = false;
  const delivered = await runPostHog((posthog) => {
    if (state.user?.id !== user.id) return;
    posthog.identify(user.id, {
      $internal_or_test_user: isTestAccount,
      analytics_account_type: isTestAccount ? 'test' : 'production',
      email: user.email,
      test_account_label: isTestAccount ? testAccountLabel ?? 'Test account' : null,
    });
    identified = true;
  });
  return delivered && identified && classificationAvailable;
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeReferralCode(value: string) {
  return value.replace(/[^a-z0-9]/giu, '').toUpperCase();
}

type PixelButtonOptions = {
  block?: boolean;
  disabled?: boolean;
  small?: boolean;
  submit?: boolean;
  tone?: 'green' | 'blue';
};

function pixelButton(label: string, action: string | null, options: PixelButtonOptions = {}) {
  const classes = [
    'pixel-btn',
    options.tone ?? '',
    options.small ? 'small' : '',
    options.block ? 'block' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const actionAttr = action ? ` data-action="${escapeHtml(action)}"` : '';

  return `
    <button class="${classes}" type="${options.submit ? 'submit' : 'button'}"${actionAttr} ${options.disabled ? 'disabled' : ''}>
      <span>${escapeHtml(label)}</span>
    </button>
  `;
}

// The app draws the wall-clock digits as 4px pixel glyphs, not with a font.
// Same glyph table as MinimalOfficeScene's CLOCK_GLYPHS.
const CLOCK_GLYPHS: Record<string, readonly string[]> = {
  '0': ['111', '101', '101', '101', '111'],
  '1': ['010', '110', '010', '010', '111'],
  '2': ['111', '001', '111', '100', '111'],
  '3': ['111', '001', '111', '001', '111'],
  '4': ['101', '101', '111', '001', '001'],
  '5': ['111', '100', '111', '001', '111'],
  '6': ['111', '100', '111', '101', '111'],
  '7': ['111', '001', '001', '001', '001'],
  '8': ['111', '101', '111', '101', '111'],
  '9': ['111', '101', '111', '001', '111'],
  ':': ['0', '1', '0', '1', '0'],
};

const CLOCK_PIXEL = 4;
const CLOCK_GLYPH_GAP = 4;

function renderClockGlyphs(value: string) {
  let offset = 0;
  let pixels = '';

  for (const char of value) {
    const glyph = CLOCK_GLYPHS[char];

    if (!glyph) {
      continue;
    }

    glyph.forEach((row, rowIndex) => {
      [...row].forEach((bit, columnIndex) => {
        if (bit === '1') {
          pixels += `<i style="left:${offset + columnIndex * CLOCK_PIXEL}px;top:${rowIndex * CLOCK_PIXEL}px"></i>`;
        }
      });
    });
    offset += (glyph[0]?.length ?? 0) * CLOCK_PIXEL + CLOCK_GLYPH_GAP;
  }

  return `<span class="clock-pixels" style="width:${Math.max(0, offset - CLOCK_GLYPH_GAP)}px">${pixels}</span>`;
}

type OfficeSceneOptions = {
  bob?: 'standing' | 'mini' | 'none';
  bubbleCentered?: boolean;
  bubbleText?: string | null;
  shade?: 'dark' | 'light' | null;
  timerText?: string;
};

/**
 * CSS recreation of the iOS MinimalOfficeScene: sky band, brick wall with the
 * session timer clock, baseboard, tiled floor, and the starter workstation
 * (desk, chair, laptop) with Bob. Uses the exact sprites shipped in the app.
 */
function renderOfficeScene({
  bob = 'standing',
  bubbleCentered = false,
  bubbleText = null,
  shade = null,
  timerText = '25:00',
}: OfficeSceneOptions = {}) {
  return `
    <div class="office-scene" aria-hidden="true">
      <div class="scene-sky"></div>
      <div class="scene-topborder"></div>
      <div class="scene-wall"></div>
      <div class="scene-divider"></div>
      <div class="scene-floor"></div>
      <div class="scene-clock">${renderClockGlyphs(timerText)}</div>
      <div class="scene-workstation">
        ${bob === 'mini' ? '<div class="scene-bob-desk"></div>' : ''}
        <div class="ws ws-chair"></div>
        <div class="ws ws-desk ws-d-tl"></div>
        <div class="ws ws-desk ws-d-tm"></div>
        <div class="ws ws-desk ws-d-tr"></div>
        <div class="ws ws-desk ws-d-bl"></div>
        <div class="ws ws-desk ws-d-bm"></div>
        <div class="ws ws-desk ws-d-br"></div>
        <div class="ws ws-laptop"></div>
        ${bob === 'standing' ? '<div class="scene-bob"></div>' : ''}
        ${bob === 'mini' ? '<div class="scene-bob-runner"></div>' : ''}
      </div>
      ${
        bubbleText
          ? `
            <div class="scene-bubble ${bubbleCentered ? 'centered' : ''}">
              <div class="scene-bubble-surface">${escapeHtml(bubbleText)}</div>
              <div class="scene-bubble-tail"></div>
            </div>
          `
          : ''
      }
      ${shade ? `<div class="scene-shade ${shade === 'light' ? 'light' : ''}"></div>` : ''}
    </div>
  `;
}

function getStringField(form: FormData, name: string) {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

function getWebAuthCallbackUrl() {
  acquisitionTracker.preserveForRedirect();
  return new URL(getTestFunnelPath('/auth/callback'), window.location.origin).toString();
}

function getTestFunnelPath(path: string) {
  const current = new URLSearchParams(window.location.search);
  if (current.get('qz') === 'fc1' && current.get('flow') === 'account' && !isAnalyticsTestActive(internalTrackingTest)) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('qz', 'fc1');
    url.searchParams.set('flow', 'account');
    return `${url.pathname}${url.search}${url.hash}`;
  }
  if (!isAnalyticsTestActive(internalTrackingTest)) return path;
  const url = new URL(path, window.location.origin);
  url.searchParams.set('qz', 'tst1');
  return `${url.pathname}${url.search}${url.hash}`;
}

function getBillingAuthCallbackUrl() {
  const url = new URL('/auth/callback', window.location.origin);
  url.searchParams.set('next', 'manage-subscription');
  return url.toString();
}

function isWebAuthCallbackRoute() {
  return initialWebAuthCallbackRoute;
}

function hasWebAuthCallbackError() {
  return initialWebAuthCallbackHadError;
}

function cleanWebAuthCallbackUrl(destination = '/') {
  if (window.location.pathname === '/auth/callback') {
    window.history.replaceState({}, '', getTestFunnelPath(destination));
  }
}

function storePendingBillingAuth() {
  try {
    sessionStorage.setItem(PENDING_BILLING_AUTH_STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function hasPendingBillingAuth() {
  if (initialWebAuthCallbackNext === 'manage-subscription') {
    return true;
  }

  try {
    const startedAt = Number(sessionStorage.getItem(PENDING_BILLING_AUTH_STORAGE_KEY));
    return Number.isFinite(startedAt) &&
      startedAt > 0 &&
      Date.now() - startedAt >= 0 &&
      Date.now() - startedAt <= PENDING_BILLING_AUTH_MAX_AGE_MS;
  } catch {
    return false;
  }
}

function clearPendingBillingAuth() {
  try {
    sessionStorage.removeItem(PENDING_BILLING_AUTH_STORAGE_KEY);
  } catch {
    // The OAuth result remains usable even when private storage cleanup fails.
  }
}

function syncAccountAnswersFromForm(form: HTMLFormElement) {
  const formData = new FormData(form);
  const userName = getStringField(formData, 'name');
  const startupName = getStringField(formData, 'companyName');

  state.answers = {
    ...state.answers,
    ...(userName ? { userName } : {}),
    ...(startupName ? { startupName } : {}),
  };
}

function isPaidQuizFunnel() {
  const code = new URLSearchParams(window.location.search).get('qz') ?? internalTrackingTest.funnelCode;
  return code === 'fc1' || code === 'tst1';
}

function getPostSignupView(): FunnelView {
  return isPaidQuizFunnel() ? 'checkout' : 'referral';
}

function storePendingWebSignup(pending: PendingWebSignup) {
  try {
    sessionStorage.setItem(PENDING_WEB_SIGNUP_STORAGE_KEY, JSON.stringify(pending));
    return true;
  } catch {
    return false;
  }
}

function persistPendingWebSignup(
  provider: PendingWebSignup['provider'],
  options: { createdAt?: number; expectedEmail?: string } = {},
) {
  const pending: PendingWebSignup = {
    answers: buildPersistedOnboarding().answers,
    createdAt: options.createdAt ?? Date.now(),
    ...(options.expectedEmail ? { expectedEmail: options.expectedEmail.toLowerCase() } : {}),
    provider,
    version: 1,
  };

  return storePendingWebSignup(pending);
}

function bindPendingWebSignupToUser(pending: PendingWebSignup, userId: string) {
  const boundPending: PendingWebSignup = {
    ...pending,
    authenticatedUserId: userId,
  };
  storePendingWebSignup(boundPending);
  return boundPending;
}

function loadPendingWebSignup(): PendingWebSignup | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PENDING_WEB_SIGNUP_STORAGE_KEY) ?? 'null') as Partial<PendingWebSignup> | null;
    const ageMs = typeof parsed?.createdAt === 'number' ? Date.now() - parsed.createdAt : -1;
    const isValid =
      parsed?.version === 1 &&
      typeof parsed.createdAt === 'number' &&
      ageMs >= 0 &&
      ageMs <= PENDING_WEB_SIGNUP_MAX_AGE_MS &&
      (parsed.provider === 'apple' || parsed.provider === 'email') &&
      (parsed.authenticatedUserId === undefined || typeof parsed.authenticatedUserId === 'string') &&
      (parsed.expectedEmail === undefined || typeof parsed.expectedEmail === 'string') &&
      parsed.answers !== null &&
      typeof parsed.answers === 'object' &&
      !Array.isArray(parsed.answers);

    if (!isValid) {
      sessionStorage.removeItem(PENDING_WEB_SIGNUP_STORAGE_KEY);
      return null;
    }

    return parsed as PendingWebSignup;
  } catch {
    try {
      sessionStorage.removeItem(PENDING_WEB_SIGNUP_STORAGE_KEY);
    } catch {
      // Storage can be unavailable in strict/private browser modes.
    }
    return null;
  }
}

function clearPendingWebSignup() {
  try {
    sessionStorage.removeItem(PENDING_WEB_SIGNUP_STORAGE_KEY);
  } catch {
    // A successful signup must not be downgraded if storage cleanup is blocked.
  }
}

function getPendingEmailSignupFromUser(user: User): PendingWebSignup | null {
  const metadata = user.user_metadata;
  const startedAt = Date.parse(
    typeof metadata.web_signup_started_at === 'string' ? metadata.web_signup_started_at : '',
  );
  const answers = metadata.onboarding_answers;

  if (
    metadata.web_signup_version !== 1 ||
    !Number.isFinite(startedAt) ||
    Date.now() - startedAt < 0 ||
    Date.now() - startedAt > PENDING_WEB_SIGNUP_MAX_AGE_MS ||
    !answers ||
    typeof answers !== 'object' ||
    Array.isArray(answers)
  ) {
    return null;
  }

  return {
    answers: answers as OnboardingAnswers,
    createdAt: startedAt,
    ...(user.email ? { expectedEmail: user.email.toLowerCase() } : {}),
    provider: 'email',
    version: 1,
  };
}

function isSessionCompatibleWithPendingSignup(session: Session, pending: PendingWebSignup) {
  if (pending.authenticatedUserId) {
    return pending.authenticatedUserId === session.user.id;
  }

  const lastSignInAt = Date.parse(session.user.last_sign_in_at ?? '');
  const isFreshSignIn =
    Number.isFinite(lastSignInAt) && lastSignInAt >= pending.createdAt - 5 * 60 * 1000;

  if (!isFreshSignIn) {
    return false;
  }

  if (
    pending.expectedEmail &&
    session.user.email?.toLowerCase() !== pending.expectedEmail.toLowerCase()
  ) {
    return false;
  }

  if (pending.provider === 'apple') {
    const providers = new Set<string>([
      ...(Array.isArray(session.user.app_metadata.providers)
        ? session.user.app_metadata.providers.filter((provider): provider is string => typeof provider === 'string')
        : []),
      ...(typeof session.user.app_metadata.provider === 'string'
        ? [session.user.app_metadata.provider]
        : []),
      ...(session.user.identities ?? []).map((identity) => identity.provider),
    ]);

    return providers.has('apple');
  }

  return true;
}

function getFriendlyAccountError(error: unknown) {
  const message = error instanceof Error ? error.message.toLowerCase() : '';

  if (message.includes('already registered') || message.includes('already exists')) {
    return 'An account already uses this email. Sign in from the FocusCorp app, or continue with Apple.';
  }

  if (message.includes('password')) {
    return 'Use a password with at least 8 characters.';
  }

  if (message.includes('email')) {
    return 'Enter a valid email address and try again.';
  }

  if (message.includes('network') || message.includes('fetch')) {
    return 'Check your internet connection and try again.';
  }

  return 'We could not create your account. Please try again.';
}

function getStepName(screen: number) {
  switch (screen) {
    case ONBOARDING_INTRO_SCREEN:
      return 'intro';
    case ONBOARDING_MINI_SESSION_SCREEN:
      return 'mini_focus_session';
    case ONBOARDING_SOCIAL_PROOF_SCREEN:
      return 'social_proof';
    default:
      return `question_${screen}`;
  }
}

function getProgressPercent() {
  const visibleScreens = [
    2, 5, 8, 9, 10, 11, 12, 13, 14, 15, 16, 24,
    ONBOARDING_PLAN_LOADING_SCREEN,
    ONBOARDING_SOCIAL_PROOF_SCREEN,
  ].filter(screen=>nameStepArm!=='no_name'||screen!==5);
  const visibleIndex = visibleScreens.indexOf(currentScreen);

  if (visibleIndex < 0) {
    return 0;
  }

  return Math.round(((visibleIndex + 1) / visibleScreens.length) * 100);
}

function clearFlowTimers() {
  if (guidedPlanFrame !== null) { cancelAnimationFrame(guidedPlanFrame); guidedPlanFrame = null; }
  if (miniTimerId !== null) {
    window.clearInterval(miniTimerId);
    miniTimerId = null;
  }

  miniPhaseTimeoutIds.forEach((id) => window.clearTimeout(id));
  miniPhaseTimeoutIds = [];

  if (planTimerId !== null) {
    window.clearTimeout(planTimerId);
    planTimerId = null;
  }
}

function pushMiniTimeout(callback: () => void, delay: number) {
  miniPhaseTimeoutIds.push(window.setTimeout(callback, delay));
}

function markStepViewed(screen: number) {
  if (lastViewedStep === screen) {
    return;
  }

  stepViewedAtMs = Date.now();
  if (!optionalTrackingEnabled) return;
  lastViewedStep = screen;
  capture('quiz_step_viewed', {
    progress_percent: getProgressPercent(),
    screen_index: screen,
    screen_name: getStepName(screen),
  });
  metaFunnelSignals.stepViewed(screen, getStepName(screen));
}

function completeStep(screen: number, properties: Record<string, unknown> = {}) {
  const progressPercent = getProgressPercent();
  capture('quiz_step_completed', {
    progress_percent: progressPercent,
    screen_index: screen,
    screen_name: getStepName(screen),
    time_on_step_ms: stepViewedAtMs ? Math.max(0, Date.now() - stepViewedAtMs) : null,
    ...properties,
  });
  metaFunnelSignals.stepCompleted(screen, getStepName(screen));
  hasTrackedMetaQuizHalfway = trackMetaQuizHalfway(
    progressPercent,
    hasTrackedMetaQuizHalfway,
    undefined,
    getTrackingConsent(),
  );
}

function captureQuizExit(reason: 'close_button' | 'page_leave') {
  if (state.view !== 'quiz' || quizExitCaptured) {
    return;
  }

  quizExitCaptured = true;
  capture('quiz_abandoned', {
    progress_percent: getProgressPercent(),
    reason,
    screen_index: currentScreen,
    screen_name: getStepName(currentScreen),
    time_in_quiz_ms: quizStartedAtMs ? Math.max(0, Date.now() - quizStartedAtMs) : null,
  });
}

function setStatus(message: string | null, tone: StatusTone | null = null) {
  state.statusMessage = message;
  state.statusTone = tone;
}

function setView(view: FunnelView) {
  if (state.view === 'firstpickReward' && view !== 'firstpickReward' && firstpickRewardTimerId !== null) {
    window.clearTimeout(firstpickRewardTimerId);
    firstpickRewardTimerId = null;
  }

  if (state.view === 'embeddedCheckout' && view !== 'embeddedCheckout') {
    captureEmbeddedCheckoutAbandoned('back_button');
    clearEmbeddedCheckoutSession();
  }

  state.view = view;

  if (view === 'referral') {
    capture('referral_viewed');
  }

  if (view === 'checkout') {
    capture('checkout_viewed', getCheckoutAnalyticsProperties());
  }

  state.founderOfferVisible = false;
  state.checkoutExitIntent = null;
  setStatus(null);
  render();
}

type OnboardingHistoryMode = 'none' | 'push' | 'replace';

function syncOnboardingRoute(screen: number, historyMode: OnboardingHistoryMode) {
  if (historyMode === 'none') {
    return;
  }

  const path = getWebOnboardingPath(screen);

  if (!path) {
    return;
  }

  const historyState = { focuscorpOnboardingScreen: screen };
  let destination = isAnalyticsTestActive(internalTrackingTest)
    ? getTestFunnelPath(path)
    : new URLSearchParams(window.location.search).get('qz') === 'fc1'
    ? `${path}?qz=fc1`
    : isInternalTrackingTestActive(internalTrackingTest)
    ? `${path}?qz=${encodeURIComponent(internalTrackingTest.funnelCode)}`
    : path;


  if (historyMode === 'replace' || window.location.pathname === path) {
    window.history.replaceState(historyState, '', destination);
    return;
  }

  window.history.pushState(historyState, '', destination);
}

function startQuizScreenEffects(screen: number) {
  // The mini session orchestrates DOM nodes, so it must run after render().
  if (screen === ONBOARDING_MINI_SESSION_SCREEN) {
    startMiniSession();
  }

  if (screen === ONBOARDING_PLAN_LOADING_SCREEN) {
    if (guidedOnboardingEnabled) {
      const startedAt = performance.now();
      const update = () => {
        if (state.view !== 'quiz' || currentScreen !== screen) return;
        const progress = getGuidedPlanProgress(performance.now() - startedAt);
        const ring = root.querySelector<HTMLElement>('.guided-plan-ring');
        ring?.style.setProperty('--plan-progress', `${progress.percent}%`);
        ring?.setAttribute('aria-valuenow', String(Math.floor(progress.percent)));
        const label = ring?.querySelector('strong');
        if (label) label.textContent = `${Math.floor(progress.percent)}%`;
        root.querySelectorAll('.guided-plan-stage').forEach((item, index) => {
          item.classList.toggle('is-complete', index < progress.completedSteps);
          item.classList.toggle('is-current', index === progress.completedSteps);
        });
        if (progress.percent === 100) {
          guidedPlanFrame = null;
          planTimerId = window.setTimeout(() => {
            completeStep(screen, { auto_advanced: true });
            goToScreen(ONBOARDING_SOCIAL_PROOF_SCREEN);
          }, 350);
        } else guidedPlanFrame = window.requestAnimationFrame(update);
      };
      update();
      return;
    }
    planTimerId = window.setTimeout(() => {
      completeStep(ONBOARDING_PLAN_LOADING_SCREEN, { auto_advanced: true });
      startReaction(
        `Plan locked, ${getUserName(state.answers)}. Bob knows the goal. Now we show up.`,
        ONBOARDING_SOCIAL_PROOF_SCREEN,
      );
    }, 2600);
  }
}

function goToScreen(screen: number, historyMode: OnboardingHistoryMode = 'push') {
  const requestedScreen=screen;
  screen = resolveNameStepScreen(screen,nameStepArm);
  if (screen!==requestedScreen && historyMode==='none') historyMode='replace';
  clearFlowTimers();
  reactionText = null;
  state.pendingScreen = null;
  currentScreen = screen;
  state.view = 'quiz';
  syncOnboardingRoute(screen, historyMode);
  markStepViewed(screen);
  render();
  if (guidedOnboardingEnabled) window.scrollTo({ top: 0, behavior: 'instant' });
  startQuizScreenEffects(screen);
}

function captureQuizStartOnce() {
  if (!optionalTrackingEnabled || !activeQuizRunId) return;
  if (isPostHogConfigured && capturedQuizStartRunId !== activeQuizRunId) {
    capture('quiz_started');
    capturedQuizStartRunId = activeQuizRunId;
  }
  if (metaQuizStartRunId !== activeQuizRunId && trackMetaPixelCustomEvent('QuizStarted', {
    content_category: 'web_funnel',
  }, undefined, getTrackingConsent())) {
    metaQuizStartRunId = activeQuizRunId;
  }
}

function startQuiz(
  initialScreen = ONBOARDING_INTRO_SCREEN,
  historyMode: OnboardingHistoryMode = 'push',
) {
  const requestedScreen=initialScreen;
  initialScreen = resolveNameStepScreen(initialScreen,nameStepArm);
  if (initialScreen!==requestedScreen && historyMode==='none') historyMode='replace';
  void preloadRunnerSprites();
  clearFlowTimers();
  currentScreen = initialScreen;
  reactionText = null;
  lastViewedStep = null;
  stepViewedAtMs = null;
  activeQuizRunId = crypto.randomUUID();
  metaFunnelSignals.beginRun(activeQuizRunId);
  quizStartedAtMs = Date.now();
  quizExitCaptured = false;
  hasTrackedMetaQuizHalfway = false;
  if (initialScreen === ONBOARDING_INTRO_SCREEN) {
    clearQuizPlan(window.location.search);
    resetGuestQuizDraft(guestCheckoutPreviewEnabled
      ? guestCheckoutTestEnabled ? import.meta.env.VITE_GUEST_TEST_SUPABASE_URL : import.meta.env.VITE_GUEST_SUPABASE_URL
      : undefined, sessionStorage);
  }
  try { sessionStorage.removeItem(`focuscorp_plan_answers_${internalTrackingTest.funnelCode}`); } catch { /* Storage may be unavailable in private browsing. */ }
  state.answers = {
    dailyPhoneHours: 3,
  };
  if (identityEntryEnabled) {
    try {
      if (initialScreen === ONBOARDING_INTRO_SCREEN) sessionStorage.removeItem(identityAnswerStorageKey);
      else state.answers.entryIdentity = normalizeEntryIdentity(sessionStorage.getItem(identityAnswerStorageKey));
    } catch { /* The quiz also works when browser storage is unavailable. */ }
  }
  state.accountSaveRetryAvailable = false;
  state.activeOffer = null;
  state.checkoutExitIntent = null;
  state.founderOfferDismissed = false;
  state.founderOfferVisible = false;
  state.view = 'quiz';
  syncOnboardingRoute(initialScreen, historyMode);
  trackMetaFunnelView();
  captureQuizStartOnce();
  markStepViewed(initialScreen);
  render();
  startQuizScreenEffects(initialScreen);
}

function startReaction(message: string, nextScreen: number) {
  if (guidedOnboardingEnabled) {
    if (nextScreen === ONBOARDING_FINAL_SCREEN) completeWebOnboarding();
    else goToScreen(nextScreen);
    return;
  }
  reactionText = message;
  state.pendingScreen = nextScreen;
  render();
  if (guidedOnboardingEnabled) window.scrollTo({ top: 0, behavior: 'instant' });
}

function completeWebOnboarding() {
  quizExitCaptured = true;
  const completedOnboarding = buildPersistedOnboarding();
  capture('quiz_completed', {
    daily_phone_hours: getDailyPhoneHours(completedOnboarding.answers),
    daily_session_target: completedOnboarding.personalization.dailySessionTarget,
    default_focus_duration_minutes: completedOnboarding.personalization.defaultFocusDurationMinutes,
    focus_killers_count: completedOnboarding.answers.focusKillers?.length ?? 0,
    goal_count: completedOnboarding.answers.thirtyDayGoals?.length ?? 0,
  });
  trackMetaPixelCustomEvent('QuizCompleted', {
    content_category: 'web_funnel',
  }, undefined, getTrackingConsent());
  state.accountSaveRetryAvailable = false;
  window.history.replaceState({}, '', getTestFunnelPath('/'));
  setView('account');
  if (guidedOnboardingEnabled) window.scrollTo({ top: 0, behavior: 'instant' });
}

function finishReaction() {
  const nextScreen = state.pendingScreen;
  reactionText = null;
  state.pendingScreen = null;

  if (nextScreen === ONBOARDING_FINAL_SCREEN) {
    completeWebOnboarding();
    return;
  }

  goToScreen(nextScreen ?? currentScreen + 1);
}

// Bob's native onboarding start is already beside the workstation's left
// access corridor. Match that shortest native route here: run up the desk's
// left side, then right behind it into the chair. The onboarding return is the
// exact reverse (left behind the desk, then down to the idle spot).
type RunLeg = {
  // While Bob is behind the workstation he must render behind the desk and
  // laptop (the app z-orders by foot depth).
  behind: boolean;
  depthSwitchProgress?: number;
  direction: 'right' | 'left' | 'up' | 'down';
  ms: number;
  x: number;
  y: number;
};

// The app moves Bob 6px per 90ms tick (~67px/s); leg durations match that.
const MINI_RUN_IN: RunLeg[] = [
  { behind: true, depthSwitchProgress: 0.18, direction: 'up', ms: 1080, x: 0, y: -72 },
  { behind: true, direction: 'right', ms: 1620, x: 109, y: -72 },
];

const MINI_RUN_OUT: RunLeg[] = [
  { behind: true, direction: 'left', ms: 1620, x: 0, y: -72 },
  { behind: false, depthSwitchProgress: 0.85, direction: 'down', ms: 1080, x: 0, y: 0 },
];

// Each run direction swaps in a different sprite strip. Swapping
// background-image to a file the browser has not decoded yet paints the
// element blank for a frame, which made Bob vanish for a beat on every
// first-use turn. Decode every strip before the run starts (and keep the
// Image references alive so the decoded bitmaps stay cached).
const RUNNER_SPRITE_URLS = [
  '/assets/office/bob-run-strip.png',
  '/assets/office/bob-run-left-strip.png',
  '/assets/office/bob-run-back-strip.png',
  '/assets/office/bob-run-front-strip.png',
  '/assets/office/bob-idle-strip.png',
];

const runnerSpriteCache: HTMLImageElement[] = [];
let runnerSpritesReady: Promise<void> | null = null;

function preloadRunnerSprites() {
  runnerSpritesReady ??= Promise.all(
    RUNNER_SPRITE_URLS.map((src) => {
      const image = new Image();
      image.src = src;
      runnerSpriteCache.push(image);
      return image.decode().catch(() => {});
    }),
  ).then(() => {});

  return runnerSpritesReady;
}

// Money lands in piles beside the desk, same spots as the app's
// moneyPilePositions (workstation coordinates).
const MONEY_PILE_POSITIONS = [
  { left: 98, top: 100 },
  { left: 110, top: 106 },
  { left: 120, top: 98 },
  { left: 76, top: 108 },
];

function runLegs(runner: HTMLElement, legs: RunLeg[], onDone: () => void) {
  // Timer-driven (not rAF) so a backgrounded tab can't stall the flow; the
  // 30ms lead gives the browser a paint between renders and leg targets.
  let elapsed = 30;

  legs.forEach((leg, index) => {
    pushMiniTimeout(() => {
      runner.className = `scene-bob-runner running-${leg.direction}`;
      if (leg.depthSwitchProgress === undefined) {
        runner.style.zIndex = leg.behind ? '1' : '4';
      } else {
        pushMiniTimeout(() => {
          runner.style.zIndex = leg.behind ? '1' : '4';
        }, Math.round(leg.ms * leg.depthSwitchProgress));
      }
      runner.style.transitionDuration = `${leg.ms}ms`;
      runner.style.transform = `translate(${leg.x}px, ${leg.y}px)`;

      if (index === legs.length - 1) {
        pushMiniTimeout(onDone, leg.ms + 30);
      }
    }, elapsed);
    elapsed += leg.ms + 16;
  });
}

// Drops a money sprite from above the screen straight down onto a pile spot
// next to the desk, where it stays — like the app's falling money.
function spawnMoneyDrop(workstation: HTMLElement, dropIndex: number) {
  const position = MONEY_PILE_POSITIONS[dropIndex % MONEY_PILE_POSITIONS.length];

  if (!position) {
    return;
  }

  const drop = document.createElement('div');
  drop.className = 'scene-money-drop';
  drop.style.left = `${position.left}px`;
  drop.style.top = `${position.top}px`;
  drop.style.transform = 'translateY(-640px)';
  workstation.appendChild(drop);
  pushMiniTimeout(() => {
    drop.style.transform = 'translateY(0)';
  }, 40);
}

/**
 * Mirrors the app's mini session: Bob takes the workstation's left corridor
 * to the chair, sits facing forward while the wall clock counts down and
 * money drops from above, retraces that corridor, then triggers full-screen
 * confetti before advancing. The scene is rendered once and mutated in place
 * so animations never restart.
 */
function startMiniSession() {
  state.miniRemaining = 10;

  const runner = root.querySelector<HTMLElement>('.scene-bob-runner');
  const deskBob = root.querySelector<HTMLElement>('.scene-bob-desk');
  const workstation = root.querySelector<HTMLElement>('.scene-workstation');
  const clock = root.querySelector<HTMLElement>('.scene-clock');

  if (!runner || !deskBob || !workstation || !clock) {
    return;
  }

  // Phase 1: take the native left-side route to the chair — but only once every
  // directional sprite strip is decoded, so no turn swaps in a blank frame.
  // The cache is warmed at quiz start, so this normally resolves instantly.
  // Hidden tabs defer image decoding, so race a timeout to keep the session
  // timer-driven — a backgrounded tab must never stall the flow.
  const decodeGrace = new Promise<void>((resolve) => pushMiniTimeout(resolve, 400));

  void Promise.race([preloadRunnerSprites(), decodeGrace]).then(() => {
    if (!runner.isConnected) {
      return;
    }

    startMiniRun(runner, deskBob, workstation, clock);
  });
}

function startMiniRun(
  runner: HTMLElement,
  deskBob: HTMLElement,
  workstation: HTMLElement,
  clock: HTMLElement,
) {
  let moneyDropCount = 0;

  runLegs(runner, MINI_RUN_IN, () => {
    runner.style.display = 'none';
    deskBob.classList.add('visible');

    // Phase 2: work while the clock counts down; money falls immediately
    // and then every 5 seconds, like the app.
    spawnMoneyDrop(workstation, moneyDropCount++);
    pushMiniTimeout(() => spawnMoneyDrop(workstation, moneyDropCount++), 5000);

    miniTimerId = window.setInterval(() => {
      state.miniRemaining -= 1;
      clock.innerHTML = renderClockGlyphs(`00:${String(Math.max(0, state.miniRemaining)).padStart(2, '0')}`);

      if (state.miniRemaining > 0) {
        return;
      }

      if (miniTimerId !== null) {
        window.clearInterval(miniTimerId);
        miniTimerId = null;
      }

      // Phase 3: money fades out while Bob runs back out around the desk.
      workstation.classList.add('money-fading');
      deskBob.classList.remove('visible');
      runner.style.display = '';
      runLegs(runner, MINI_RUN_OUT, () => {
        runner.className = 'scene-bob-runner standing';

        // Phase 4: confetti, then advance — same beat as the app.
        launchConfetti();
        pushMiniTimeout(() => {
          completeStep(ONBOARDING_MINI_SESSION_SCREEN, { mini_session_completed: true });
          goToScreen(ONBOARDING_DAY_ONE_SCREEN);
        }, 1500);
      });
    }, 1000);
  });
}

// Full-screen confetti burst with the app's origins, colors, and timing.
function launchConfetti() {
  const layer = root.querySelector<HTMLElement>('.confetti-layer');

  if (!layer) {
    return;
  }

  const colors = ['#EE7A2F', '#F2BD3C', '#FFFFFF', '#79D96B', '#F45B69'];
  const origins = [0.1, 0.25, 0.5, 0.75, 0.9];
  let pieceIndex = 0;
  const pieces = origins.flatMap((originX) =>
    Array.from({ length: 18 }, () => {
      const color = colors[pieceIndex % colors.length] ?? '#EE7A2F';
      pieceIndex += 1;
      const size = 6 + Math.round(Math.random() * 4);
      const duration = 800 + Math.round(Math.random() * 600);
      const delay = Math.round(Math.random() * 120);
      const jitterX = -12 + Math.round(Math.random() * 24);
      const translateX = -80 + Math.round(Math.random() * 160);
      const translateY = 200 + Math.round(Math.random() * 300);
      const rotate = Math.round(Math.random() * 360);

      return `
        <span
          class="confetti-piece"
          style="left:calc(${originX * 100}% + ${jitterX}px);top:20%;width:${size}px;height:${size}px;background:${color};animation-duration:${duration}ms;animation-delay:${delay}ms;--tx:${translateX}px;--ty:${translateY}px;--rot:${rotate}deg"
        ></span>
      `;
    }),
  );

  layer.innerHTML = pieces.join('');
}

function buildGeneratedPlan(answers: OnboardingAnswers): GeneratedPlan {
  const persistedAnswers = buildPersistedAnswers(answers,{omitMissingName:nameStepArm==='no_name'});
  const personalization = buildOnboardingPersonalizationProfile(persistedAnswers);
  const risks = persistedAnswers.biggestFears?.join(' + ') || 'Giving up halfway';

  return {
    blockList: personalization.blockedAppsCategory,
    dailyTarget: `${personalization.dailySessionTarget} session(s) per day`,
    firstSession: `${personalization.defaultFocusDurationMinutes} minutes`,
    focusWindow: persistedAnswers.sharpestTime || 'Mid morning',
    mission: `Build ${personalization.startupName}`,
    risk: risks,
  };
}

function buildPersistedOnboarding(): PersistedOnboarding {
  const answers = buildPersistedAnswers(state.answers,{omitMissingName:nameStepArm==='no_name'});
  const personalization = buildOnboardingPersonalizationProfile(answers);
  const experimentRunId = offerBadgeEligible ? reportableExperimentRunId(getTrackingConsent(),activeQuizRunId) : null;

  return {
    answers,
    generatedPlan: buildGeneratedPlan(answers),
    personalization,
    ...(experimentRunId ? {experimentRunId} : {}),
  };
}

async function requireSession() {
  if (!supabase) {
    throw new Error('Supabase is not configured for this deployment.');
  }

  const { data, error } = await supabase.auth.getSession();

  if (error) {
    throw error;
  }

  const session = data.session;

  if (!session) {
    throw new Error('Create an account before continuing.');
  }

  state.session = session;
  state.user = session.user;
  void identifyUser(session.user);
  return session;
}

async function persistOnboardingProfile(user: User) {
  if (!supabase) {
    throw new Error('Supabase is not configured for this deployment.');
  }

  const payload = buildPersistedOnboarding();
  const name = payload.answers.userName ?? 'Founder';
  const companyName = payload.personalization.startupName;
  const timer = createDevelopmentTimer('account_persistence');

  // Apple OAuth does not return the user's full name. The web onboarding has
  // already collected the preferred name and company, so make those values
  // authoritative across auth metadata and the canonical app tables.
  const { error: metadataError } = await supabase.auth.updateUser({
    data: {
      company_name: companyName,
      onboarding_answers: payload.answers,
      onboarding_personalization: payload.personalization,
      username: name,
    },
  });

  if (metadataError) {
    throw metadataError;
  }

  const profileCompanyResult = await supabase.rpc('ensure_profile_and_company', {
    preferred_company_name: companyName,
    preferred_username: name,
  });

  if (profileCompanyResult.error) {
    throw profileCompanyResult.error;
  }

  // ensure_profile_and_company intentionally avoids overwriting existing
  // rows. Apply the explicit onboarding values after it has guaranteed that
  // both rows exist, then save the full web onboarding payload.
  const [profileResult, companyResult, onboardingResult] = await Promise.all([
    supabase.from('profiles').update({ email: user.email ?? null, username: name }).eq('id', user.id),
    supabase.from('companies').update({ company_name: companyName }).eq('user_id', user.id),
    supabase.from('onboarding_profiles').upsert(
      {
        answers: payload.answers,
        company_name: companyName,
        completed_at: new Date().toISOString(),
        generated_plan: payload.generatedPlan,
        name,
        personalization: payload.personalization,
        user_id: user.id,
      },
      { onConflict: 'user_id' },
    ),
  ]);

  timer.finish('metadata_profile_company_and_onboarding');

  if (profileResult.error) {
    throw profileResult.error;
  }

  if (companyResult.error) {
    throw companyResult.error;
  }

  if (onboardingResult.error) {
    throw onboardingResult.error;
  }
}

async function finalizeAuthenticatedWebSignup(user: User, provider: PendingWebSignup['provider']) {
  state.user = user;
  void identifyUser(user);
  await persistOnboardingProfile(user);
  const { error: intentCleanupError } = await supabase?.auth.updateUser({
    data: {
      web_signup_started_at: null,
      web_signup_version: null,
    },
  }) ?? { error: null };

  if (intentCleanupError) {
    throw intentCleanupError;
  }

  clearPendingWebSignup();
  capture('account_created', {
    email_confirmation_required: false,
    event_id: `registration:${user.id}`,
    $insert_id: `registration:${user.id}`,
    provider,
  });
  void trackMetaRegistration(provider, user.id);
}

async function submitAccount(form: HTMLFormElement) {
  if (state.isBusy) {
    return;
  }

  if (!supabase) {
    setStatus('Supabase is not configured yet. Add the web environment variables before testing account creation.', 'error');
    render();
    return;
  }

  syncAccountAnswersFromForm(form);
  const formData = new FormData(form);
  const email = getStringField(formData, 'email');
  const password = getStringField(formData, 'password');
  const persisted = buildPersistedOnboarding();
  const name = persisted.answers.userName || 'Founder';
  const companyName = persisted.personalization.startupName;
  const signupStartedAt = Date.now();

  if (!email || !password) {
    setStatus('Enter an email and password to continue.', 'error');
    render();
    return;
  }

  if (!persistPendingWebSignup('email', { createdAt: signupStartedAt, expectedEmail: email })) {
    setStatus('Your browser could not save this onboarding session. Allow site storage and try again.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  state.accountSaveRetryAvailable = false;
  setStatus(null);
  render();
  const timer = createDevelopmentTimer('account_signup');
  let authenticatedSignupUserId: string | null = null;

  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      options: {
        data: {
          company_name: companyName,
          onboarding_answers: persisted.answers,
          onboarding_personalization: persisted.personalization,
          username: name,
          web_signup_started_at: new Date(signupStartedAt).toISOString(),
          web_signup_version: 1,
        },
        emailRedirectTo: getWebAuthCallbackUrl(),
      },
      password,
    });
    timer.checkpoint('supabase_auth');

    if (error) {
      throw error;
    }

    if (!data.session || !data.user) {
      setStatus('Check your email to confirm your account. We will continue automatically when you return.', 'success');
      capture('account_confirmation_required', { provider: 'email' });
      timer.finish('confirmation_required');
      return;
    }

    state.session = data.session;
    authenticatedSignupUserId = data.user.id;
    const pending = loadPendingWebSignup();
    if (pending) {
      bindPendingWebSignupToUser(pending, data.user.id);
    }
    await finalizeAuthenticatedWebSignup(data.user, 'email');
    timer.checkpoint('critical_persistence');

    if (checkoutCompletionSessionId) {
      checkoutCompletionStarted = false;
      checkoutCompletionState = 'loading';
      setView('checkoutComplete');
      return;
    }

    setView(getPostSignupView());
    timer.finish('click_to_ready');
  } catch (error) {
    timer.finish('failed');
    if (authenticatedSignupUserId) {
      state.accountSaveRetryAvailable = true;
    } else {
      clearPendingWebSignup();
    }
    capture('account_setup_failed', {
      error_type: error instanceof Error ? error.name : 'unknown',
      provider: 'email',
    });
    setStatus(
      authenticatedSignupUserId
        ? 'Your account is connected, but we could not save your plan yet. Check your connection and try again.'
        : getFriendlyAccountError(error),
      'error',
    );
  } finally {
    state.isBusy = false;
    render();
  }
}

async function startAppleSignup(form: HTMLFormElement) {
  if (state.isBusy) {
    return;
  }

  if (!supabase) {
    setStatus('Apple sign-up is not configured for this deployment yet.', 'error');
    render();
    return;
  }

  const companyInput = form.querySelector<HTMLInputElement>('[name="companyName"]');
  if (companyInput && !companyInput.reportValidity()) return;
  syncAccountAnswersFromForm(form);

  if (!persistPendingWebSignup('apple')) {
    setStatus('Your browser could not save this onboarding session. Allow site storage and try again.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  setStatus(null);
  capture('account_signup_started', { provider: 'apple' });
  render();
  const timer = createDevelopmentTimer('account_signup_apple');

  try {
    const { error } = await supabase.auth.signInWithOAuth({
      options: {
        redirectTo: getWebAuthCallbackUrl(),
      },
      provider: 'apple',
    });

    if (error) {
      throw error;
    }

    timer.finish('redirect_started');
  } catch (error) {
    timer.finish('failed');
    clearPendingWebSignup();
    capture('account_setup_failed', {
      error_type: error instanceof Error ? error.name : 'unknown',
      provider: 'apple',
    });
    setStatus(getFriendlyAccountError(error), 'error');
    state.isBusy = false;
    render();
  }
}

async function resumePendingWebSignup(session: Session, pending: PendingWebSignup) {
  const boundPending = pending.authenticatedUserId
    ? pending
    : bindPendingWebSignupToUser(pending, session.user.id);
  state.answers = buildPersistedAnswers(boundPending.answers);
  state.session = session;
  state.user = session.user;
  state.view = 'account';
  state.isBusy = true;
  state.accountSaveRetryAvailable = false;
  setStatus(null);

  try {
    await finalizeAuthenticatedWebSignup(session.user, boundPending.provider);
    cleanWebAuthCallbackUrl();
    state.isBusy = false;
    state.accountSaveRetryAvailable = false;

    if (checkoutCompletionSessionId) {
      checkoutCompletionStarted = false;
      checkoutCompletionState = 'loading';
      state.view = 'checkoutComplete';
    } else {
      state.view = getPostSignupView();
      if (state.view === 'checkout') capture('checkout_viewed', getCheckoutAnalyticsProperties());
      else capture('referral_viewed');
    }
  } catch (error) {
    state.isBusy = false;
    state.view = 'account';
    state.accountSaveRetryAvailable = true;
    capture('account_setup_failed', {
      error_type: error instanceof Error ? error.name : 'unknown',
      provider: boundPending.provider,
      resumed_after_redirect: true,
    });
    setStatus('Your account is connected, but we could not save your plan yet. Check your connection and try again.', 'error');
  }

  return true;
}

async function retryAuthenticatedWebSignup() {
  if (state.isBusy || !supabase) {
    return;
  }

  const pending = loadPendingWebSignup();
  const { data, error } = await supabase.auth.getSession();

  if (
    error ||
    !data.session ||
    !pending?.authenticatedUserId ||
    pending.authenticatedUserId !== data.session.user.id
  ) {
    state.accountSaveRetryAvailable = false;
    setStatus('We could not verify the connected account. Please start account creation again.', 'error');
    render();
    return;
  }

  await resumePendingWebSignup(data.session, pending);
  render();
}

function getFriendlyBillingSignInError(error: unknown) {
  const message = error instanceof Error ? error.message.toLowerCase() : '';

  if (message.includes('invalid login') || message.includes('invalid credentials')) {
    return 'The email or password is incorrect. Use the same login as your FocusCorp purchase.';
  }

  if (message.includes('email not confirmed')) {
    return 'Confirm your email address before signing in.';
  }

  if (message.includes('network') || message.includes('fetch')) {
    return 'Check your internet connection and try again.';
  }

  return 'We could not sign you in. Please try again.';
}

function openManageSubscriptionPage(historyMode: 'push' | 'replace' = 'push') {
  const method = historyMode === 'replace' ? 'replaceState' : 'pushState';
  window.history[method]({}, '', '/manage-subscription');
  setView('manageSubscription');
}

async function signInForSubscriptionManagement(form: HTMLFormElement) {
  if (state.isBusy || !supabase) {
    if (!supabase) {
      setStatus('Account sign-in is temporarily unavailable.', 'error');
      render();
    }
    return;
  }

  const formData = new FormData(form);
  const email = getStringField(formData, 'email');
  const password = getStringField(formData, 'password');

  if (!email || !password) {
    setStatus('Enter your email and password to continue.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  setStatus(null);
  render();

  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error || !data.session) {
      throw error ?? new Error('Missing authenticated session.');
    }

    state.session = data.session;
    state.user = data.user;
    void identifyUser(data.user);
    setStatus('Signed in. You can now open your secure billing portal.', 'success');
  } catch (error) {
    setStatus(getFriendlyBillingSignInError(error), 'error');
  } finally {
    state.isBusy = false;
    render();
  }
}

async function signInWithAppleForSubscriptionManagement() {
  if (state.isBusy || !supabase) {
    if (!supabase) {
      setStatus('Apple sign-in is temporarily unavailable.', 'error');
      render();
    }
    return;
  }

  if (!storePendingBillingAuth()) {
    setStatus('Your browser could not save this sign-in attempt. Allow site storage and try again.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  setStatus(null);
  render();

  try {
    const { error } = await supabase.auth.signInWithOAuth({
      options: { redirectTo: getBillingAuthCallbackUrl() },
      provider: 'apple',
    });

    if (error) {
      throw error;
    }
  } catch (error) {
    clearPendingBillingAuth();
    state.isBusy = false;
    setStatus(getFriendlyBillingSignInError(error), 'error');
    render();
  }
}

async function openStripeSubscriptionPortal() {
  if (state.isBusy || !supabase || !state.session) {
    setStatus('Sign in before opening subscription management.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  setStatus(null);
  render();

  try {
    const { data, error } = await supabase.functions.invoke<StripePortalResponse>('createStripePortal');
    const portalUrl = data?.url?.trim() ?? '';

    if (error) {
      const response = (error as { context?: unknown }).context;
      let serverMessage = data?.error?.trim() ?? '';

      if (!serverMessage && response instanceof Response) {
        try {
          const payload = await response.clone().json() as StripePortalResponse;
          serverMessage = payload.error?.trim() ?? '';
        } catch {
          // Fall back to the friendly generic message below.
        }
      }

      throw new Error(serverMessage || 'Subscription management is temporarily unavailable.');
    }

    if (!portalUrl) {
      throw new Error(data?.error ?? 'No web subscription was found for this account.');
    }

    const parsedUrl = new URL(portalUrl);

    if (parsedUrl.protocol !== 'https:' || parsedUrl.hostname !== 'billing.stripe.com') {
      throw new Error('Stripe returned an invalid billing portal URL.');
    }

    window.location.assign(parsedUrl.toString());
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    setStatus(
      message.includes('No web subscription')
        ? 'No web subscription was found for this account. Apple purchases are managed in the App Store.'
        : 'Subscription management is temporarily unavailable. Please try again.',
      'error',
    );
    state.isBusy = false;
    render();
  }
}

async function signOutFromSubscriptionManagement() {
  if (state.isBusy || !supabase) {
    return;
  }

  state.isBusy = true;
  setStatus(null);
  render();
  const { error } = await supabase.auth.signOut();

  if (error) {
    state.isBusy = false;
    setStatus('We could not sign you out. Please try again.', 'error');
    render();
    return;
  }

  state.session = null;
  state.user = null;
  state.isBusy = false;
  setStatus('Signed out.', 'success');
  render();
}

async function applyReferral(form: HTMLFormElement) {
  if (state.isBusy) {
    return;
  }

  const timer = createDevelopmentTimer('referral');
  referralDraft = getStringField(new FormData(form), 'referralCode');
  referralExpanded = true;
  const code = normalizeReferralCode(referralDraft);
  timer.checkpoint('validation');

  if (!/^[A-Z0-9]{8,12}$/u.test(code)) {
    setStatus('Enter a valid referral code or continue without one.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  setStatus(null);
  state.referralCode = code;
  capture('referral_entered', {
    code_suffix: code.slice(-4),
    referral_code: code,
  });
  render();

  try {
    const session = await requireSession();
    timer.checkpoint('session');

    // FOCUSCORP is an offer code, not a free-entitlement grant. Do not call
    // the legacy referral endpoint for it because that endpoint still grants
    // 30 RevenueCat entitlement days before returning.
    if (code === 'FOCUSCORP') {
      state.activeOffer = 'focuscorp';
      state.selectedPlan = 'annual';
      capture('referral_offer_unlocked', { offer: 'focuscorp', referral_code: code });
      referralResolvedOnPlans = true;
      setView('checkout');
      timer.finish('focuscorp_offer_ready');
      return;
    }

    const cacheKey = `${session.user.id}:${code}`;
    let payload = successfulReferralCache.get(cacheKey) ?? null;

    if (!payload) {
      const response = await fetch(`${supabaseUrl}/functions/v1/applyReferralCode`, {
        body: JSON.stringify({ code, ...(import.meta.env.DEV ? { performance: true } : {}) }),
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: supabaseAnonKey,
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      timer.checkpoint('application');
      payload = (await response.json().catch(() => null)) as ReferralResponse | null;

      if (!response.ok) {
        throw new Error(payload?.error ?? 'Referral failed. Please try again.');
      }

      successfulReferralCache.set(cacheKey, payload ?? { status: 'rewarded' });
    } else {
      timer.checkpoint('successful_cache_hit');
    }

    capture('referral_success', {
      code_suffix: code.slice(-4),
      premium_granted_until: payload?.premiumGrantedUntil,
      referral_code: code,
      reward_type: payload?.rewardType ?? 'premium',
    });
    referralResolvedOnPlans = true;

    if (
      code === 'FIRSTPICK' &&
      payload?.rewardType === 'inventory_item' &&
      payload.grantedItemId === 'firstpick-neon-sign'
    ) {
      state.activeOffer = 'firstpick';
      state.selectedPlan = 'annual';
      setView('firstpickReward');
      capture('firstpick_reward_reveal_viewed', { granted_item_id: payload.grantedItemId });
      firstpickRewardTimerId = window.setTimeout(() => {
        firstpickRewardTimerId = null;
        setView('checkout');
      }, 3500);
      timer.finish('firstpick_reward_revealed');
      return;
    }

    if (payload?.rewardType === 'inventory_item') {
      state.activeOffer = null;
      state.selectedPlan = 'annual';
      setView('checkout');
      timer.finish('inventory_reward_checkout_ready');
      return;
    }

    state.premiumSource = 'referral';
    setView('download');
    timer.finish('click_to_ready');
  } catch (error) {
    timer.finish('failed');
    capture('referral_failed', {
      error_type: error instanceof Error ? error.name : 'unknown',
      referral_code: code,
    });
    setStatus(error instanceof Error ? error.message : 'Referral failed. Please try again.', 'error');
  } finally {
    state.isBusy = false;
    render();
  }
}

async function startCheckout() {
  if (isPaidQuizFunnel() && !state.activeOffer && !state.planTermsAccepted) {
    setStatus('Please agree to the terms before continuing.', 'error');
    render();
    return;
  }
  if (state.isBusy || state.view === 'embeddedCheckout') {
    return;
  }

  if (!supabase) {
    setStatus('Supabase is not configured yet. Add the web environment variables before testing checkout.', 'error');
    render();
    return;
  }

  if (!prepareStripe()) {
    setStatus('Stripe is not configured yet. Add VITE_STRIPE_PUBLISHABLE_KEY before testing checkout.', 'error');
    render();
    return;
  }

  state.isBusy = true;
  setStatus(null);
  const analytics = getCheckoutAnalyticsProperties();
  const metaCheckoutEventId = `checkout:${crypto.randomUUID()}`;
  const metaTracking = getCurrentMetaTrackingContext(metaCheckoutEventId);
  if (isPaidQuizFunnel() && referralViewedOnPlans && !referralResolvedOnPlans) {
    capture('referral_skipped', { placement: 'plan_selection' });
    referralResolvedOnPlans = true;
  }
  capture('checkout_started', analytics);
  capture('checkout_session_requested', analytics);
  render();
  checkoutReadyTimer = createDevelopmentTimer('checkout_click_to_ready');

  const attemptVersion = ++checkoutAttemptVersion;
  const isCurrentAttempt = () => attemptVersion === checkoutAttemptVersion && state.view === 'embeddedCheckout';
  const prepareSession = async () => {
    await requireSession();
    if (!isCurrentAttempt()) throw new Error('Checkout was closed.');
    checkoutReadyTimer?.checkpoint('session');
    const { data, error } = await supabase.functions.invoke<CheckoutResponse>('createStripeCheckout', {
      body: {
        attribution: getCheckoutAttribution(),
        checkoutUi: isPaidQuizFunnel() ? 'elements' : 'embedded',
        metaTracking,
        offer: state.activeOffer,
        onboarding: buildPersistedOnboarding(),
        plan: state.selectedPlan,
        ...(import.meta.env.DEV ? { performance: true } : {}),
        referralStatus: state.activeOffer ? `${state.activeOffer}_offer` : 'not_applied',
      },
    });
    checkoutReadyTimer?.checkpoint('checkout_session_creation');

    if (error) {
      throw error;
    }

    if (
      (data?.checkoutUi !== undefined && data.checkoutUi !== 'embedded' && data.checkoutUi !== 'elements') ||
      !data?.clientSecret ||
      !data.clientSecret.includes('_secret_') ||
      !data.checkoutSessionId?.startsWith('cs_')
    ) {
      throw new Error('Checkout did not return a valid Embedded Checkout Session.');
    }

    if (!isCurrentAttempt()) throw new Error('Checkout was closed.');
    checkoutUi = data.checkoutUi === 'elements' ? 'elements' : 'embedded';
    checkoutCanApplyPromotionCode = data.canApplyPromotionCode === true;
    checkoutExpiresAt = typeof data.expiresAt === 'number' && Number.isFinite(data.expiresAt) ? data.expiresAt : null;
    embeddedCheckoutClientSecret = data.clientSecret;
    embeddedCheckoutSessionId = data.checkoutSessionId;
    embeddedCheckoutError = null;
    embeddedCheckoutAbandonCaptured = false;
    embeddedCheckoutSubmitted = false;
    embeddedCheckoutViewedCaptured = false;
    persistEmbeddedCheckout();
    capture('checkout_session_created', {
      ...analytics,
      session_id: data.checkoutSessionId,
    });
    if (metaTracking.consent === 'accepted') {
      trackMetaPixelEvent(
        'InitiateCheckout',
        {
          content_category: 'subscription',
          content_name: `${state.activeOffer ?? 'standard'} ${state.selectedPlan}`,
          currency: analytics.currency,
          value: analytics.price,
        },
        metaCheckoutEventId,
      );
    }
    return data.clientSecret;
  };

  embeddedCheckoutError = null;
  embeddedCheckoutAbandonCaptured = false;
  embeddedCheckoutSubmitted = false;
  embeddedCheckoutViewedCaptured = false;
  checkoutUi = isPaidQuizFunnel() ? 'pending' : 'embedded';
  embeddedCheckoutClientSecretPromise = withPaymentLoadTimeout(prepareSession());
  // Stripe can prepare its iframe while our server creates the session.
  state.isBusy = false;
  state.view = 'embeddedCheckout';
  window.scrollTo({ top: 0, behavior: 'instant' });
  window.history.pushState({ focuscorp_view: 'embedded_checkout' }, '', getTestFunnelPath('/checkout'));
  render();

  try {
    await embeddedCheckoutClientSecretPromise;
  } catch (error) {
    if (!isCurrentAttempt()) return;
    checkoutReadyTimer?.finish('failed');
    checkoutReadyTimer = null;
    const failureProperties = {
      ...analytics,
      error_type: error instanceof Error ? error.name : 'unknown',
      stage: 'session_creation',
    };
    capture('checkout_session_failed', failureProperties);
    capture('checkout_failed', failureProperties);
    // Invalidate the preparation before a late network response can persist
    // its secret or overwrite a newer attempt.
    destroyEmbeddedCheckout();
    embeddedCheckoutError = error instanceof Error ? error.message : 'Checkout failed. Please try again.';
    state.isBusy = false;
    render();
  }
}

function handleEmbeddedCheckoutAnalytics(event: StripeEmbeddedCheckoutAnalyticsEventUnion) {
  const analytics = getCheckoutAnalyticsProperties();

  if (event.eventType === 'checkoutRendered') {
    dismissEmbeddedCheckoutLoading();
    checkoutReadyTimer?.finish('payment_form_rendered');
    checkoutReadyTimer = null;

    if (!embeddedCheckoutViewedCaptured) {
      embeddedCheckoutViewedCaptured = true;
      capture('checkout_embedded_viewed', analytics);
    }
    return;
  }

  if (event.eventType === 'checkoutSubmitted' && !embeddedCheckoutSubmitted) {
    embeddedCheckoutSubmitted = true;
    capture('checkout_payment_started', analytics);
    return;
  }

  if (event.eventType === 'checkoutSubmitFailed') {
    capture('checkout_session_failed', {
      ...analytics,
      failure_reason: event.details.failureReason,
      stage: 'payment_submission',
    });
  }
}

function dismissEmbeddedCheckoutLoading() {
  checkoutResizeObserver?.disconnect();
  checkoutResizeObserver = null;
  root.querySelector<HTMLElement>('.embedded-checkout-loading')?.remove();
}

function destroyEmbeddedCheckout() {
  checkoutResizeObserver?.disconnect();
  checkoutResizeObserver = null;
  checkoutAttemptVersion += 1;
  embeddedCheckoutClientSecretPromise = null;
  checkoutReadyTimer = null;
  embeddedCheckout?.destroy();
  embeddedCheckout = null;
  embeddedCheckoutMountPromise = null;
}

function clearEmbeddedCheckoutSession() {
  destroyEmbeddedCheckout();
  checkoutUi = 'embedded';
  checkoutCanApplyPromotionCode = false;
  checkoutExpiresAt = null;
  embeddedCheckoutClientSecret = null;
  embeddedCheckoutSessionId = null;
  embeddedCheckoutError = null;
  embeddedCheckoutSubmitted = false;
  embeddedCheckoutViewedCaptured = false;
  sessionStorage.removeItem(EMBEDDED_CHECKOUT_STORAGE_KEY);
}

function captureEmbeddedCheckoutAbandoned(reason: 'back_button' | 'browser_back' | 'page_leave') {
  if (state.view !== 'embeddedCheckout' || embeddedCheckoutAbandonCaptured || embeddedCheckoutSubmitted) {
    return;
  }

  embeddedCheckoutAbandonCaptured = true;
  capture('checkout_abandoned', {
    ...getCheckoutAnalyticsProperties(),
    reason,
    session_id: embeddedCheckoutSessionId,
  });
}

function cancelEmbeddedCheckout(reason: 'back_button' | 'browser_back') {
  captureEmbeddedCheckoutAbandoned(reason);
  clearEmbeddedCheckoutSession();
  state.view = 'checkout';
  setStatus('Checkout was closed. Your plan is still selected.', 'error');
  window.history.replaceState({}, '', getTestFunnelPath('/'));
  render();
}

function armCheckoutExitGuard() {
  if (state.view !== 'checkout' || state.founderOfferVisible || checkoutExitGuardArmed) {
    return;
  }

  window.history.pushState({ focuscorp_view: 'checkout_offer' }, '', window.location.href);
  checkoutExitGuardArmed = true;
}

function completeCheckoutExit(intent: CheckoutExitIntent) {
  saveQuizPlan(state.answers, 'account', window.location.search);
  state.founderOfferVisible = false;
  state.checkoutExitIntent = null;
  state.founderOfferDismissed = true;
  checkoutExitGuardArmed = false;
  capture('founder_offer_declined', { exit_intent: intent });
  window.history.replaceState({}, '', quizPlanHomePath(window.location.search));
  setView('landing');
}

function requestCheckoutExit(intent: CheckoutExitIntent) {
  if (state.activeOffer === 'founder' || state.founderOfferDismissed) {
    completeCheckoutExit(intent);
    return;
  }

  state.checkoutExitIntent = intent;
  state.founderOfferVisible = true;
  capture('founder_offer_viewed', {
    exit_intent: intent,
    previous_offer: state.activeOffer ?? 'standard',
  });
  render();
  queueMicrotask(() => root.querySelector<HTMLElement>('[data-action="founder-offer-accept"]')?.focus());
}

async function mountEmbeddedCheckout() {
  if (
    state.view !== 'embeddedCheckout' ||
    embeddedCheckout ||
    embeddedCheckoutMountPromise ||
    embeddedCheckoutError
  ) {
    return;
  }

  if (!prepareStripe() || (!embeddedCheckoutClientSecret && !embeddedCheckoutClientSecretPromise)) {
    embeddedCheckoutError = 'This Checkout Session is missing or expired. Create a new payment attempt.';
    render();
    return;
  }

  const mountTarget = root.querySelector<HTMLElement>('#embedded-checkout');

  if (!mountTarget) {
    return;
  }

  const attemptVersion = checkoutAttemptVersion;
  const clientSecret = embeddedCheckoutClientSecret;
  const clientSecretPromise = embeddedCheckoutClientSecretPromise;
  const isCurrentAttempt = () => attemptVersion === checkoutAttemptVersion && state.view === 'embeddedCheckout';
  embeddedCheckoutMountPromise = (async () => {
    const mountTimer = createDevelopmentTimer('embedded_checkout_mount');

    try {
      const stripe = await withPaymentLoadTimeout(stripePromise!);
      mountTimer.checkpoint('stripe_sdk_ready');

      if (!stripe) {
        throw new Error('Stripe.js could not be loaded. Check the publishable key and try again.');
      }

      // Wait for the actual renderer: older deployed endpoints may still
      // return an embedded secret even when the client requests Elements.
      if (checkoutUi === 'pending') await clientSecretPromise;
      if (!isCurrentAttempt() || !mountTarget.isConnected) return;
      if (checkoutUi === 'elements') {
        mountTarget.closest('.stripe-checkout-panel')?.classList.add('elements-checkout-panel');
        const controller = await mountPaymentElements({
          stripe, clientSecret: embeddedCheckoutClientSecret!, target: mountTarget,
          canApplyPromotionCode: checkoutCanApplyPromotionCode, isCurrent: isCurrentAttempt,
          expiresAt: checkoutExpiresAt,
          summaryTarget: root.querySelector<HTMLElement>('[data-payment-summary]'),
          expiryTarget: root.querySelector<HTMLElement>('[data-checkout-expiry]'),
          ready: () => {
            if (!isCurrentAttempt()) return;
            dismissEmbeddedCheckoutLoading();
            checkoutReadyTimer?.finish('payment_form_rendered');
            checkoutReadyTimer = null;
            if (!embeddedCheckoutViewedCaptured) {
              embeddedCheckoutViewedCaptured = true;
              capture('checkout_embedded_viewed', { ...getCheckoutAnalyticsProperties(), checkout_ui: 'elements' });
            }
          },
          submitted: () => {
            embeddedCheckoutSubmitted = true;
            capture('checkout_payment_started', { ...getCheckoutAnalyticsProperties(), checkout_ui: 'elements' });
          },
          failed: () => {
            embeddedCheckoutSubmitted = false;
            capture('checkout_session_failed', { ...getCheckoutAnalyticsProperties(), stage: 'payment_submission', checkout_ui: 'elements' });
          },
          complete: (sessionId) => {
            if (isCurrentAttempt() && sessionId === embeddedCheckoutSessionId) {
              window.location.assign(getTestFunnelPath(`/checkout/complete?session_id=${encodeURIComponent(sessionId)}`));
            }
          },
          fatal: (message) => {
            if (!isCurrentAttempt()) return;
            embeddedCheckoutError = message;
            capture('checkout_session_failed', { ...getCheckoutAnalyticsProperties(), stage: 'elements_load' });
            destroyEmbeddedCheckout();
            render();
          },
        });
        if (!isCurrentAttempt() || !mountTarget.isConnected) { controller.destroy(); return; }
        embeddedCheckout = controller;
        mountTimer.finish('elements_mounted');
        return;
      }

      const checkout = await stripe.createEmbeddedCheckoutPage({
        ...(clientSecret
          ? { clientSecret }
          : { fetchClientSecret: () => clientSecretPromise! }),
        onAnalyticsEvent: (event) => {
          if (isCurrentAttempt()) handleEmbeddedCheckoutAnalytics(event);
        },
        onComplete: () => {
          if (!isCurrentAttempt()) return;
          embeddedCheckoutSubmitted = true;
          const sessionId = embeddedCheckoutSessionId;
          if (sessionId) {
            window.location.assign(getTestFunnelPath(`/checkout/complete?session_id=${encodeURIComponent(sessionId)}`));
          }
        },
      });
      mountTimer.checkpoint('checkout_instance_created');

      if (!isCurrentAttempt() || !mountTarget.isConnected) {
        checkout.destroy();
        return;
      }

      embeddedCheckout = checkout;
      checkout.mount(mountTarget);
      mountTimer.finish('mounted');
      // Stripe resizes its cross-origin iframe when the form is ready. Some
      // versions omit checkoutRendered; a content-sized frame is our fallback.
      const checkFormSize = () => {
        if (isCurrentAttempt() && embeddedCheckoutClientSecret && mountTarget.getBoundingClientRect().height > 300) {
          dismissEmbeddedCheckoutLoading();
          checkoutReadyTimer?.finish('payment_form_sized');
          checkoutReadyTimer = null;
          if (!embeddedCheckoutViewedCaptured) {
            embeddedCheckoutViewedCaptured = true;
            capture('checkout_embedded_viewed', getCheckoutAnalyticsProperties());
          }
        }
      };
      checkoutResizeObserver = new ResizeObserver(checkFormSize);
      checkoutResizeObserver.observe(mountTarget);
      checkFormSize();

    } catch (error) {
      if (!isCurrentAttempt()) return;
      embeddedCheckoutError = error instanceof Error ? error.message : 'Embedded Checkout could not be loaded.';
      capture('checkout_session_failed', {
        ...getCheckoutAnalyticsProperties(),
        error_type: error instanceof Error ? error.name : 'unknown',
        stage: 'embedded_mount',
      });
      render();
    } finally {
      if (isCurrentAttempt()) embeddedCheckoutMountPromise = null;
    }
  })();

  await embeddedCheckoutMountPromise;
}

async function verifyCheckoutCompletion() {
  if (state.view !== 'checkoutComplete' || checkoutCompletionStarted) {
    return;
  }

  checkoutCompletionStarted = true;
  checkoutCompletionState = 'loading';
  checkoutCompletionMessage = null;

  try {
    if (!supabase) {
      throw new Error('Supabase is not configured for Checkout verification.');
    }

    if (!checkoutCompletionSessionId) {
      throw new Error('The Checkout Session ID is missing. Return to the plan page and try again.');
    }

    await requireSession();
    const { data, error } = await supabase.functions.invoke<CheckoutVerificationResponse>('verifyStripeCheckout', {
      body: { sessionId: checkoutCompletionSessionId },
    });

    if (error) {
      throw error;
    }

    if (!data?.verified) {
      capture('checkout_session_failed', {
        ...getCheckoutAnalyticsProperties(data?.plan ?? state.selectedPlan),
        payment_status: data?.paymentStatus,
        session_status: data?.sessionStatus,
        stage: 'verification',
        subscription_status: data?.subscriptionStatus,
      });
      checkoutCompletionState = 'incomplete';
      checkoutCompletionMessage = data?.sessionStatus === 'open'
        ? 'Payment was not completed. Return to your plan and start a new payment attempt.'
        : data?.error ?? 'Stripe has not confirmed this payment yet. You can retry verification.';
      render();
      return;
    }

    const restoreResult = await supabase.functions.invoke<RestoreResponse>('restoreWebPremium');

    if (restoreResult.error || !restoreResult.data?.restored) {
      throw new Error(
        restoreResult.data?.message ??
        'Payment is confirmed, but Premium is still syncing. Retry verification in a moment.',
      );
    }

    const completionKey = `focuscorp_checkout_completed_${checkoutCompletionSessionId}`;

    if (!sessionStorage.getItem(completionKey)) {
      capture('checkout_completed', {
        ...getCheckoutAnalyticsProperties(data.plan ?? state.selectedPlan),
        session_id: checkoutCompletionSessionId,
      });
      sessionStorage.setItem(completionKey, 'true');
    }
    state.selectedPlan = data.plan ?? state.selectedPlan;
    state.premiumSource = 'checkout';
    sessionStorage.removeItem(EMBEDDED_CHECKOUT_STORAGE_KEY);
    checkoutCompletionState = 'idle';
    setView('download');
  } catch (error) {
    capture('checkout_session_failed', {
      ...getCheckoutAnalyticsProperties(),
      error_type: error instanceof Error ? error.name : 'unknown',
      stage: 'verification_or_entitlement_sync',
    });
    checkoutCompletionState = 'error';
    checkoutCompletionMessage = error instanceof Error ? error.message : 'Checkout verification failed. Please try again.';
    render();
  }
}

function renderTopbar(checkoutExit = false, refinedClose = false) {
  return `
    <header class="topbar${refinedClose ? ' offer-header' : ''}">
      <a class="brand" href="/" data-action="${checkoutExit ? 'checkout-exit' : 'landing'}">
        <img src="/assets/optimized/logo.webp" width="48" height="48" alt="FocusCorp logo" />
        <span>FOCUSCORP</span>
      </a>
      ${
        checkoutExit
          ? `<button class="icon-button checkout-close-button" type="button" data-action="checkout-exit" aria-label="Close offer">${refinedClose ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>' : '×'}</button>`
          : `<nav class="nav-links" aria-label="Primary">
              <a href="/#how">How it works</a>
              <a href="/privacy">Privacy</a>
              <a href="/support">Support</a>
              ${pixelButton('Get my plan', 'start-quiz', { small: true })}
            </nav>`
      }
    </header>
  `;
}

function renderLandingAppStoreButton(downloadMethod: 'hero' | 'final-cta') {
  return `
    <a class="pixel-btn blue landing-download-button" href="${escapeHtml(appStoreUrl)}" data-action="download-clicked" data-download-method="${downloadMethod}" aria-label="Download FocusCorp on the App Store">
      <span>Download the app</span>
    </a>
  `;
}

type TourRow = {
  accent: 'a-blue' | 'a-purple' | 'a-red' | 'a-gold' | 'a-orange';
  body: string;
  bullets: string[];
  image: string;
  kicker: string;
  title: string;
};

// The app tour uses the real App Store screenshots (web/public/assets/appstore).
const TOUR_ROWS: TourRow[] = [
  {
    kicker: '01 — Stop scrolling',
    accent: 'a-blue',
    title: 'Turn attention into visible progress.',
    body: 'Your focus sessions build a living pixel office. Bob and the team show up when you do, so every protected block of work moves the company forward.',
    bullets: ['A startup that grows with your focus', 'Progress you can see after every session'],
    image: '/assets/optimized/meet-bob.webp',
  },
  {
    kicker: '02 — Take control',
    accent: 'a-red',
    title: 'Deep work, timed.',
    body: "Pick 5 to 90 minutes and start. Your distracting apps lock, the wall clock counts down, and Bob gets to work. Flip on Hardcore Mode and there's no cancelling — the timer decides when you're done.",
    bullets: ['5–90 minute sessions', 'Hardcore Mode for zero escape routes'],
    image: '/assets/optimized/deep-work.webp',
  },
  {
    kicker: '03 — Block distractions',
    accent: 'a-purple',
    title: 'Block apps. Schedule focus.',
    body: 'Sort apps into Always Allowed, Never Allowed, and Distracting. Create scheduled rules and daily limits so FocusCorp blocks what eats your hours with real iPhone Screen Time controls.',
    bullets: ['Real Screen Time blocking, not reminders', 'Scheduled rules and daily limits'],
    image: '/assets/optimized/block-apps.webp',
  },
  {
    kicker: '04 — Build discipline',
    accent: 'a-gold',
    title: 'Compete in weekly leagues.',
    body: 'Every finished session earns valuation for the weekly standings. Keep showing up, climb through the leagues, and measure your consistency against other focused founders.',
    bullets: ['Weekly founder standings', 'League promotions powered by focused work'],
    image: '/assets/optimized/earn-rewards.webp',
  },
  {
    kicker: '05 — Earn rewards',
    accent: 'a-blue',
    title: 'Earn rewards. Shape your office.',
    body: 'Focused work earns money you can spend on furniture, plants, decorations, and upgrades. Your office becomes a record of the sessions you actually completed.',
    bullets: ['Office items earned through focus', 'A workspace that grows with the company'],
    image: '/assets/optimized/office-customisation.webp',
  },
  {
    kicker: '06 — Finish what you start',
    accent: 'a-orange',
    title: 'Your focus builds your startup.',
    body: 'Focused time, money earned, streaks, company valuation, and league progress turn deep work into a record you can see. Show up and the company grows.',
    bullets: ['Focus reports and company valuation', 'Long-term progress that stays visible'],
    image: '/assets/optimized/your-focus.webp',
  },
];

const FAQ_ITEMS: Array<[string, string]> = [
  [
    'Who is FocusCorp built for?',
    'FocusCorp is built for founders. Whether you are validating the idea, launching the first version, or growing the company, it protects the focused hours that move your business forward.',
  ],
  [
    'Does FocusCorp really block apps?',
    "Yes. On iPhone, FocusCorp uses Apple's Screen Time controls to actually block the apps you pick during focus sessions and scheduled rules. It is a real block, not a motivational pop-up.",
  ],
  [
    'What happens after the quiz?',
    'Bob turns your answers into a founder focus plan. You save it to an account, unlock Premium, and download the app — when you log in, your plan, your company, and Premium are already waiting.',
  ],
  [
    'Do I need an iPhone?',
    'Yes. FocusCorp is an iOS app built on Apple Screen Time. The quiz and checkout work in any browser, and Premium is attached to your account when you log into the app.',
  ],
  [
    'What is Hardcore Mode?',
    "No cancelling, no early exits. Once a hardcore session or rule starts, the timer decides when you're done. It exists because you asked for it at 11pm and thanked yourself at 9am.",
  ],
  [
    'Do referral codes work here?',
    'Yes. If you received a referral code, enter it during account checkout to see the offer or reward available to you.',
  ],
  [
    'Can I cancel my subscription?',
    'Anytime. Web subscriptions are handled by Stripe and can be cancelled in a couple of clicks; Premium simply runs until the end of the period you paid for.',
  ],
];

function renderTourRow(row: TourRow, index: number) {
  return `
    <div class="tour-row ${row.accent} ${index % 2 === 1 ? 'flip' : ''}">
      <div class="tour-copy">
        <div class="eyebrow">${escapeHtml(row.kicker)}</div>
        <h3>${escapeHtml(row.title)}</h3>
        <p>${escapeHtml(row.body)}</p>
        <ul>
          ${row.bullets.map((bullet) => `<li><span class="dot"></span>${escapeHtml(bullet)}</li>`).join('')}
        </ul>
      </div>
      <div class="tour-media">
        <div class="tour-shot"><img src="${escapeHtml(row.image)}" alt="${escapeHtml(row.title)} — FocusCorp app screenshot" loading="lazy" decoding="async" width="720" height="1558" /></div>
      </div>
    </div>
  `;
}

function renderLanding() {
  const savedPlan = readQuizPlan(window.location.search);
  return `
    <div class="site-shell">
      ${renderTopbar()}
      <main>
        <section class="hero">
          <div class="hero-copy">
            <div class="eyebrow">Block the scroll · Do the work · Watch it pay</div>
            <h1>Stop scrolling. <span>Get shit done.</span></h1>
            <p class="hero-lede">
              Founders don't need more willpower. You need the feed locked and a reason to show up. FocusCorp blocks your distracting apps for real and turns every focus session into progress in a pixel startup that grows as you do.
            </p>
            <div class="hero-actions">
              ${pixelButton(savedPlan ? 'Continue my plan' : 'Get my focus plan', 'start-quiz')}
              ${renderLandingAppStoreButton('hero')}
            </div>
            <div class="proof-strip">
              <span class="proof-pill"><span class="dot"></span>Actually blocks apps — not just reminders</span>
              <span class="proof-pill"><span class="dot"></span>Built specifically for founders</span>
              <span class="proof-pill app-store-rating" aria-label="Rated 5 out of 5 stars on the App Store"><span class="rating-stars" aria-hidden="true">★★★★★</span><span>5.0 on the App Store</span></span>
            </div>
          </div>
          <div class="hero-device" aria-label="FocusCorp on iPhone">
            <div class="hero-device-screen">
              <img class="hero-screenshot" src="/assets/optimized/hero-office-660.webp"
                srcset="/assets/optimized/hero-office-660.webp 660w, /assets/optimized/hero-office-990.webp 990w"
                sizes="(max-width: 680px) 78vw, 310px" width="1170" height="2532"
                fetchpriority="high" decoding="async" alt="FocusCorp app: pixel office, focus timer, and your growing company" />
              <span class="hero-device-notch" aria-hidden="true"></span>
              <span class="hero-device-home" aria-hidden="true"></span>
            </div>
          </div>
        </section>
        <div class="ticker-wrap" aria-hidden="true">
          <div class="ticker">
            <div class="ticker-track">
              ${Array.from(
                { length: 6 },
                () => `
                  <div class="ticker-group">
                    <span>INSERT COIN TO START</span><span>INSTAGRAM BLOCKED</span><span>SESSION COMPLETE</span><span>+$250 EARNED</span><span>OFFICE UPGRADED</span><span>STREAK SAVED</span>
                  </div>
                `,
              ).join('')}
            </div>
          </div>
          <div class="ticker-unicorn" aria-hidden="true"></div>
        </div>
        <section class="section stat-band">
          <div class="section-header">
            <h2>The scroll is stealing years. Literally.</h2>
            <p>Three hours a day is the average. That's not "relaxing" — it's this:</p>
          </div>
          <div class="stat-grid">
            <div class="stat-cell c1"><strong>3h</strong><span>every day</span></div>
            <div class="stat-cell c2"><strong>45</strong><span>days every year</span></div>
            <div class="stat-cell c3"><strong>1.3</strong><span>years every decade</span></div>
          </div>
          <p class="stat-footnote">The focus quiz runs your own numbers. They usually hurt more.</p>
        </section>
        <section class="section tour" id="how">
          <div class="section-header">
            <h2>From doomscrolling to deep work.</h2>
            <p>The same loop that runs in the app: block the noise, do the work, watch the company grow.</p>
          </div>
          ${TOUR_ROWS.map((row, index) => renderTourRow(row, index)).join('')}
        </section>
        <section class="section flow-band">
          <div class="section-header">
            <h2>You're not lazy. Your feed is engineered.</h2>
            <p>Every scroll is tuned by teams whose only job is keeping you watching. FocusCorp points the same psychology — streaks, rewards, progression — at the thing you actually want to build.</p>
          </div>
          <div class="step-grid">
            ${renderStep('01 · THE TRAP', 'The 2am rabbit hole', 'One "quick check" becomes 40 minutes of shorts. That is not a willpower problem — the apps are built to win that fight.')}
            ${renderStep('02 · THE SWITCH', 'Borrow the game loop', "Blocks you can't talk your way past, a timer that pays out, a streak you don't want to break. The same hooks, pointed at deep work.")}
            ${renderStep('03 · THE PAYOFF', 'Ship the thing', 'A protected session every day compounds into the product, customer, and sales work you keep almost starting.')}
          </div>
        </section>
        <section class="section faq-band">
          <div class="section-header">
            <h2>Fair questions, straight answers.</h2>
          </div>
          <div class="faq-list">
            ${FAQ_ITEMS.map(
              ([question, answer]) => `
                <details class="faq-item">
                  <summary>${escapeHtml(question)}<span class="faq-mark">+</span></summary>
                  <p>${escapeHtml(answer)}</p>
                </details>
              `,
            ).join('')}
          </div>
        </section>
        <section class="section cta-band">
          <div class="section-header">
            <div class="bob-runner" aria-hidden="true"></div>
            <h2>Ready to stop donating your best hours?</h2>
            <p>Three minutes of questions. One plan built around your goals, your schedule, and the apps that steal from you. Bob is already at the office.</p>
            <div class="cta-actions">
              ${pixelButton('Get my focus plan', 'start-quiz')}
              ${renderLandingAppStoreButton('final-cta')}
            </div>
          </div>
        </section>
      </main>
      ${renderFooter()}
    </div>
  `;
}

function renderStep(chip: string, title: string, body: string) {
  return `
    <article class="step-card">
      <div class="step-num">${escapeHtml(chip)}</div>
      <strong>${escapeHtml(title)}</strong>
      <p>${escapeHtml(body)}</p>
    </article>
  `;
}

function renderFooter() {
  return `
    <footer class="footer">
      <div class="footer-grid">
        <div class="footer-brand">
          <a class="brand" href="/" data-action="landing">
            <img src="/assets/optimized/logo.webp" width="48" height="48" alt="FocusCorp logo" />
            <span>FOCUSCORP</span>
          </a>
          <p>Deep work, gamified. Block distractions, run focus sessions, and grow a pixel startup that only your focus can pay for.</p>
        </div>
        <div class="footer-col">
          <strong>Product</strong>
          <a href="/#how">How it works</a>
          <a href="/start" data-action="start-quiz">Start the quiz</a>
          <a href="${escapeHtml(appStoreUrl)}" data-action="download-clicked" data-download-method="footer">Download on the App Store</a>
          <a href="/manage-subscription" data-action="manage-subscription">Manage web subscription</a>
          <a href="/support">Support</a>
        </div>
        <div class="footer-col">
          <strong>Legal</strong>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <button class="footer-text-button" type="button" data-action="change-tracking-consent">Tracking preferences</button>
        </div>
        <div class="footer-col">
          <strong>Contact</strong>
          <a href="mailto:hi@buddyapp.lt">hi@buddyapp.lt</a>
        </div>
      </div>
      <div class="footer-base">
          <span>© 2026 Buddy app, MB. Built for founders who ship.</span>
      </div>
    </footer>
  `;
}

function renderTrackingConsent() {
  if (isAnalyticsTestActive(internalTrackingTest)) return '';
  if (isInternalTrackingTestActive(internalTrackingTest) && !trackingPreferencesVisible) {
    return '';
  }
  const consent = getSavedTrackingConsent();

  if (consent !== null && !trackingPreferencesVisible) {
    return '';
  }

  const isPreferencesDialog = trackingPreferencesVisible;
  const title = isPreferencesDialog ? 'Tracking preferences' : 'Your privacy, your choice';
  const description = isPreferencesDialog
    ? 'Choose whether FocusCorp may use optional analytics and Meta advertising measurement. Necessary account and payment features always keep working.'
    : 'Optional analytics and ad measurement help us improve FocusCorp. Your plan works either way.';

  return `
    ${isPreferencesDialog ? '<div class="tracking-consent-backdrop" aria-hidden="true"></div>' : ''}
    <section class="tracking-consent ${isPreferencesDialog ? 'is-dialog' : ''}" role="dialog" aria-modal="${isPreferencesDialog ? 'true' : 'false'}" aria-labelledby="tracking-consent-title">
      ${isPreferencesDialog ? '<button class="tracking-consent-close" type="button" data-action="close-tracking-consent" aria-label="Close tracking preferences">×</button>' : ''}
      ${isPreferencesDialog ? '<span class="tracking-consent-kicker">OPTIONAL TRACKING</span>' : ''}
      <h2 id="tracking-consent-title">${escapeHtml(title)}</h2>
      <p>${escapeHtml(description)} <a href="/privacy">Privacy policy</a></p>
      <div class="tracking-consent-actions">
        <button class="tracking-consent-choice secondary" type="button" data-action="reject-tracking">Necessary only</button>
        <button class="tracking-consent-choice primary" type="button" data-action="accept-tracking">Allow optional</button>
      </div>
      ${isPreferencesDialog && consent ? `<small>Current choice: ${consent === 'accepted' ? 'optional tracking allowed' : 'necessary only'}.</small>` : ''}
    </section>
  `;
}

function renderGuidedQuiz() {
  const progress = getWebQuizQuestionProgress(currentScreen,nameStepArm);
  const prompt = getGuidedPrompt(currentScreen, state.answers).replaceAll('—', '-');
  return `
    <main class="identity-entry guided-quiz${currentScreen === 16 ? ' guided-summary' : ''}${currentScreen === ONBOARDING_SOCIAL_PROOF_SCREEN ? ' guided-final' : ''}${currentScreen === ONBOARDING_PLAN_LOADING_SCREEN ? ' guided-generating' : ''}" data-screen="${currentScreen}">
      <header class="topbar funnel-centered-topbar">
        <div class="identity-brand"><img src="/assets/optimized/logo.webp" width="34" height="34" alt="" /><span>FOCUSCORP</span></div>
      </header>
      <div class="guided-content">
        <nav class="guided-progress" aria-label="Your focus plan">
          <button class="guided-back" type="button" data-action="quiz-back" aria-label="Back"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6"/></svg></button>
          <div class="guided-progress-track" role="progressbar" aria-label="Questions answered" aria-valuemin="0" aria-valuemax="${progress?.total ?? 9}" aria-valuenow="${progress?.current ?? 0}"><span style="width:${(progress?.current ?? 0) / (progress?.total ?? 9) * 100}%"></span></div>
          <span class="guided-count">${progress?.current ?? 1}<span> / ${progress?.total ?? 9}</span></span>
        </nav>
        <section class="guided-bob" aria-label="Bob, your focus guide">
          <div class="guided-bob-art" aria-hidden="true"><div class="guided-bob-sprite"></div></div>
          <button type="button" class="guided-dialogue" data-action="reveal-guided-speech" aria-label="Bob: ${escapeHtml(prompt)}" title="Tap to reveal the full message">
            <span class="guided-bob-name" aria-hidden="true">BOB</span>
            <span class="guided-speech" aria-hidden="true"><span class="guided-speech-measure">${escapeHtml(prompt)}</span><span class="guided-speech-visible"></span></span>
          </button>
        </section>
        <section class="guided-question${currentScreen === 12 || currentScreen === 8 ? ' guided-grid-options' : ''}">
          ${renderQuestion()}
        </section>
      </div>
    </main>`;
}

function renderQuiz() {
  if (guidedOnboardingEnabled && currentScreen !== ONBOARDING_INTRO_SCREEN) return renderGuidedQuiz();
  const isPaidQuiz = organicGuestEnabled || ['fc1', 'tst1'].includes(new URLSearchParams(window.location.search).get('qz') ?? '');
  const questionProgress = isPaidQuiz ? getWebQuizQuestionProgress(currentScreen,nameStepArm) : null;
  // Intro: full-bleed office scene, exactly like the iOS intro screen —
  // Bob by the desk, speech bubble, and the orange LET'S GO action.
  if (currentScreen === ONBOARDING_INTRO_SCREEN && !reactionText) {
    if (isPaidQuiz) {
      return identityEntryEnabled ? identityEntryHtml : paidQuizIntroHtml;
    }
    return `
      <div class="quiz-stage">
        <div class="quiz-scene-layer">
          ${renderOfficeScene({ bubbleText: 'Hi, I am Bob' })}
        </div>
        <div class="quiz-topline" style="grid-template-columns: 1fr 42px">
          <div></div>
          <button class="icon-button" type="button" data-action="landing" aria-label="Close">×</button>
        </div>
        <div class="quiz-bottom-layer">
          ${pixelButton("LET'S GO", 'intro-next')}
        </div>
      </div>
    `;
  }

  // Bob reactions: speech bubble in the scene + CONTINUE, like the app
  // (no overlay — the office stays fully visible during reactions).
  if (reactionText) {
    return `
      <div class="quiz-stage">
        <div class="quiz-scene-layer">
          ${renderOfficeScene({ bubbleCentered: true, bubbleText: reactionText })}
        </div>
        <div class="quiz-bottom-layer">
          ${pixelButton('CONTINUE', 'finish-reaction')}
        </div>
      </div>
    `;
  }

  // Mini focus session: Bob runs to the desk, works while the wall clock
  // counts down, runs back, then confetti — orchestrated by startMiniSession.
  if (currentScreen === ONBOARDING_MINI_SESSION_SCREEN) {
    return `
      <div class="quiz-stage">
        <div class="quiz-scene-layer">
          ${renderOfficeScene({ bob: 'mini', timerText: '00:10' })}
        </div>
        <div class="confetti-layer"></div>
      </div>
    `;
  }

  // The reflection and plan-loading steps are borderless panels over the office in the app,
  // not cards.
  const isPanelScreen =
    currentScreen === ONBOARDING_REFLECTION_SCREEN ||
    currentScreen === ONBOARDING_PLAN_LOADING_SCREEN;

  // Questions: office scene behind a floating card, like the app. Desktop
  // uses the same layout in a centered phone-width column, so buttons stay
  // where the pointer already is.
  return `
    <div class="quiz-stage">
      <div class="quiz-scene-layer">
        ${renderOfficeScene({ shade: 'dark' })}
      </div>
      <div class="quiz-topline">
        <button class="icon-button" type="button" data-action="quiz-back" aria-label="Back">‹</button>
        <div class="progress-track" aria-label="Quiz progress">
          <div class="progress-fill" style="width:${questionProgress ? Math.round(questionProgress.current / questionProgress.total * 100) : getProgressPercent()}%"></div>
        </div>
        ${isPaidQuiz
          ? (questionProgress ? `<span class="quiz-question-count" aria-label="Question ${questionProgress.current} of ${questionProgress.total}">${questionProgress.current}<span>/${questionProgress.total}</span></span>` : '<span></span>')
          : '<button class="icon-button" type="button" data-action="landing" aria-label="Close">×</button>'}
      </div>
      <div class="quiz-card-layer">
        <div class="question-card ${isPanelScreen ? 'panel' : ''}">${renderQuestion()}</div>
      </div>
    </div>
  `;
}

function renderQuestion() {
  const activeChoiceStep = CHOICE_STEP_BY_SCREEN.get(currentScreen) ?? null;
  const activeTextStep = TEXT_STEP_BY_SCREEN.get(currentScreen) ?? null;
  const activeMultiStep = MULTI_STEP_BY_SCREEN.get(currentScreen) ?? null;

  if (currentScreen === ONBOARDING_PITCH_SCREEN) {
    if (guidedOnboardingEnabled) return renderInfoQuestion(
      'Most founders lose 3 hours a day to distraction.',
      'That’s about 45 days a year. Let’s put some of that time back into building.',
      '', 'BUILD MY PLAN →', 'pitch-next');
    const identityPitch = identityEntryEnabled ? getIdentityPitch(state.answers.entryIdentity) : null;
    if (identityPitch) {
      return renderInfoQuestion(identityPitch,
        'Block the distractions. Make time for what you want to build.',
        'A few quick answers. A focus plan that fits your day.', 'BUILD MY PLAN →', 'pitch-next');
    }
    return renderInfoQuestion(
      'Most founders lose 3 hours a day to distraction.',
      "That's 45 days a year. FocusCorp gives them back.",
      "Let's build Bob's focus plan.",
      'SOUNDS LIKE BOB →',
      'pitch-next',
    );
  }

  if (activeChoiceStep) {
    if (guidedOnboardingEnabled && activeChoiceStep.answerKey === 'commitmentLevel') {
      return guidedCommitmentChoices
        ? renderChoiceStep(activeChoiceStep) + '<button class="guided-commitment-alternative" type="button" data-action="guided-commitment-hold">Try the hold gesture</button>'
        : renderGuidedCommitment(state.answers);
    }
    return renderChoiceStep(activeChoiceStep);
  }

  if (activeTextStep) {
    return renderTextStep(activeTextStep);
  }

  if (currentScreen === ONBOARDING_PHONE_HOURS_SCREEN) {
    return renderPhoneHours();
  }

  if (currentScreen === ONBOARDING_PHONE_MATH_SCREEN) {
    return renderPhoneMath();
  }

  if (currentScreen === ONBOARDING_RECLAIM_SCREEN) {
    const hours = getDailyPhoneHours(state.answers);
    const userName = getUserName(state.answers);
    const startupName = getStartupName(state.answers);
    const copy =
      hours > 1
        ? `What if ${userName} had ${hours - 1} more hours every day? That's ${startupName}'s unfair advantage.`
        : `What if ${userName} had even 30 more minutes every day? That's ${startupName}'s unfair advantage.`;
    return renderInfoQuestion("It doesn't have to be this way.", guidedOnboardingEnabled ? getGuidedReclaimCopy(state.answers) : copy, '', 'BUILD THE PLAN →', 'reclaim-next');
  }

  if (activeMultiStep) {
    return renderMultiStep(activeMultiStep);
  }

  if (currentScreen === ONBOARDING_REFLECTION_SCREEN) {
    return renderReflection();
  }

  if (currentScreen === ONBOARDING_PLAN_LOADING_SCREEN) {
    return renderPlanLoading();
  }

  if (currentScreen === ONBOARDING_SOCIAL_PROOF_SCREEN) {
    return renderScreenTimeComparison();
  }

  return '';
}

function renderInfoQuestion(
  title: string,
  body: string,
  small: string,
  buttonLabel: string,
  action: string,
) {
  return `
    <div class="question-wrap">
      <h2 class="question-title">${escapeHtml(title)}</h2>
      ${body ? `<p class="question-body">${escapeHtml(body)}</p>` : ''}
      ${small ? `<p class="question-body">${escapeHtml(small)}</p>` : ''}
      ${pixelButton(buttonLabel, action)}
    </div>
  `;
}

function buildScreenTimeChartPoints(values: readonly number[], maxHours: number) {
  const width = 320;
  const height = 88;
  const horizontalPadding = 10;
  const verticalPadding = 8;
  const usableWidth = width - horizontalPadding * 2;
  const usableHeight = height - verticalPadding * 2;

  return values.map((value, index) => {
    const x = horizontalPadding + (usableWidth * index) / Math.max(1, values.length - 1);
    const y = verticalPadding + (1 - Math.min(maxHours, Math.max(0, value)) / maxHours) * usableHeight;
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  });
}

function renderScreenTimeComparisonChart(
  typicalValues: readonly number[],
  focuscorpValues: readonly number[],
  maxHours: number,
  description: string,
) {
  const typicalPoints = buildScreenTimeChartPoints(typicalValues, maxHours);
  const focuscorpPoints = buildScreenTimeChartPoints(focuscorpValues, maxHours);
  const buildLinePath = buildSmoothLinePath;
  const buildAreaPath = (points: ReturnType<typeof buildScreenTimeChartPoints>) => {
    const linePath = buildLinePath(points);
    return `${linePath} L ${points.at(-1)?.x ?? 310} 80 L ${points[0]?.x ?? 10} 80 Z`;
  };

  return `
    <section class="screen-time-chart" aria-label="${escapeHtml(description)}">
      <div class="screen-time-chart-heading">
        <span>30-day screen time</span>
        <strong>YOUR FORECAST</strong>
      </div>
      <div class="screen-time-chart-legend" aria-hidden="true">
        <span class="screen-time-legend-key typical">Typical</span>
        <span class="screen-time-legend-key focuscorp">With FocusCorp</span>
      </div>
      <svg class="screen-time-chart-plot" viewBox="0 0 320 88" role="img" aria-label="${escapeHtml(description)}" preserveAspectRatio="none">
        <title>${escapeHtml(description)}</title>
        <path class="screen-time-gridline" d="M 10 24 L 310 24 M 10 52 L 310 52 M 10 80 L 310 80" />
        <path class="screen-time-area typical" d="${buildAreaPath(typicalPoints)}" />
        <path class="screen-time-area focuscorp" d="${buildAreaPath(focuscorpPoints)}" />
        <path class="screen-time-line typical" d="${buildLinePath(typicalPoints)}" />
        <path class="screen-time-line focuscorp" d="${buildLinePath(focuscorpPoints)}" />
      </svg>
      <div class="screen-time-days" aria-hidden="true">
        ${['DAY 1', '5', '10', '15', '20', '25', '30'].map((day) => `<span>${day}</span>`).join('')}
      </div>
    </section>
  `;
}

function renderScreenTimeComparison() {
  const startupName = getStartupName(state.answers);
  const userName = getUserName(state.answers);
  const personalization = buildOnboardingPersonalizationProfile(state.answers);
  const startingHours = Math.max(4.8, getDailyPhoneHours(state.answers));
  const typicalValues = [
    startingHours,
    startingHours + 0.8,
    startingHours + 0.1,
    startingHours + 1,
    startingHours + 0.3,
    startingHours + 0.9,
    startingHours + 0.2,
  ];
  const focuscorpValues = [
    startingHours,
    startingHours * 0.62,
    startingHours * 0.68,
    startingHours * 0.48,
    startingHours * 0.53,
    startingHours * 0.35,
    startingHours * 0.29,
  ];
  const chartMaxHours = Math.max(8, ...typicalValues) + 0.5;
  const monthlyBuildHours =
    (personalization.defaultFocusDurationMinutes * personalization.dailySessionTarget * 30) / 60;
  const monthlyBuildHoursLabel = Number.isInteger(monthlyBuildHours)
    ? String(monthlyBuildHours)
    : monthlyBuildHours.toFixed(1);

  if (guidedOnboardingEnabled) {
    return `<div class="question-wrap screen-time-proof">
      <h2 class="question-title">Make room to build.</h2>
      <div class="screen-time-charts">${renderGuidedOutcomeChart()}</div>
      <div class="guided-forecast-payoff"><strong>${monthlyBuildHoursLabel}h<span> / month</span></strong><p>For ${escapeHtml(startupName)}<small>${personalization.dailySessionTarget} × ${personalization.defaultFocusDurationMinutes} min a day</small></p></div>
      <p class="guided-forecast-note">Your routine, visualised. Progress will vary.</p>
      <div class="guided-final-cta">${pixelButton('SAVE MY PLAN', 'social-proof-next')}</div>
    </div>`;
  }

  return `
    <div class="question-wrap screen-time-proof">
      <div class="question-kicker">YOUR FOCUS FORECAST</div>
      <h2 class="question-title">Turn screen time into ${escapeHtml(startupName)} time.</h2>
      <p class="question-body">Less scrolling. More focused work moving the company forward.</p>
      <div class="screen-time-charts">
        ${renderScreenTimeComparisonChart(
          typicalValues,
          focuscorpValues,
          chartMaxHours,
          `Typical screen time stays high and uneven. With FocusCorp, it fluctuates naturally while trending down and returning focused time to ${startupName}.`,
        )}
      </div>
      <div class="screen-time-payoff">
        <strong>${monthlyBuildHoursLabel}H / MONTH</strong>
        <span>Based on ${personalization.dailySessionTarget} × ${personalization.defaultFocusDurationMinutes}-minute daily sessions, that focused time goes back to ${escapeHtml(startupName)}.</span>
      </div>
      ${pixelButton(`${userName.toUpperCase()} IS READY`, 'social-proof-next')}
    </div>
  `;
}

function renderChoiceStep(step: ChoiceStep) {
  const selected = getStoredStringAnswer(state.answers, step.answerKey);
  const options = step.options
    .filter(option => !guidedOnboardingEnabled || step.answerKey !== 'userAge' || option !== 'Under 18')
    .map(
      (option) => `
        <button class="option-button ${selected === option ? 'selected' : ''}" type="button" ${guidedOnboardingEnabled ? `aria-pressed="${selected === option}"` : ''} data-action="choice" data-option="${escapeHtml(option)}">
          ${escapeHtml(option)}
        </button>
      `,
    )
    .join('');

  return `
    <div class="question-wrap">
      <h2 class="question-title">${escapeHtml(resolveCopy(step.title, state.answers))}</h2>
      <div class="option-list">${options}</div>
    </div>
  `;
}

function renderTextStep(step: TextStep) {
  const value = getStoredStringAnswer(state.answers, step.answerKey);

  return `
    <div class="question-wrap">
      <h2 class="question-title">${escapeHtml(resolveCopy(step.title, state.answers))}</h2>
      ${step.subtitle ? `<p class="question-body">${escapeHtml(step.subtitle)}</p>` : ''}
      <div class="form-grid">
        <label class="field">
          <span>${escapeHtml(step.placeholder)}</span>
          ${
            step.multiline
              ? `<textarea data-answer-key="${step.answerKey}" maxlength="220">${escapeHtml(value)}</textarea>`
              : `<input data-answer-key="${step.answerKey}" maxlength="${step.answerKey === 'startupName' ? 42 : 32}" value="${escapeHtml(value)}" />`
          }
        </label>
        ${pixelButton(step.buttonLabel, 'text-submit')}
      </div>
    </div>
  `;
}

function renderMultiStep(step: MultiStep) {
  const selected = getStoredArrayAnswer(state.answers, step.answerKey);
  const options = step.options
    .map(
      (option) => `
        <button class="option-button ${selected.includes(option) ? 'selected' : ''}" type="button" ${guidedOnboardingEnabled ? `aria-pressed="${selected.includes(option)}"` : ''} data-action="multi-toggle" data-option="${escapeHtml(option)}">
          ${escapeHtml(option)}
        </button>
      `,
    )
    .join('');

  return `
    <div class="question-wrap">
      <h2 class="question-title">${escapeHtml(resolveCopy(step.title, state.answers))}</h2>
      <div class="option-list">${options}</div>
      ${selected.length > 0 || guidedOnboardingEnabled ? pixelButton('CONTINUE', 'multi-submit', { disabled: selected.length === 0 }) : ''}
    </div>
  `;
}

function renderPhoneHours() {
  const hours = getDailyPhoneHours(state.answers);

  return `
    <div class="question-wrap">
      <h2 class="question-title">How many hours a day do you spend on your phone?</h2>
      <div class="stepper">
        <button class="icon-button" type="button" data-action="hours-dec" aria-label="Decrease">−</button>
        <div class="stepper-value"><strong>${hours}</strong><span>hours</span></div>
        <button class="icon-button" type="button" data-action="hours-inc" aria-label="Increase">+</button>
      </div>
      ${pixelButton('SEE THE MATH', 'phone-hours-next')}
    </div>
  `;
}

function renderPhoneMath() {
  const hours = getDailyPhoneHours(state.answers);
  const userName = getUserName(state.answers);
  const weeklyHours = hours * 7;
  const yearlyDays = Math.round((hours * 365) / 24);
  const decadeYears = ((hours * 365 * 10) / 24 / 365).toFixed(1);

  return `
    <div class="question-wrap">
      <h2 class="question-title">The math hits different, ${escapeHtml(userName)}.</h2>
      <div class="stat-list">
        <div class="stat-row"><strong>${weeklyHours} hours</strong><span>every week</span></div>
        <div class="stat-row"><strong>${yearlyDays} days</strong><span>every year</span></div>
        <div class="stat-row"><strong>${decadeYears} years</strong><span>over the next decade</span></div>
      </div>
      <p class="stat-footer">That's not a habit. That's a life sentence.</p>
      ${pixelButton(`${userName.toUpperCase()} GETS IT`, 'phone-math-next')}
    </div>
  `;
}

// Mirrors the app's reflection panel: borderless checkmark lines
// that fade in over the shaded office.
function renderReflection() {
  if (guidedOnboardingEnabled) return renderGuidedProfile(state.answers) + pixelButton('THAT’S MY PLAN', 'reflection-next');
  const userName = getUserName(state.answers);
  const startupName = getStartupName(state.answers);
  const lines = [
    `Sharpest ${state.answers.sharpestTime ?? 'when it counts'}`,
    `Can focus for ${state.answers.focusDuration ?? '25 min'}`,
    `${state.answers.dailySessions ?? '1'} session(s) a day`,
    `Building ${startupName}`,
  ];

  return `
    <div class="question-wrap">
      <h2 class="question-title">Here's what we know about ${escapeHtml(userName)} so far.</h2>
      <div class="reflection-lines">
        ${lines
          .map(
            (line, index) => `
              <div class="reflection-line" style="animation-delay:${index * 220}ms">
                <span>✓</span>${escapeHtml(line)}
              </div>
            `,
          )
          .join('')}
      </div>
      ${pixelButton(`THAT'S ${userName.toUpperCase()} →`, 'reflection-next')}
    </div>
  `;
}

// Mirrors the app's loading panel: an orange progress bar filling
// while the plan checks fade in, then it auto-advances.
function renderPlanLoading() {
  const userName = getUserName(state.answers);
  const selectedDistractions = state.answers.focusKillers?.length
    ? state.answers.focusKillers.join(' + ')
    : 'Selected distractions';
  const checks = [
    `✓ Peak hours: ${state.answers.sharpestTime ?? 'Anytime'}`,
    `✓ Focus rhythm: ${state.answers.dailySessions ?? '1'} x ${state.answers.focusDuration ?? '25 min'}`,
    `✓ Blocklist: ${selectedDistractions}`,
  ];

  if (guidedOnboardingEnabled) {
    const stages = [
      ['Your focus window', state.answers.sharpestTime ?? 'Flexible'],
      ['Your daily rhythm', `${state.answers.dailySessions ?? '1'} × ${state.answers.focusDuration ?? '25 min'}`],
      ['Your distractions', selectedDistractions],
    ];
    return `<div class="question-wrap">
      <h2 class="question-title">Putting your plan together.</h2>
      <div class="guided-plan-ring" role="progressbar" aria-label="Preparing your focus plan" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><div><strong>0%</strong><span>PERSONALISING</span></div></div>
      <div class="guided-plan-stages">${stages.map(([label, value]) => `<div class="guided-plan-stage"><span class="guided-stage-check" aria-hidden="true">✓</span><div><span>${label}</span><strong>${escapeHtml(value)}</strong></div></div>`).join('')}</div>
    </div>`;
  }

  return `
    <div class="question-wrap">
      <h2 class="question-title">Building ${escapeHtml(userName)}'s plan...</h2>
      <div class="loading-track"><div class="loading-fill"></div></div>
      <div class="plan-checks">
        ${checks
          .map(
            (line, index) => `
              <div class="plan-check" style="animation-delay:${500 + index * 600}ms">${escapeHtml(line)}</div>
            `,
          )
          .join('')}
      </div>
    </div>
  `;
}

function renderAccount() {
  const payload = buildPersistedOnboarding();
  const name = payload.answers.userName ?? 'Founder';
  const company = state.answers.startupName ?? '';

  return `
    <div class="site-shell${isPaidQuizFunnel() ? ' account-sky-shell' : ''}">
      ${isPaidQuizFunnel() ? `<header class="topbar funnel-centered-topbar">
        <div class="identity-brand">
          <img src="/assets/optimized/logo.webp" width="34" height="34" alt="" />
          <span>FOCUSCORP</span>
        </div>
      </header>` : renderTopbar()}
      <main class="account-layout ${isPaidQuizFunnel() ? 'account-layout-compact' : ''}">
        ${isPaidQuizFunnel() ? '' : renderSummaryPanel("Your plan is built. Don't lose it.", 'One account keeps your plan, your startup, and Premium — on the web and in the app.', 'One account. Everywhere.')}
        <section class="form-panel">
          <h1>Save your plan.</h1>
          <p>30 seconds. Create the same account you’ll use in the app — everything you just built carries over.</p>
          <form id="account-form" class="form-grid">
            <label class="field"><span>Company name</span><input name="companyName" autocomplete="organization" maxlength="42" placeholder="Your company or idea name" required value="${escapeHtml(company)}" /></label>
            <button class="apple-signup-button" type="button" data-action="apple-signup" ${state.isBusy ? 'disabled' : ''}>
              <svg class="apple-signup-logo" aria-hidden="true" viewBox="0 0 384 512">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.5 4 272.6 4 298.7 8.8 325.7 18.4 353c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.9-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.8-88-61.8-90.2zM260.7 104.5c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
              <span>${state.isBusy ? 'CONNECTING TO APPLE…' : 'CONTINUE WITH APPLE'}</span>
            </button>
            <div class="auth-divider"><span>OR SIGN UP WITH EMAIL</span></div>
            ${isPaidQuizFunnel() ? '' : `
              <label class="field"><span>Name</span><input name="name" autocomplete="name" value="${escapeHtml(name)}" /></label>
            `}
            <label class="field"><span>Email</span><input name="email" type="email" autocomplete="email" required /></label>
            <label class="field"><span>Password</span><input name="password" type="password" autocomplete="new-password" required minlength="8" /></label>
            ${pixelButton(state.isBusy ? 'Creating your account...' : 'Continue', null, { block: true, disabled: state.isBusy, submit: true })}
          </form>
          ${renderStatus()}
          ${
            state.accountSaveRetryAvailable
              ? `<div class="form-grid account-save-retry">${pixelButton('RETRY SAVING MY PLAN', 'retry-account-save', { block: true, disabled: state.isBusy })}</div>`
              : ''
          }
        </section>
      </main>
    </div>
  `;
}

function renderManageSubscription() {
  const signedIn = Boolean(state.session && state.user);
  const email = state.user?.email ?? 'Your FocusCorp account';

  return `
    <div class="site-shell">
      ${renderTopbar()}
      <main class="account-layout subscription-management-layout">
        <aside class="summary-panel subscription-management-summary">
          <div class="summary-scene">
            ${renderOfficeScene({ bubbleText: 'You stay in control.' })}
          </div>
          <div class="summary-body">
            <div class="question-kicker">WEB BILLING</div>
            <h2>Your plan. Your choice.</h2>
            <p>Use Stripe’s secure portal to switch an eligible web plan, update payment details, view invoices, or cancel.</p>
            <div class="billing-feature-list">
              <div><span>✓</span><strong>Change your web plan</strong></div>
              <div><span>✓</span><strong>Cancel without contacting support</strong></div>
              <div><span>✓</span><strong>Billing details stay with Stripe</strong></div>
            </div>
          </div>
        </aside>
        <section class="form-panel subscription-management-panel">
          <div class="question-kicker">MANAGE SUBSCRIPTION</div>
          <h1>${signedIn ? 'Your web subscription.' : 'Sign in to continue.'}</h1>
          <p>${
            signedIn
              ? `Signed in as ${escapeHtml(email)}. Stripe will open in a secure page.`
              : 'Use the same FocusCorp account you used when purchasing on the web.'
          }</p>
          ${
            signedIn
              ? `
                <div class="billing-account-card">
                  <span>FOCUSCORP ACCOUNT</span>
                  <strong>${escapeHtml(email)}</strong>
                  <small>Web purchases only</small>
                </div>
                <div class="form-grid">
                  ${pixelButton(state.isBusy ? 'Opening secure portal...' : 'Open subscription manager', 'open-stripe-portal', { block: true, disabled: state.isBusy })}
                  <button class="ghost-button" type="button" data-action="billing-sign-out" ${state.isBusy ? 'disabled' : ''}>Sign out</button>
                </div>
              `
              : `
                <button class="apple-signup-button" type="button" data-action="billing-apple-signin" ${state.isBusy ? 'disabled' : ''}>
                  <svg class="apple-signup-logo" aria-hidden="true" viewBox="0 0 384 512">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.5 4 272.6 4 298.7 8.8 325.7 18.4 353c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.9-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.8-88-61.8-90.2zM260.7 104.5c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                  </svg>
                  <span>${state.isBusy ? 'CONNECTING…' : 'SIGN IN WITH APPLE'}</span>
                </button>
                <div class="auth-divider"><span>OR SIGN IN WITH EMAIL</span></div>
                <form id="billing-login-form" class="form-grid">
                  <label class="field"><span>Email</span><input name="email" type="email" autocomplete="email" required /></label>
                  <label class="field"><span>Password</span><input name="password" type="password" autocomplete="current-password" required /></label>
                  ${pixelButton(state.isBusy ? 'Signing in...' : 'Sign in', null, { block: true, disabled: state.isBusy, submit: true })}
                </form>
              `
          }
          ${renderStatus()}
          <p class="billing-platform-note"><strong>Purchased through Apple?</strong> Manage that subscription in iPhone Settings → Apple Account → Subscriptions.</p>
        </section>
      </main>
      ${renderFooter()}
    </div>
  `;
}

function renderInlineReferral() {
  if (!isPaidQuizFunnel()) return '';
  return `
    <details class="checkout-referral" id="checkout-referral" ${referralExpanded ? 'open' : ''}>
      <summary>Have a referral code?</summary>
      <form id="referral-form" class="form-grid">
        <label class="field"><span>Referral code</span><input name="referralCode" autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="14" placeholder="ENTER CODE" value="${escapeHtml(referralDraft)}" ${state.isBusy ? 'disabled' : ''} /></label>
        <button class="referral-apply" type="submit" ${state.isBusy ? 'disabled' : ''}>${state.isBusy ? 'Applying…' : 'Apply code'}</button>
      </form>
    </details>
  `;
}

function renderReferral() {
  return `
    <div class="site-shell">
      ${renderTopbar()}
      <main class="account-layout">
        ${renderSummaryPanel('Have a referral code?', 'Apply it to unlock its exclusive reward or dedicated subscription offer.', 'Founders help founders.')}
        <section class="form-panel">
          <h1>Have a referral code?</h1>
          <p>Enter it to unlock the offer attached to that code. A code does not automatically activate Premium. No code? Continue to pick your plan.</p>
          <form id="referral-form" class="form-grid">
            <label class="field"><span>Referral code</span><input name="referralCode" autocomplete="off" maxlength="14" placeholder="ENTER CODE" /></label>
            ${pixelButton(state.isBusy ? 'Checking your code...' : 'Apply code', null, { block: true, disabled: state.isBusy, submit: true })}
            <button class="ghost-button" type="button" data-action="skip-referral">I do not have a code</button>
          </form>
          ${renderStatus()}
        </section>
      </main>
    </div>
  `;
}

function renderFirstpickReward() {
  return `
    <div class="reward-reveal-page">
      <main class="reward-reveal-card" aria-live="polite">
        <div class="question-kicker">FIRSTPICK REWARD</div>
        <h1>CONGRATS, FOUNDER!</h1>
        <div class="reward-art-stage">
          <div class="reward-art-glow" aria-hidden="true"></div>
          <img src="${escapeHtml(firstpickNeonSignUrl)}" alt="Firstpick Neon Sign" />
        </div>
        <div class="reward-exclusive-badge">EXCLUSIVE • NOT FOR SALE</div>
        <p>Your Firstpick Neon Sign is unlocked and already waiting in Owned Items.</p>
        <p class="reward-note">FIRSTPICK unlocks this item only. Your exclusive subscription offer comes next.</p>
        <div class="reward-countdown" aria-hidden="true"><span></span></div>
      </main>
    </div>
  `;
}

function renderFounderOfferModal() {
  if (!state.founderOfferVisible) {
    return '';
  }

  return `
    <div class="founder-offer-backdrop" data-action="founder-offer-decline">
      <section class="founder-offer-modal" role="dialog" aria-modal="true" aria-labelledby="founder-offer-title" data-action="founder-offer-stay">
        <button class="founder-offer-close" type="button" data-action="founder-offer-decline" aria-label="Decline founder offer">×</button>
        <div class="question-kicker">ONE TIME OFFER</div>
        <div class="founder-offer-badge">FOUNDER OFFER</div>
        <h1 id="founder-offer-title">Before you go…</h1>
        <p>Lock in your first year of FocusCorp Premium at our lowest founder price.</p>
        <div class="founder-offer-price">
          <span class="founder-offer-regular">Normally ${escapeHtml(annualPriceLabel)}</span>
          <strong>${escapeHtml(founderOfferPriceLabel)}<small>/ first year</small></strong>
        </div>
        <p class="founder-offer-renewal">Then ${escapeHtml(annualPriceLabel)} per year. Renews automatically until cancelled.</p>
        <div class="founder-offer-actions">
          ${pixelButton('Claim founder offer', 'founder-offer-accept', { block: true, disabled: state.isBusy })}
          <button class="quiet-button" type="button" data-action="founder-offer-decline">No thanks, leave</button>
        </div>
      </section>
    </div>
  `;
}

function renderCheckout() {
  if (state.activeOffer === 'founder') {
    return `
      <div class="site-shell">
        ${renderTopbar(true)}
        <main class="checkout-layout">
          ${renderSummaryPanel('Your founder price is unlocked.', `Get your first year for ${founderOfferPriceLabel}, then renew at ${annualPriceLabel} per year.`, 'Lowest price ever.')}
          <section class="form-panel">
            <div class="question-kicker offer-kicker">FOUNDER OFFER</div>
            <h1>One last chance to build with Bob.</h1>
            <p>Get the complete annual Premium plan at the exclusive founder price for your first year.</p>
            <div class="price-grid single-plan">
              ${renderPriceCard('annual', 'Founder Annual', founderOfferPriceLabel, 'first year', `Then ${annualPriceLabel} per year. Renews until cancelled.`, 'Founder price')}
            </div>
            <div class="form-grid">
              ${pixelButton(state.isBusy ? 'Preparing your checkout...' : 'Claim founder offer', 'start-checkout', { block: true, disabled: state.isBusy })}
            </div>
            ${renderInlineReferral()}
            ${renderStatus()}
          </section>
        </main>
      </div>
      ${renderFounderOfferModal()}
    `;
  }

  if (state.activeOffer === 'focuscorp') {
    return `
      <div class="site-shell">
        ${renderTopbar(true)}
        <main class="checkout-layout">
          ${renderSummaryPanel('Your free month is ready.', `Add your payment details now. You will be charged ${focuscorpOfferPriceLabel} for the annual plan after the 30-day trial unless you cancel.`, 'FOCUSCORP unlocked.')}
          <section class="form-panel">
            <div class="question-kicker offer-kicker">FOCUSCORP CODE UNLOCKED</div>
            <h1>Start one month free.</h1>
            <p>Enter your payment details in secure Stripe Checkout. You will not be charged today.</p>
            <div class="price-grid single-plan">
              ${renderTrialPriceCard(focuscorpOfferPriceLabel, '30-day free trial', '1-month free trial')}
            </div>
            <div class="form-grid">
              ${pixelButton(state.isBusy ? 'Preparing your checkout...' : 'START FREE TRIAL', 'start-checkout', { block: true, disabled: state.isBusy })}
            </div>
            ${renderInlineReferral()}
            ${renderStatus()}
          </section>
        </main>
      </div>
      ${renderFounderOfferModal()}
    `;
  }

  if (state.activeOffer === 'firstpick') {
    return `
      <div class="site-shell">
        ${renderTopbar(true)}
        <main class="checkout-layout">
          ${renderSummaryPanel('Your FIRSTPICK perk is unlocked.', 'The neon sign is yours. Your dedicated yearly Premium offer includes a 7-day trial.', 'Reward secured.')}
          <section class="form-panel">
            <div class="question-kicker offer-kicker">FIRSTPICK EXCLUSIVE</div>
            <h1>Focus like a founder.</h1>
            <p>Start with 7 days free, then continue on the dedicated FIRSTPICK yearly plan. Cancel before the trial ends to avoid a charge.</p>
            <div class="price-grid single-plan">
              ${renderTrialPriceCard(firstpickOfferPriceLabel, '7-day free trial', '7-day free trial')}
            </div>
            <div class="form-grid">
              ${pixelButton(state.isBusy ? 'Preparing your checkout...' : 'START FREE TRIAL', 'start-checkout', { block: true, disabled: state.isBusy })}
            </div>
            ${renderInlineReferral()}
            ${renderStatus()}
          </section>
        </main>
      </div>
      ${renderFounderOfferModal()}
    `;
  }

  if (isPaidQuizFunnel()) return renderPaidPlanSelection();

  return `
    <div class="site-shell">
      ${renderTopbar(true)}
      <main class="checkout-layout">
        ${renderSummaryPanel('Your plan is ready. Unlock it.', 'Secure checkout by Stripe. Premium unlocks in the app the moment you log in — and you can cancel anytime in two clicks.', 'Almost there.')}
        <section class="form-panel">
          <h1>Invest in your focus.</h1>
          <p>You lose hours to the feed for free. This costs less — and pays you back in shipped work. Cancel anytime.</p>
          <div class="price-grid">
            ${renderPriceCard(
              'annual',
              'Annual',
              annualWeeklyLabel ?? annualPriceLabel,
              annualWeeklyLabel ? 'week' : 'year',
              `${annualPriceLabel} billed yearly`,
              annualSavingsPercent ? `Save ${annualSavingsPercent}%` : 'Best value',
            )}
            ${renderPriceCard('monthly', '4-Week', monthlyPriceLabel, '4 weeks', 'Billed every 4 weeks', null)}
            ${renderPriceCard('weekly', '7-Day', weeklyPriceLabel, 'week', 'Billed weekly', null)}
          </div>
          <div class="form-grid">
            ${pixelButton(state.isBusy ? 'Preparing your checkout...' : 'Unlock my focus plan', 'start-checkout', { block: true, disabled: state.isBusy })}
          </div>
          ${renderInlineReferral()}
            ${renderStatus()}
        </section>
      </main>
    </div>
    ${renderFounderOfferModal()}
  `;
}

function renderTrustIcon(kind: 'shield' | 'calendar' | 'message') {
  const paths = {
    shield: '<path d="m12 3-8 3v6c0 5 8 9 8 9s8-4 8-9V6Z"/><path d="m8 12 3 3 5-6"/>',
    calendar: '<rect x="3" y="5" width="18" height="16" rx="3"/><path d="M7 3v4m10-4v4M3 10h18m-13 5 3 3 5-5"/>',
    message: '<path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H4l-2 2v-10.5A8.5 8.5 0 0 1 10.5 3h2a8.5 8.5 0 0 1 8.5 8.5Z"/><path d="M7 10h9m-9 4h6"/>',
  };
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${paths[kind]}</svg>`;
}

function renderPaidPlanSelection() {
  const planCard = (plan: PlanKey) => {
    const annual = plan === 'annual';
    const price = annual ? annualPrice : plan === 'monthly' ? monthlyPrice : weeklyPrice;
    const label = annual ? annualPriceLabel : plan === 'monthly' ? monthlyPriceLabel : weeklyPriceLabel;
    const comparison = price?.prefix.includes('€') ? getPriceComparison('eur', annual ? 'year' : 'week', plan === 'monthly' ? 4 : 1, Math.round(price.amount * 100)) : null;
    return `<button class="offer-plan-card ${state.selectedPlan === plan ? 'selected' : ''}" type="button" data-action="select-plan" data-plan="${plan}" aria-pressed="${state.selectedPlan === plan}">
      ${annual ? '<span class="offer-plan-banner">BEST VALUE</span>' : plan === 'monthly' ? '<span class="offer-plan-banner offer-plan-banner--flexible">MOST POPULAR</span>' : ''}
      <span class="offer-plan-row"><span class="offer-plan-radio" aria-hidden="true"></span><span class="offer-plan-copy"><strong>${annual ? 'Year-Plan' : plan === 'monthly' ? '4-Week plan' : '7-Day plan'}</strong>
      <span>${comparison ? `<s aria-label="${comparison.label}">€${(comparison.reference / 100).toFixed(2)}</s> ` : ''}<b>${escapeHtml(label)}</b> / ${annual ? 'year' : plan === 'monthly' ? '4 weeks' : 'week'}</span>
      ${comparison ? `<small>${comparison.label} · save ${comparison.percent}%</small>` : ''}</span>
      <span class="offer-unit-price"><strong>${escapeHtml(annual && annualWeeklyLabel ? annualWeeklyLabel : plan === 'monthly' && monthlyPrice ? formatPrice(monthlyPrice.amount / 4, monthlyPrice) : label)}</strong><small>per week</small></span></span>
    </button>`;
  };
  const selectedPrice = state.selectedPlan === 'annual' ? annualPriceLabel : state.selectedPlan === 'monthly' ? monthlyPriceLabel : weeklyPriceLabel;
  const selectedInterval = state.selectedPlan === 'annual' ? 'year' : state.selectedPlan === 'monthly' ? '4 weeks' : 'week';
  const planAnswers = rememberPlanAnswers(state.answers, sessionStorage, internalTrackingTest.funnelCode, state.user?.id ?? null);
  const personalized = getPersonalizedPlan(planAnswers);
  const selectedParsed = state.selectedPlan === 'annual' ? annualPrice : state.selectedPlan === 'monthly' ? monthlyPrice : weeklyPrice;
  const saving = selectedParsed?.prefix.includes('€') ? getPriceComparison('eur', state.selectedPlan === 'annual' ? 'year' : 'week', state.selectedPlan === 'monthly' ? 4 : 1, Math.round(selectedParsed.amount * 100))?.percent : null;
  const choices = () => `<section class="offer-plan-options" id="choose-plan" aria-label="Choose your Premium plan">
    <div class="offer-plan-list">${planCard('annual')}${planCard('monthly')}${planCard('weekly')}</div>
    ${pixelButton(state.isBusy ? 'Preparing your checkout...' : 'Unlock my plan', 'start-checkout', { block: true, disabled: state.isBusy || !state.planTermsAccepted })}
    <label class="offer-terms"><input type="checkbox" data-plan-terms ${state.planTermsAccepted ? 'checked' : ''} /><span>I agree to the <a href="/terms" target="_blank" rel="noopener">T&amp;Cs</a> and <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a></span></label>
    ${renderInlineReferral()}${renderStatus()}
    <div class="offer-trust-line"><span>✓ Cancel anytime</span><span>✓ Full Premium access</span></div>
    <p class="offer-renewal">${escapeHtml(selectedPrice)} charged today. Renews at ${escapeHtml(selectedPrice)} per ${selectedInterval} until cancelled.</p>
  </section>`;
  return `<div class="site-shell paid-plan-selection">${renderTopbar(true, true)}
    <div class="reference-checkout-expiry offer-sticky-bar"><div><div class="offer-clock">${saving ? `<b class="offer-saving-label">Save <em class="offer-saving-percent">${saving}%</em> on your plan</b>` : ''}<span data-plan-clock-label>The offer expires in:</span><strong data-plan-countdown>15:00</strong></div><button type="button" data-action="view-plans" class="offer-bar-cta">Get my plan <span aria-hidden="true">→</span></button></div></div>
    <main class="offer-plan-main">
      <section class="offer-plan-hero"><div class="offer-plan-intro"><span class="question-kicker">FOCUSCORP PREMIUM</span><h1>Your focus plan is ready.</h1><p>Choose the membership that works for you.</p></div>
      ${choices()}</section>
      <section class="offer-highlights"><span class="offer-eyebrow">BUILT AROUND YOUR ANSWERS</span><h2>A plan that feels like yours.</h2>${readQuizPlan(window.location.search)?.checkout === 'account' ? `<button class="quiet-button offer-restart-quiz" type="button" data-action="restart-quiz">Start a new quiz</button>` : ''}<div>
        ${personalized.highlights.map(item => `<article><img class="offer-highlight-art" src="/assets/checkout/${item.icon}-flat.svg" width="72" height="72" alt="" loading="lazy" /><div><h3>${escapeHtml(item.title)}</h3>${item.facts.length ? `<ul class="offer-answer-chips" aria-label="Your quiz answers">${item.facts.map(fact => `<li>${escapeHtml(fact)}</li>`).join('')}</ul>` : ''}<p>${escapeHtml(item.body)}</p></div></article>`).join('')}
      </div></section>
      <section class="offer-confidence"><span class="offer-eyebrow">FOCUS ON YOUR NEXT STEP</span><h2>Clear terms. You’re in control.</h2><div class="offer-confidence-grid">
        <article><span class="offer-confidence-icon" aria-hidden="true">${renderTrustIcon('shield')}</span><h3>Secure payment</h3><p>Stripe processes your payment. Your full card number never reaches FocusCorp.</p><div class="payment-brand-marks"><img src="/assets/payments/visa.svg" alt="Visa" /><img src="/assets/payments/mastercard.svg" alt="Mastercard" /></div></article>
        <article><span class="offer-confidence-icon" aria-hidden="true">${renderTrustIcon('calendar')}</span><h3>Cancel future renewals</h3><p>Manage your web subscription from your account, whenever you need to.</p><a href="/manage-subscription">Manage subscription →</a></article>
        <article><span class="offer-confidence-icon" aria-hidden="true">${renderTrustIcon('message')}</span><h3>Help when you need it</h3><p>Questions about billing, signing in or getting started? Get in touch with our team.</p><a href="/support">Contact FocusCorp →</a></article>
      </div></section>
      <section class="offer-faq"><h2>Before you start</h2>
        <details><summary>What do I get with each plan?</summary><p>All plans include the same FocusCorp Premium features: distracting-app blocking, a focus timer, rewards and office upgrades, and weekly leagues. The difference is how often you pay.</p></details>
        <details><summary>How much will I pay, and when?</summary><p>${escapeHtml(selectedPrice)} today, then ${escapeHtml(selectedPrice)} every ${selectedInterval} until you cancel. ${state.selectedPlan === 'annual' ? 'The weekly amount shown on the annual card is an equivalent; the full annual amount is billed once a year.' : state.selectedPlan === 'monthly' ? 'The 4-Week plan renews every four weeks. The weekly amount shown is one quarter of the four-week charge.' : 'The 7-Day plan renews each week.'} The final total is shown in checkout before you pay.</p></details>
        <details><summary>How do I cancel my subscription?</summary><p>Open <a href="/manage-subscription">Manage web subscription</a> and sign in with the account you used to buy. Cancel before your next renewal to stop future charges. Cancelling a renewal does not refund a completed payment.</p></details>
        <details><summary>How do I use my purchase in the iPhone app?</summary><p>Download FocusCorp from the App Store and sign in with the same account you created here. Your web purchase is linked to that account. If Premium does not appear, use the app’s restore option or <a href="/support">contact support</a>.</p></details>
        <details><summary>Does it actually block apps?</summary><p>Yes. On your iPhone, grant the requested Screen Time permission and choose the apps you want to restrict. FocusCorp uses those permissions to block distractions. You decide which apps belong on your list.</p></details>
        <details><summary>Do I have to follow this routine exactly?</summary><p>No. Your quiz answers provide a starting point. Choose your task, adjust the focus timer and block list in the app, and build a routine you can keep.</p></details>
      </section>
    </main>${renderFounderOfferModal()}</div>`;
}

function renderEmbeddedPlanSummary() {
  const isAnnual = state.selectedPlan === 'annual';
  const price = getCheckoutPriceLabel();
  const interval = isAnnual ? 'year' : state.selectedPlan === 'monthly' ? '4 weeks' : 'week';
  const planTitle = state.activeOffer === 'firstpick'
    ? 'FIRSTPICK Annual'
    : state.activeOffer === 'focuscorp'
      ? 'FOCUSCORP Annual'
      : state.activeOffer === 'founder'
        ? 'Founder Annual'
      : isAnnual
        ? 'Year-Plan'
        : state.selectedPlan === 'monthly' ? '4-Week plan' : '7-Day plan';
  const valueNote = state.activeOffer === 'firstpick'
    ? '7-day trial, then billed yearly'
    : state.activeOffer === 'focuscorp'
      ? '30-day trial, then billed yearly'
      : state.activeOffer === 'founder'
        ? `First year ${founderOfferPriceLabel}, then ${annualPriceLabel}`
      : isAnnual && annualWeeklyLabel
        ? `About ${annualWeeklyLabel} per week`
        : '';

  return `
    <aside class="embedded-plan-summary">
      <div class="question-kicker">YOUR PREMIUM PLAN</div>
      <div class="embedded-plan-heading">
        <div>
          <span>${escapeHtml(planTitle)}</span>
          ${checkoutUi === 'embedded' ? `<strong>${escapeHtml(price)}<small>/ ${interval}</small></strong>` : ''}
        </div>
        ${valueNote && checkoutUi === 'embedded' ? `<div class="embedded-value-note">${escapeHtml(valueNote)}</div>` : ''}
      </div>
      <ul class="embedded-benefits">
        <li><span>✓</span>Block distracting apps</li>
        <li><span>✓</span>Earn rewards for focused time</li>
        <li><span>✓</span>Upgrade your office and climb leagues</li>
        <li><span>✓</span>Sync Premium with your FocusCorp account</li>
      </ul>
      <div class="embedded-reassurance">
        <span>Secure payment by Stripe</span>
        <span>Cancel anytime</span>
        <span>Premium syncs automatically</span>
      </div>
    </aside>
  `;
}

function renderNativeCheckout() {
  return `
    <div class="site-shell reference-checkout-page">
      ${renderTopbar(true, true)}
      <div class="reference-checkout-expiry" data-checkout-expiry hidden><div><div class="offer-clock"><span data-checkout-timer-label>The offer expires in:</span></div><strong data-checkout-countdown></strong></div></div>
      <main class="reference-checkout-main">
        <a class="checkout-back-button" href="${getTestFunnelPath('/')}" data-action="cancel-embedded-checkout">← Back to plans</a>
        <div class="reference-checkout-columns">
          <div class="reference-payment-column"><section class="stripe-checkout-panel reference-payment-card">
            <h1>Select a payment method</h1>
            ${embeddedCheckoutError ? `
              <div class="embedded-checkout-error" role="alert"><strong>Payment details couldn’t load.</strong><p>${escapeHtml(embeddedCheckoutError)}</p>${pixelButton('Try again', 'retry-embedded-checkout', { block: true })}</div>
            ` : `
              <div class="embedded-checkout-frame">
                <div class="embedded-checkout-loading" aria-live="polite"><span class="checkout-spinner" aria-hidden="true"></span>Loading secure payment…</div>
                <div id="embedded-checkout"></div>
              </div>
            `}
          </section>
          ${renderPaymentReassurance()}</div>
          <aside class="reference-order-card" data-payment-summary aria-label="Order summary"><p>Loading your plan…</p></aside>
        </div>
        <div class="native-checkout-legal"><a href="/terms" target="_blank" rel="noopener">Terms</a><span>·</span><a href="/privacy" target="_blank" rel="noopener">Privacy</a><span>·</span><a href="/support" target="_blank" rel="noopener">Need a hand?</a></div>
      </main>
    </div>`;
}

function renderEmbeddedCheckout() {
  if (checkoutUi !== 'embedded') return renderNativeCheckout();
  const heading = checkoutUi !== 'embedded' ? 'Make room for real focus.' : state.activeOffer === 'firstpick'
    ? 'Start your FIRSTPICK trial.'
    : state.activeOffer === 'focuscorp'
      ? 'Start your free month.'
      : state.activeOffer === 'founder'
        ? 'Claim your founder price.'
      : 'Lock in. Build your company.';
  const body = checkoutUi !== 'embedded' ? 'Your plan is ready. One last step to start building.' : state.activeOffer === 'firstpick'
    ? 'Your neon reward is secured. Complete checkout to begin the 7-day Premium trial.'
    : state.activeOffer === 'focuscorp'
      ? `Add your payment details now. Your ${focuscorpOfferPriceLabel} annual subscription starts after the 30-day free trial unless cancelled.`
      : state.activeOffer === 'founder'
        ? `Pay ${founderOfferPriceLabel} for the first year, then ${annualPriceLabel} per year until cancelled.`
      : 'Turn every distraction-free session into progress inside your virtual startup.';

  return `
    <div class="site-shell embedded-checkout-page${checkoutUi !== 'embedded' ? ' native-checkout-page' : ''}">
      ${renderTopbar()}
      <main class="embedded-checkout-layout">
        <section class="stripe-checkout-panel">
          <button class="checkout-back-button" type="button" data-action="cancel-embedded-checkout">← Back to plans</button>
          <div class="question-kicker">SECURE CHECKOUT</div>
          <h1>${escapeHtml(heading)}</h1>
          <p>${escapeHtml(body)}</p>
          ${
            embeddedCheckoutError
              ? `
                <div class="embedded-checkout-error" role="alert">
                  <strong>Checkout could not load.</strong>
                  <p>${escapeHtml(embeddedCheckoutError)}</p>
                  ${pixelButton('Try again', 'retry-embedded-checkout', { block: true })}
                </div>
              `
              : `
                <div class="embedded-checkout-frame">
                  <div class="embedded-checkout-loading" aria-live="polite">
                    <span class="checkout-spinner" aria-hidden="true"></span>
                    Loading secure payment…
                  </div>
                  <div id="embedded-checkout"></div>
                </div>
              `
            }
        </section>
        ${renderEmbeddedPlanSummary()}
      </main>
    </div>
  `;
}

function renderCheckoutComplete() {
  const isLoading = checkoutCompletionState === 'loading' || checkoutCompletionState === 'idle';
  const title = isLoading
    ? 'Confirming your payment…'
    : checkoutCompletionState === 'incomplete'
      ? 'Payment is not complete.'
      : 'We could not verify the payment yet.';
  const body = isLoading
    ? 'Stripe is confirming the Checkout Session and FocusCorp is syncing Premium to your account.'
    : checkoutCompletionMessage ?? 'Return to your plan or retry verification.';

  return `
    <div class="site-shell embedded-checkout-page">
      ${renderTopbar()}
      <main class="checkout-complete-layout">
        <section class="checkout-complete-card">
          ${isLoading ? '<span class="checkout-spinner large" aria-hidden="true"></span>' : '<div class="checkout-state-icon">!</div>'}
          <div class="question-kicker">CHECKOUT STATUS</div>
          <h1>${escapeHtml(title)}</h1>
          <p>${escapeHtml(body)}</p>
          ${
            isLoading
              ? ''
              : `
                <div class="checkout-complete-actions">
                  ${pixelButton('Retry verification', 'retry-checkout-verification', { block: true })}
                  <button class="ghost-button" type="button" data-action="completion-return-plans">Return to plans</button>
                </div>
              `
          }
        </section>
      </main>
    </div>
  `;
}

function renderPriceCard(
  plan: PlanKey,
  title: string,
  price: string,
  interval: string,
  billingNote: string,
  badge: string | null,
) {
  return `
    <button class="price-card ${state.selectedPlan === plan ? 'selected' : ''}" type="button" data-action="select-plan" data-plan="${plan}">
      ${badge ? `<span class="price-badge">${escapeHtml(badge)}</span>` : ''}
      <strong>${escapeHtml(title)}</strong>
      <div class="price-line"><span>${escapeHtml(price)}</span><small>/ ${escapeHtml(interval)}</small></div>
      <p>${escapeHtml(billingNote)}</p>
    </button>
  `;
}

function renderTrialPriceCard(usualPrice: string, trialLabel: string, badge: string) {
  return `
    <button class="price-card trial-price-card ${state.selectedPlan === 'annual' ? 'selected' : ''}" type="button" data-action="select-plan" data-plan="annual">
      <span class="price-badge">${escapeHtml(badge)}</span>
      <strong>Yearly Premium</strong>
      <div class="trial-price-line">
        <span class="trial-price-now"><span class="trial-price-amount">€0</span><small>Today</small></span>
        <span class="trial-price-was">Usually <s>${escapeHtml(usualPrice)}/year</s></span>
      </div>
      <p class="trial-renewal-note">Renews at ${escapeHtml(usualPrice)}/year after your ${escapeHtml(trialLabel)}. Cancel anytime.</p>
    </button>
  `;
}

function renderDownload() {
  const sourceCopy = state.premiumSource === 'referral'
      ? 'Your referral reward is active.'
      : state.premiumSource === 'restore'
        ? 'Your existing web purchase is active.'
        : 'Your Stripe purchase is being activated.';

  return `
    <div class="site-shell">
      ${renderTopbar()}
      <main class="download-layout">
        ${renderSummaryPanel('Premium is attached to this login.', 'Install the iOS app and sign in with the account you just created.', 'See you at the office!')}
        <section class="download-card">
          <div class="question-kicker">CONGRATULATIONS</div>
          <h1>Your FocusCorp Premium is ready.</h1>
          <p class="question-body">${escapeHtml(sourceCopy)} Download the app and log in with this account — your plan, your startup, and Premium will be waiting. First session starts today.</p>
          <div class="download-actions">
            <a class="app-store-button" href="${escapeHtml(appStoreUrl)}" data-action="download-clicked" data-download-method="badge" aria-label="Download FocusCorp on the App Store">
              <img src="/assets/download-on-app-store.png" alt="Download on the App Store" />
            </a>
            <div class="download-qr">
              <img src="/assets/app-store-qr.png" alt="QR code linking to FocusCorp on the App Store" />
              <span>On a computer?<br /><strong>Scan with your iPhone</strong></span>
            </div>
          </div>
        </section>
      </main>
      ${renderFooter()}
    </div>
  `;
}

function renderSummaryPanel(title: string, body: string, bubbleText: string) {
  const payload = buildPersistedOnboarding();

  return `
    <aside class="summary-panel">
      <div class="summary-scene">
        ${renderOfficeScene({ bubbleText })}
      </div>
      <div class="summary-body">
        <h2>${escapeHtml(title)}</h2>
        <p>${escapeHtml(body)}</p>
        <div class="plan-list">
          <div class="plan-row"><span>Name</span><strong>${escapeHtml(payload.answers.userName ?? 'Founder')}</strong></div>
          <div class="plan-row"><span>Company</span><strong>${escapeHtml(payload.personalization.startupName)}</strong></div>
          <div class="plan-row"><span>Plan</span><strong>${escapeHtml(payload.generatedPlan.dailyTarget)}</strong></div>
        </div>
      </div>
    </aside>
  `;
}

function renderStatus() {
  if (!state.statusMessage || !state.statusTone) {
    return '';
  }

  return `<div class="status ${state.statusTone}" role="status">${escapeHtml(state.statusMessage)}</div>`;
}

let planRefreshTimer: ReturnType<typeof setInterval> | undefined;
function bindPlanRefreshClock() {
  if (planRefreshTimer) clearInterval(planRefreshTimer);
  planRefreshTimer = undefined;
  const clock = root.querySelector<HTMLElement>('[data-plan-countdown]');
  if (!clock) return;
  const deadline = getCheckoutRefreshDeadline('plan_review', null, sessionStorage);
  const tick = () => {
    const remaining = Math.max(0, deadline - Math.floor(Date.now() / 1000));
    clock.textContent = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
    if (!remaining) {
      clearInterval(planRefreshTimer);
    }
  };
  planRefreshTimer = setInterval(tick, 1000);
  tick();
}

function syncOrganicHeader() {
  if (!organicGuestEnabled) return;
  const header = root.querySelector<HTMLElement>('.topbar');
  if (!header || header.classList.contains('organic-funnel-header')) return;
  const close = header.querySelector('.checkout-close-button')?.outerHTML ?? '';
  const links = '<a href="/#how">How it works</a><a href="/privacy">Privacy</a><a href="/support">Support</a>';
  header.outerHTML = `<header class="topbar organic-funnel-header">
    <a class="brand" href="/" aria-label="FocusCorp home"><img src="/assets/optimized/logo.webp" width="34" height="34" alt="" /><span>FOCUSCORP</span></a>
    <div class="organic-header-actions">
      <nav class="organic-header-links" aria-label="Primary">${links}</nav>
      <details class="organic-header-menu"><summary>Menu <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 5h14M3 10h14M3 15h14" /></svg></summary><nav aria-label="Primary">${links}</nav></details>
      ${close}
    </div>
  </header>`;
}

function syncGuestConsent() {
  syncOrganicHeader();
  root.querySelectorAll('.tracking-consent, .tracking-consent-backdrop, .guest-tracking-preferences').forEach(element => element.remove());
  if (organicGuestEnabled) root.insertAdjacentHTML('beforeend', '<div class="guest-tracking-preferences"><button class="footer-text-button" type="button" data-action="change-tracking-consent">Tracking preferences</button></div>');
  root.insertAdjacentHTML('beforeend', renderTrackingConsent());
  document.documentElement.removeAttribute('data-fc1-entry');
}

function render() {
  guidedSpeechEffect?.cancel();
  guidedHoldEffect?.destroy();
  document.documentElement.toggleAttribute('data-quiz-active', state.view === 'quiz');
  document.documentElement.toggleAttribute('data-guided-quiz', guidedOnboardingEnabled && state.view === 'quiz');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', state.view === 'quiz' && !guidedOnboardingEnabled ? '#061120' : '#f6f5f0');
  if (devOfferPreview === 'plan-loading') { root.innerHTML = renderFunnelLoading(); return; }
  // Warm the email UI while the final quiz screen is visible. This only loads
  // its code/assets; guest sessions are still created when the flow mounts.
  if (guestCheckoutPreviewEnabled && state.view === 'quiz' && currentScreen === ONBOARDING_SOCIAL_PROOF_SCREEN) {
    void loadGuestCheckoutModule().catch(() => { /* The actual handoff can retry. */ });
  }
  if (guestCheckoutPreviewEnabled && (state.view === 'account' || window.location.pathname.startsWith('/guest/'))) {
    if (!guestCheckoutPreviewStarted) {
      guestCheckoutPreviewStarted = true;
      root.innerHTML = renderFunnelLoading();
      void Promise.all([loadGuestCheckoutModule(),offerBadgeArmReady]).then(([{ mountGuestCheckoutPreview },badgeArm]) => mountGuestCheckoutPreview(root, buildPersistedOnboarding().answers, buildPersistedOnboarding() as unknown as Record<string, unknown>, {nameStepArm,badgeArm,onRestoredNameStepArm:arm=>{nameStepArm=arm;},skipMethods:organicGuestEnabled,live:guestCheckoutHostedEnabled,backend:guestCheckoutTestEnabled ? {url:import.meta.env.VITE_GUEST_TEST_SUPABASE_URL,anonKey:import.meta.env.VITE_GUEST_TEST_SUPABASE_ANON_KEY,expectedLivemode:false} : undefined,appStoreUrl,afterRender:syncGuestConsent,onAccountCheckout:answers=>{guestCheckoutPreviewEnabled=false;state.answers=buildPersistedAnswers(answers as OnboardingAnswers);history.replaceState({},'',organicGuestEnabled ? '/?flow=account' : guestCheckoutTestEnabled ? '/?qz=tst1&flow=account' : '/?qz=fc1&flow=account');setView(state.user ? 'checkout' : 'account');},analytics:createGuestAnalytics({consent:getTrackingConsent,search:()=>location.search,capture,pixel:trackMetaPixelEvent,customPixel:(name,properties)=>trackMetaPixelCustomEvent(name,properties,undefined,getTrackingConsent()),context:getCurrentMetaTrackingContext,receiptStorage:sessionStorage}),attribution:getCheckoutAttribution})).catch(() => {
        root.innerHTML = '<div class="site-shell"><p role="alert">Checkout could not load. Please refresh to retry.</p></div>';
      });
    }
    syncGuestConsent();
    return;
  }
  if (['account', 'referral', 'firstpickReward', 'checkout', 'embeddedCheckout'].includes(state.view)) {
    void prepareStripe()?.catch(() => { /* Checkout offers a retry if the SDK download fails. */ });
  }
  if (lastTrackedFunnelView !== state.view) {
    lastTrackedFunnelView = state.view;
    capture('funnel_viewed', { funnel_view: state.view });
  }

  switch (state.view) {
    case 'landing':
      root.innerHTML = renderLanding();
      break;
    case 'quiz':
      root.innerHTML = renderQuiz();
      break;
    case 'account':
      root.innerHTML = renderAccount();
      break;
    case 'referral':
      root.innerHTML = renderReferral();
      break;
    case 'firstpickReward':
      root.innerHTML = renderFirstpickReward();
      break;
    case 'checkout':
      root.innerHTML = renderCheckout();
      break;
    case 'embeddedCheckout':
      // Keep Stripe's managed iframe attached when consent or other UI changes.
      if (embeddedCheckoutError || !root.querySelector('#embedded-checkout')) {
        root.innerHTML = renderEmbeddedCheckout();
      }
      break;
    case 'checkoutComplete':
      root.innerHTML = renderCheckoutComplete();
      break;
    case 'download':
      root.innerHTML = renderDownload();
      break;
    case 'manageSubscription':
      root.innerHTML = renderManageSubscription();
      break;
  }

  if (state.view === 'embeddedCheckout') {
    // Update the exit offer without detaching Stripe's managed payment fields.
    root.querySelector('.founder-offer-backdrop')?.remove();
    root.insertAdjacentHTML('beforeend', renderFounderOfferModal());
  }

  root.querySelectorAll('.tracking-consent, .tracking-consent-backdrop').forEach((element) => element.remove());
  root.insertAdjacentHTML('beforeend', renderTrackingConsent());
  document.documentElement.removeAttribute('data-fc1-entry');
  if (guidedOnboardingEnabled) {
    if (state.view === 'quiz') syncOrganicHeader();
    normalizeGuidedDashes(root);
    const speech = root.querySelector<HTMLElement>('.guided-speech-visible');
    const fullSpeech = root.querySelector('.guided-speech-measure')?.textContent ?? '';
    if (speech) {
      const key = `${activeQuizRunId}:${currentScreen}:${fullSpeech}`;
      const bob = root.querySelector<HTMLElement>('.guided-bob-sprite');
      guidedSpeechEffect = startStableGuidedSpeech(speech, fullSpeech, key === lastGuidedSpeechKey || window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        typing => { if (bob) setGuidedBobTyping(bob, typing); });
      lastGuidedSpeechKey = key;
    } else lastGuidedSpeechKey = null;
    const hold = root.querySelector<HTMLButtonElement>('.guided-hold-button');
    if (hold) guidedHoldEffect = mountGuidedCommitment(hold, () => {
      state.answers = { ...state.answers, commitmentLevel: 'Ready to start' };
      completeStep(currentScreen, { answer_key: 'commitmentLevel', answer_value: 'Ready to start', commitment_method: 'hold' });
      goToScreen(getNextOnboardingFlowScreen(currentScreen));
    });
  }
  bindPlanRefreshClock();

  if (state.view === 'embeddedCheckout' && !embeddedCheckoutError) {
    queueMicrotask(() => void mountEmbeddedCheckout());
  }

  if (state.view === 'checkout') {
    queueMicrotask(armCheckoutExitGuard);
  }

  if (state.view === 'checkoutComplete') {
    queueMicrotask(() => void verifyCheckoutCompletion());
  }
}

function handleChoice(step: ChoiceStep, option: string) {
  const nextAnswers = {
    ...state.answers,
    [step.answerKey]: option,
  };
  state.answers = nextAnswers;
  completeStep(step.screen, {
    answer_key: step.answerKey,
    answer_value: option,
  });
  const reaction = step.reactions[option];

  if (!reaction || !isQuestionReactionCheckpoint(step.screen)) {
    goToScreen(getNextOnboardingFlowScreen(step.screen));
    return;
  }

  startReaction(
    getQuestionCheckpointReaction(step.screen, nextAnswers, reaction),
    getNextOnboardingFlowScreen(step.screen),
  );
}

function refreshMultiSelection() {
  if (!guidedOnboardingEnabled) { render(); return; }
  const step = MULTI_STEP_BY_SCREEN.get(currentScreen);
  if (!step) return;
  const selected = getStoredArrayAnswer(state.answers, step.answerKey);
  root.querySelectorAll<HTMLButtonElement>('[data-action="multi-toggle"]').forEach(button => {
    const active = selected.includes(button.dataset.option ?? '');
    button.classList.toggle('selected', active);
    button.setAttribute('aria-pressed', String(active));
  });
  const next = root.querySelector<HTMLButtonElement>('[data-action="multi-submit"]');
  if (next) next.disabled = selected.length === 0;
}

function handleMultiToggle(step: MultiStep, option: string) {
  const currentSelections = getStoredArrayAnswer(state.answers, step.answerKey);

  if (step.answerKey === 'focusKillers' && option === 'All of the above') {
    state.answers = {
      ...state.answers,
      [step.answerKey]: currentSelections.includes(option) ? [] : [option],
    };
    refreshMultiSelection();
    return;
  }

  const withoutAll =
    step.answerKey === 'focusKillers'
      ? currentSelections.filter((selection) => selection !== 'All of the above')
      : currentSelections;
  const nextSelections = withoutAll.includes(option)
    ? withoutAll.filter((selection) => selection !== option)
    : [...withoutAll, option];

  state.answers = {
    ...state.answers,
    [step.answerKey]: nextSelections,
  };
  refreshMultiSelection();
}

function handleMultiSubmit(step: MultiStep) {
  const selections = getStoredArrayAnswer(state.answers, step.answerKey);

  if (selections.length === 0) {
    return;
  }

  const nextAnswers = {
    ...state.answers,
    [step.answerKey]: selections,
  };
  state.answers = nextAnswers;
  completeStep(step.screen, {
    answer_key: step.answerKey,
    answer_value: selections,
  });
  const reaction = getMultiReaction(step.answerKey, selections, nextAnswers);

  if (!isQuestionReactionCheckpoint(step.screen)) {
    goToScreen(getNextOnboardingFlowScreen(step.screen));
    return;
  }

  startReaction(
    getQuestionCheckpointReaction(step.screen, nextAnswers, reaction),
    getNextOnboardingFlowScreen(step.screen),
  );
}

function handleTextSubmit(step: TextStep) {
  const rawValue = getStoredStringAnswer(state.answers, step.answerKey);
  const trimmedValue = rawValue.replace(/\s+/g, ' ').trim();

  if (step.validatesRequired && trimmedValue.length === 0) {
    return;
  }

  const nextAnswers = {
    ...state.answers,
    [step.answerKey]: step.multiline ? rawValue.trim() : trimmedValue,
  };
  state.answers = nextAnswers;
  completeStep(step.screen, {
    answer_key: step.answerKey,
    answer_value_length: trimmedValue.length,
  });

  if (!isQuestionReactionCheckpoint(step.screen)) {
    goToScreen(getNextOnboardingFlowScreen(step.screen));
    return;
  }

  startReaction(
    getQuestionCheckpointReaction(step.screen, nextAnswers, step.reaction),
    getNextOnboardingFlowScreen(step.screen),
  );
}

function handleClick(event: MouseEvent) {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-action]') : null;

  if (!target) {
    return;
  }

  // Before hydration, Close remains a normal link. The static CTA is disabled
  // until a quiz run exists, so it cannot trigger a second document load.
  if (target.closest('#paid-quiz-entry')) {
    return;
  }

  const action = target.dataset.action;

  if (!action) {
    return;
  }

  if (action === 'reveal-guided-speech') {
    guidedSpeechEffect?.finish();
    return;
  }
  if (action === 'guided-commitment-choices' || action === 'guided-commitment-hold') {
    guidedCommitmentChoices = action === 'guided-commitment-choices';
    render();
    return;
  }

  if (action === 'change-tracking-consent') {
    event.preventDefault();
    trackingPreferencesVisible = true;
    render();
    return;
  }

  if (action === 'close-tracking-consent') {
    event.preventDefault();
    trackingPreferencesVisible = false;
    render();
    return;
  }

  if (action === 'accept-tracking' || action === 'reject-tracking') {
    event.preventDefault();
    if (isInternalTrackingTestActive(internalTrackingTest)) stopInternalTrackingTest();
    const consent = action === 'accept-tracking' ? 'accepted' : 'rejected';
    setTrackingConsent(consent);
    trackingPreferencesVisible = false;

    if (consent === 'accepted') {
      initializeOptionalTracking();
      void identifyUser(state.user);
    } else {
      disableOptionalTracking();
    }

    render();
    return;
  }

  // Keep this as a normal anchor navigation so iOS can hand the URL directly
  // to the App Store instead of requiring a long-press/open-in-new-tab action.
  if (action === 'download-clicked') {
    metaFunnelSignals.appStoreClicked(target.dataset.downloadMethod ?? 'badge');
    capture('download_clicked', {
      download_method: target.dataset.downloadMethod ?? 'badge',
      premium_source: state.premiumSource,
    });
    return;
  }

  event.preventDefault();

  if (action === 'manage-subscription') {
    openManageSubscriptionPage();
    return;
  }

  if (action === 'billing-apple-signin') {
    void signInWithAppleForSubscriptionManagement();
    return;
  }

  if (action === 'open-stripe-portal') {
    void openStripeSubscriptionPortal();
    return;
  }

  if (action === 'billing-sign-out') {
    void signOutFromSubscriptionManagement();
    return;
  }

  if (action === 'checkout-exit') {
    requestCheckoutExit('landing');
    return;
  }

  if (action === 'founder-offer-stay') {
    return;
  }

  if (action === 'founder-offer-accept') {
    capture('founder_offer_accepted', {
      exit_intent: state.checkoutExitIntent ?? 'unknown',
      previous_offer: state.activeOffer ?? 'standard',
    });
    if (state.view === 'embeddedCheckout') {
      setView('checkout');
    }
    state.activeOffer = 'founder';
    state.selectedPlan = 'annual';
    state.founderOfferVisible = false;
    state.checkoutExitIntent = null;
    void startCheckout();
    return;
  }

  if (action === 'founder-offer-decline') {
    completeCheckoutExit(state.checkoutExitIntent ?? 'landing');
    return;
  }

  if (action === 'landing') {
    if (state.view === 'checkout') {
      requestCheckoutExit('landing');
      return;
    }

    captureQuizExit('close_button');
    clearFlowTimers();
    if (
      state.view === 'manageSubscription' ||
      getWebOnboardingScreenForPath(window.location.pathname) !== null
    ) {
      window.history.pushState({}, '', getTestFunnelPath('/'));
    }
    setView('landing');
    return;
  }

  if (action === 'start-quiz') {
    const savedPlan = readQuizPlan(window.location.search);
    if (savedPlan) {
      state.answers = buildPersistedAnswers(savedPlan.answers,{omitMissingName:nameStepArm==='no_name'});
      try { resetPlanReviewDeadline(sessionStorage); } catch { /* Continuing still works when browser storage is blocked. */ }
      if (savedPlan.checkout === 'guest' && guestCheckoutPreviewEnabled) {
        try { sessionStorage.setItem('focuscorp_guest_onboarding_draft', JSON.stringify(buildPersistedOnboarding())); } catch { /* The server draft remains available. */ }
        window.location.assign(quizPlanHomePath(window.location.search).replace('/', '/guest/plans'));
      } else setView(state.user ? 'checkout' : 'account');
      return;
    }
    startQuiz();
    return;
  }
  if (action === 'restart-quiz') {
    clearQuizPlan(window.location.search);
    startQuiz();
    return;
  }

  if (action === 'quiz-back') {
    if (currentScreen > ONBOARDING_INTRO_SCREEN && !reactionText) {
      // Screen Time and notification permission prompts are native-only.
      goToScreen(resolveNameStepScreen(
        currentScreen === ONBOARDING_SOCIAL_PROOF_SCREEN
          ? ONBOARDING_PLAN_LOADING_SCREEN
          : getPreviousOnboardingFlowScreen(currentScreen),nameStepArm,'backward'),
      );
    }
    return;
  }

  if (action === 'finish-reaction') {
    finishReaction();
    return;
  }

  if (action === 'intro-next') {
    completeStep(ONBOARDING_INTRO_SCREEN);
    goToScreen(ONBOARDING_PITCH_SCREEN);
    return;
  }

  if (action === 'choose-entry-identity') {
    const identity = normalizeEntryIdentity(target.dataset.identity);
    if (!identityEntryEnabled || state.view !== 'quiz' || currentScreen !== ONBOARDING_INTRO_SCREEN || !identity) return;
    state.answers.entryIdentity = identity;
    try { sessionStorage.setItem(identityAnswerStorageKey, identity); } catch { /* Optional answer persistence. */ }
    completeStep(ONBOARDING_INTRO_SCREEN, { entry_variant: 'identity', entry_identity: identity });
    goToScreen(ONBOARDING_PITCH_SCREEN);
    return;
  }

  if (action === 'apple-signup') {
    const form = target.closest<HTMLFormElement>('form');

    if (form) {
      void startAppleSignup(form);
    }
    return;
  }

  if (action === 'retry-account-save') {
    void retryAuthenticatedWebSignup();
    return;
  }

  if (action === 'choice') {
    const step = CHOICE_STEP_BY_SCREEN.get(currentScreen);
    const option = target.dataset.option;

    if (step && option) {
      handleChoice(step, option);
    }
    return;
  }

  if (action === 'multi-toggle') {
    const step = MULTI_STEP_BY_SCREEN.get(currentScreen);
    const option = target.dataset.option;

    if (step && option) {
      handleMultiToggle(step, option);
    }
    return;
  }

  if (action === 'multi-submit') {
    const step = MULTI_STEP_BY_SCREEN.get(currentScreen);

    if (step) {
      handleMultiSubmit(step);
    }
    return;
  }

  if (action === 'text-submit') {
    const step = TEXT_STEP_BY_SCREEN.get(currentScreen);

    if (step) {
      handleTextSubmit(step);
    }
    return;
  }

  if (action === 'hours-dec' || action === 'hours-inc') {
    const delta = action === 'hours-inc' ? 1 : -1;
    state.answers = {
      ...state.answers,
      dailyPhoneHours: Math.max(1, Math.min(12, getDailyPhoneHours(state.answers) + delta)),
    };
    if (guidedOnboardingEnabled) {
      const hours = root.querySelector('.stepper-value strong');
      if (hours) hours.textContent = String(getDailyPhoneHours(state.answers));
    } else render();
    return;
  }

  if (action === 'pitch-next') {
    completeStep(ONBOARDING_PITCH_SCREEN);
    void funnelExperimentReady.then(()=>goToScreen(ONBOARDING_FIRST_QUESTION_SCREEN));
    return;
  }

  if (action === 'phone-hours-next') {
    state.answers = {
      ...state.answers,
      dailyPhoneHours: getDailyPhoneHours(state.answers),
    };
    completeStep(ONBOARDING_PHONE_HOURS_SCREEN, {
      answer_key: 'dailyPhoneHours',
      answer_value: state.answers.dailyPhoneHours,
    });
    goToScreen(ONBOARDING_PHONE_MATH_SCREEN);
    return;
  }

  if (action === 'phone-math-next') {
    completeStep(ONBOARDING_PHONE_MATH_SCREEN);
    startReaction(
      getPhoneMathReaction(getDailyPhoneHours(state.answers), state.answers),
      ONBOARDING_RECLAIM_SCREEN,
    );
    return;
  }

  if (action === 'reclaim-next') {
    completeStep(ONBOARDING_RECLAIM_SCREEN);
    goToScreen(ONBOARDING_RECLAIM_SCREEN + 1);
    return;
  }

  if (action === 'reflection-next') {
    completeStep(ONBOARDING_REFLECTION_SCREEN);
    goToScreen(getNextOnboardingFlowScreen(ONBOARDING_REFLECTION_SCREEN));
    return;
  }

  if (action === 'social-proof-next') {
    completeStep(ONBOARDING_SOCIAL_PROOF_SCREEN);
    completeWebOnboarding();
    return;
  }

  if (action === 'skip-referral') {
    capture('referral_skipped');
    state.activeOffer = null;
    setView('checkout');
    return;
  }

  if (action === 'view-plans') {
    root.querySelector('#choose-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  if (action === 'select-plan') {
    state.selectedPlan = target.dataset.plan === 'weekly' ? 'weekly' : target.dataset.plan === 'monthly' ? 'monthly' : 'annual';
    capture('plan_selected', { plan: state.selectedPlan });
    capture('checkout_plan_selected', getCheckoutAnalyticsProperties());
    render();
    return;
  }

  if (action === 'start-checkout') {
    void startCheckout();
    return;
  }

  if (action === 'cancel-embedded-checkout') {
    cancelEmbeddedCheckout('back_button');
    return;
  }

  if (action === 'retry-embedded-checkout') {
    clearEmbeddedCheckoutSession();
    state.view = 'checkout';
    setStatus(null);
    render();
    void startCheckout();
    return;
  }

  if (action === 'retry-checkout-verification') {
    checkoutCompletionStarted = false;
    checkoutCompletionState = 'loading';
    checkoutCompletionMessage = null;
    render();
    return;
  }

  if (action === 'completion-return-plans') {
    checkoutCompletionSessionId = null;
    checkoutCompletionStarted = false;
    checkoutCompletionState = 'idle';
    window.history.replaceState({}, '', getTestFunnelPath('/'));
    setView('checkout');
    return;
  }

}

function handleInput(event: Event) {
  const target = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement
    ? event.target
    : null;

  if (target?.name === 'referralCode') {
    referralDraft = target.value;
    return;
  }

  if (!target?.dataset.answerKey) {
    return;
  }

  const key = target.dataset.answerKey as TextAnswerKey;
  state.answers = {
    ...state.answers,
    [key]: target.value,
  };
}

function handleSubmit(event: SubmitEvent) {
  const form = event.target instanceof HTMLFormElement ? event.target : null;

  if (!form) {
    return;
  }

  if (form.id === 'account-form') {
    event.preventDefault();
    void submitAccount(form);
  }

  if (form.id === 'referral-form') {
    event.preventDefault();
    void applyReferral(form);
  }

  if (form.id === 'billing-login-form') {
    event.preventDefault();
    void signInForSubscriptionManagement(form);
  }
}

async function hydrateAuth(resumePendingSignup = true) {
  if (!supabase) {
    return;
  }

  const isCallback = isWebAuthCallbackRoute();
  const callbackHasError = isCallback && hasWebAuthCallbackError();
  const billingAuthCallback = isCallback && hasPendingBillingAuth();
  const storedPending = loadPendingWebSignup();

  // Install the observer even if the initial storage/session read fails.
  supabase.auth.onAuthStateChange((_event, session) => {
    state.session = session;
    state.user = session?.user ?? null;
    void identifyUser(state.user);
  });

  const { data: storedSessionData, error: storedSessionError } = await supabase.auth.getSession();
  state.session = storedSessionData.session;
  state.user = storedSessionData.session?.user ?? null;
  void identifyUser(state.user);

  if (callbackHasError) {
    if (billingAuthCallback) {
      clearPendingBillingAuth();
      state.view = 'manageSubscription';
      setStatus('Apple sign-in was not completed. Please try again.', 'error');
      cleanWebAuthCallbackUrl('/manage-subscription');
      return;
    }

    if (storedPending) {
      state.answers = buildPersistedAnswers(storedPending.answers);
    }
    clearPendingWebSignup();
    state.view = 'account';
    setStatus(
      storedPending?.provider === 'email'
        ? 'Email confirmation was not completed. You can try again.'
        : 'Apple sign-up was not completed. You can try again.',
      'error',
    );
    cleanWebAuthCallbackUrl();
    return;
  }

  if (!isCallback) {
    if (
      resumePendingSignup &&
      !storedSessionError &&
      storedPending?.authenticatedUserId &&
      storedSessionData.session?.user.id === storedPending.authenticatedUserId
    ) {
      await resumePendingWebSignup(storedSessionData.session, storedPending);
    }
    return;
  }

  const isBoundRetry = Boolean(
    storedPending?.authenticatedUserId &&
    storedSessionData.session &&
    storedPending.authenticatedUserId === storedSessionData.session.user.id,
  );

  if (isBoundRetry && storedPending && storedSessionData.session) {
    await resumePendingWebSignup(storedSessionData.session, storedPending);
    return;
  }

  const exchangeResult = initialWebAuthCallbackCode
    ? await supabase.auth.exchangeCodeForSession(initialWebAuthCallbackCode)
    : null;
  const verifiedSession = exchangeResult?.data.session ?? null;
  const authError = exchangeResult?.error ?? storedSessionError;

  if (authError || !verifiedSession) {
    if (billingAuthCallback) {
      clearPendingBillingAuth();
      state.view = 'manageSubscription';
      setStatus('We could not finish signing you in. Please try again.', 'error');
      cleanWebAuthCallbackUrl('/manage-subscription');
      return;
    }

    if (storedPending) {
      state.answers = buildPersistedAnswers(storedPending.answers);
    }
    clearPendingWebSignup();
    state.view = 'account';
    setStatus('We could not finish creating your account. Please try again.', 'error');
    cleanWebAuthCallbackUrl();
    return;
  }

  // Only the session returned by this tab's successful PKCE exchange can
  // complete an unbound signup. Never fall back to a session from storage.
  state.session = verifiedSession;
  state.user = verifiedSession.user;
  void identifyUser(verifiedSession.user);

  if (billingAuthCallback) {
    clearPendingBillingAuth();
    cleanWebAuthCallbackUrl('/manage-subscription');
    state.view = 'manageSubscription';
    setStatus('Signed in. You can now open your secure billing portal.', 'success');
    return;
  }

  cleanWebAuthCallbackUrl();

  const pending = storedPending ?? getPendingEmailSignupFromUser(verifiedSession.user);

  if (
    !pending ||
    !isSessionCompatibleWithPendingSignup(verifiedSession, pending)
  ) {
    if (pending) {
      state.answers = buildPersistedAnswers(pending.answers);
    }
    clearPendingWebSignup();
    state.view = 'account';
    setStatus('We could not verify this account sign-up. Please try again.', 'error');
    cleanWebAuthCallbackUrl();
    return;
  }

  await resumePendingWebSignup(verifiedSession, pending);
}

function hydrateManageSubscriptionRoute() {
  if (window.location.pathname !== '/manage-subscription') {
    return false;
  }

  state.view = 'manageSubscription';

  if (new URLSearchParams(window.location.search).get('returned') === '1') {
    setStatus('Your Stripe portal visit is complete. Subscription changes can take a moment to sync.', 'success');
    window.history.replaceState({}, '', '/manage-subscription');
  }

  return true;
}

function hydrateOnboardingRoute() {
  const screen = getWebOnboardingScreenForPath(window.location.pathname);

  if (screen === null) {
    return false;
  }

  startQuiz(screen, 'none');
  return true;
}

function hydrateCheckoutRoute() {
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get('checkout');
  const isCompletionPath = window.location.pathname === '/checkout/complete';

  if (isCompletionPath || checkout === 'success') {
    const sessionId = params.get('session_id');
    restoreEmbeddedCheckoutFromStorage();
    clearEmbeddedCheckoutSession();
    checkoutCompletionSessionId = sessionId;
    checkoutCompletionStarted = false;
    checkoutCompletionMessage = null;
    checkoutCompletionState = sessionId ? 'loading' : 'error';
    state.view = 'checkoutComplete';
    capture('checkout_returned', {
      ...getCheckoutAnalyticsProperties(),
      return_source: isCompletionPath ? 'embedded' : 'legacy_hosted',
      session_id: sessionId,
    });

    if (!isCompletionPath) {
      window.history.replaceState({}, '', getTestFunnelPath(`/checkout/complete${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`));
    }

    if (!sessionId) {
      checkoutCompletionStarted = true;
      checkoutCompletionMessage = 'The Checkout Session ID is missing. Return to your plan and try again.';
    }

    return true;
  }

  if (window.location.pathname === '/checkout') {
    if (restoreEmbeddedCheckoutFromStorage()) {
      state.view = 'embeddedCheckout';
    } else {
      state.view = 'checkout';
      setStatus('Your previous Checkout Session expired. Choose a plan and start a new payment attempt.', 'error');
      window.history.replaceState({}, '', getTestFunnelPath('/'));
    }

    return true;
  }

  if (checkout === 'plans' && isPaidQuizFunnel()) {
    state.view = 'checkout';
    window.history.replaceState({}, '', getTestFunnelPath('/'));
    return true;
  }

  if (checkout === 'cancelled') {
    capture('checkout_cancelled');
    state.view = 'checkout';
    setStatus('Checkout was cancelled. Choose a plan to try again.', 'error');
    window.history.replaceState({}, '', getTestFunnelPath(window.location.pathname));
    return true;
  }

  return false;
}

root.addEventListener('click', handleClick);
root.addEventListener('input', handleInput);
root.addEventListener('change', (event) => {
  const checkbox = event.target;
  if (!(checkbox instanceof HTMLInputElement) || !checkbox.matches('[data-plan-terms]')) return;
  state.planTermsAccepted = checkbox.checked;
  const button = root.querySelector<HTMLButtonElement>('.offer-plan-options [data-action="start-checkout"]');
  if (button) button.disabled = state.isBusy || !state.planTermsAccepted;
});
root.addEventListener('submit', handleSubmit);
root.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || details.id !== 'checkout-referral') return;
  if (details.open && !referralViewedOnPlans) {
    referralViewedOnPlans = true;
    capture('referral_viewed', { placement: 'plan_selection' });
  }
  referralExpanded = details.open;
}, true);
window.addEventListener('pagehide', () => captureQuizExit('page_leave'));
window.addEventListener('pagehide', () => captureEmbeddedCheckoutAbandoned('page_leave'));
window.addEventListener('popstate', () => {
  const onboardingScreen = getWebOnboardingScreenForPath(window.location.pathname);

  if (onboardingScreen !== null) {
    if (state.view === 'quiz' && activeQuizRunId) {
      goToScreen(onboardingScreen, 'none');
    } else {
      startQuiz(onboardingScreen, 'none');
    }
    return;
  }

  if (state.view === 'quiz') {
    clearFlowTimers();
    reactionText = null;
    state.pendingScreen = null;
    state.view = 'landing';
    setStatus(null);
    render();
    return;
  }

  if (window.location.pathname === '/manage-subscription') {
    state.view = 'manageSubscription';
    setStatus(null);
    render();
    return;
  }

  if (state.view === 'manageSubscription') {
    state.view = 'landing';
    setStatus(null);
    render();
    return;
  }

  if (state.view === 'embeddedCheckout') {
    cancelEmbeddedCheckout('browser_back');
    return;
  }

  if (state.view === 'checkout' && checkoutExitGuardArmed) {
    checkoutExitGuardArmed = false;
    requestCheckoutExit('browser_back');
  }
});
window.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && state.founderOfferVisible) {
    completeCheckoutExit(state.checkoutExitIntent ?? 'landing');
  }
});

initializeOptionalTracking();

// Public quiz entry cannot depend on a network-backed session refresh. Auth
// callbacks and checkout returns retain their existing ordered restoration.
const publicQuizEntry = !isWebAuthCallbackRoute()
  && !new URLSearchParams(window.location.search).has('checkout')
  && getWebOnboardingScreenForPath(window.location.pathname) !== null;
const publicGuestEntry = guestCheckoutPreviewEnabled && location.pathname.startsWith('/guest/');
if (publicGuestEntry) render();
if (publicQuizEntry) void funnelExperimentReady.then(()=>hydrateOnboardingRoute());

void hydrateAuth(!publicQuizEntry && !publicGuestEntry).finally(() => {
  captureLandingViewOnce();
  // Do not start a second run or reset answers when background auth completes.
  if (publicQuizEntry || publicGuestEntry) return;
  const manageSubscriptionRouteHandled = hydrateManageSubscriptionRoute();
  const checkoutRouteHandled = manageSubscriptionRouteHandled ? false : hydrateCheckoutRoute();
  const onboardingRouteHandled =
    !manageSubscriptionRouteHandled && !checkoutRouteHandled ? hydrateOnboardingRoute() : false;

  if (
    !manageSubscriptionRouteHandled &&
    !checkoutRouteHandled &&
    !onboardingRouteHandled &&
    window.location.hash === '#quiz'
  ) {
    startQuiz(ONBOARDING_INTRO_SCREEN, 'replace');
    return;
  }

  if (!onboardingRouteHandled) {
    render();
  }
});
